import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { useCredits } from '@/contexts/CreditsContext';
import axios from 'axios';
import { toast } from 'sonner';
import Header from '@/components/Header';
import HelpBenefits, { HELP_URLS } from '@/components/HelpBenefits';
import FeatureVideoHero from '@/components/FeatureVideoHero';
import WingmanTriggerPrompts from '@/components/WingmanTriggerPrompts';
import { useSortFilterBar } from '@/components/SortFilterBar';
import HarvestCorrectionBadge, {
  statusFilterLabel as harvestCorrectionFilterLabel,
  statusSortRank as harvestCorrectionSortRank,
} from '@/components/HarvestCorrectionBadge';
import FCRVersionGuidanceNote from '@/components/FCRVersionGuidanceNote';
import AquaManagerUploadPanel from '@/components/AquaManagerUploadPanel';
import FishtalkUploadPanel from '@/components/FishtalkUploadPanel';
import AquaManagerFishComparison from '@/components/AquaManagerFishComparison';
import ComparePicker from '@/components/ComparePicker';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import useRfiScenario from '@/hooks/useRfiScenario';
import RFIAdjustmentTable from '@/components/RFIAdjustmentTable';
import { useApplySpeciesDefaults } from '@/hooks/useSpeciesDefaults';
import { rankSpreadsheetCauses } from '@/utils/spreadsheetCauseRanking';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { FcrGuidanceInfo, FcrGuidanceModal } from '@/components/FcrGuidance';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { 
  ArrowLeft, FileSpreadsheet, Download, Upload, Trash2, FolderOpen, 
  TrendingUp, CheckCircle, AlertTriangle, Maximize2, X, RefreshCw, ChevronDown, ChevronUp, FileText,
  Search, ArrowUpCircle, ArrowDownCircle, Info, Brain, Scale, Utensils, GitCompareArrows, Lock, Pencil, Check, Fish, Loader2, Save
} from 'lucide-react';
import { InlineRename } from '@/components/InlineRename';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Scatter, ScatterChart, ComposedChart, Legend, ReferenceLine, ReferenceArea } from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { MissingFishIntelligenceSection, MissingFishIntelligenceReport, PlanningEstimateSummary } from '@/components/MissingFishIntelligence';
import { SampleJumpAnalysisPanel, FCRAnalysisPanel } from '@/components/MissingFishIntelligence';
import GuardrailsToggle from '@/components/GuardrailsToggle';
import { SpreadsheetStockIntegrity } from '@/components/StockIntegrityScore';
import { lookupSFR, ensureSFRTable, classifyFeedingStatus } from '@/utils/feedRecommendation';
import { MissingDataExplanation } from '@/components/MissingDataExplanation';
import { calculateEconomicImpact, computeAdjustedActualCurve, getModelFCR } from '@/utils/missingFishDetection';
import { generateMissingFishIntelligence } from '@/utils/generateIntelligence';
import { calculateBiologicalFCR } from '@/utils/calculationEngine';
import { SectionVisibilityControls } from '@/components/SectionVisibilityControls';
import { ALL_VISIBLE, hasAnyVisible } from '@/config/sectionVisibility';
import DecisionReportPdf from '@/components/DecisionReportPdf';
import { applySSOTOverride } from '@/utils/ssotOverride';
import { sliceRecordAtClosure } from '@/utils/cageClosure';
import { recomputeActualTrajectoryWithExclusions } from '@/utils/recomputeActualTrajectory';

import { detectVerifiedHarvestCorrection } from '@/utils/verifiedHarvestCorrection';
import VerifiedHarvestCorrectionCard from '@/components/VerifiedHarvestCorrectionCard';
import AdminHiddenSectionsOverview from '@/components/AdminHiddenSectionsOverview';
import { useReportVisibility } from '@/contexts/ReportVisibilityContext';
import WingmanInsight from '@/components/WingmanInsight';
import LossWindowEstimatorPanel from '@/components/LossWindowEstimatorPanel';
import WingmanNudge, { detectSpreadsheetPatterns } from '@/components/WingmanNudge';
import RecordHistoryPanel, { saveMfiToHistory } from '@/components/RecordHistoryPanel';
import ModuleExamplesBar from '@/components/ModuleExamplesBar';
import UpdateCuratedExampleButton from '@/components/UpdateCuratedExampleButton';
import ReportGlossary from '@/components/ReportGlossary';
import { useCuratedExamples } from '@/hooks/useCuratedExamples';
import { formatMissingFishRange } from '@/utils/reportFormatters';
import { useAdminMissingFishBaseline } from '@/hooks/useAdminMissingFishBaseline';

// ============================================================================
// SUMMARY REPORT VIEW — Simplified client-facing decision report
// ★ ALL narrative text sourced ONLY from DecisionContext via applySSOTOverride
// ============================================================================

const SummaryReportView = ({ activeRecord, missingFishIntelligence, economicParams, dataQuality, modelFCR, fcrResult, biologicalFCR, cageStatus, manualMissingFishOverride = null, manualAssumedFishNumber = null, activeFcrVersion = null, modelAdjustmentPercent = null }) => {
  const { user, token } = useAuth();
  const isAdminViewer = !!user?.is_admin;
  const baselineHook = useAdminMissingFishBaseline(isAdminViewer, token);
  const baselineActive = isAdminViewer && baselineHook.enabled;
  const baselineFloor = baselineHook.floor || 10000;
  const ssot = missingFishIntelligence?.ssot;
  const decisionContext = missingFishIntelligence?.decisionContext;
  const sharedMetrics = missingFishIntelligence?.sharedMetrics;
  const fcrBasedDetection = missingFishIntelligence?.fcrBasedDetection;
  
  // Indicative mode: show all data regardless of confidence gating
  const [indicativeMode, setIndicativeMode] = useState(false);
  
  const overriddenOutput = useMemo(() => {
    return applySSOTOverride({
      detection: ssot?.conclusion?.type,
      interpretation: null,
      growthInsight: null,
      feeding: ssot?.feedingState,
      feedingExplanation: null,
    }, decisionContext, ssot);
  }, [ssot, decisionContext]);
  
  const conclusion = ssot?.conclusion;
  const fd = overriddenOutput?.finalDecision || {};
  
  // In indicative mode, always show missing fish data
  const missingFishUserVisible = indicativeMode ? true : (fd.missingFishUserVisible !== false);
  
  const financialConfidenceLevel = indicativeMode
    ? (ssot?.financialConfidenceLevel || 'indicative')
    : (missingFishUserVisible ? (ssot?.financialConfidenceLevel || 'not_actionable') : 'not_actionable');
  
  // In indicative mode, always pull raw values from ssot/conclusion
  const rawMissingCountFromSsot = conclusion?.rawMissingFishCount ?? conclusion?.missingFishCount ?? ssot?.rawMissingFishCount ?? ssot?.missingFishCount ?? 0;
  const rawMissingPercentFromSsot = conclusion?.rawMissingFishPercent ?? conclusion?.missingFishPercent ?? ssot?.rawMissingFishPercent ?? ssot?.missingFishPercent ?? 0;
  const rawDetectionStrength = conclusion?.detectionStrength ?? ssot?.detectionStrength ?? 'Low';
  // Admin baseline injection — when analysis returned exactly zero
  // missing fish AND the admin baseline toggle is on, treat the
  // baseline floor as a genuine detection so ALL downstream metrics
  // (RFI narrative, revenue risk, adjusted fish count, wasted-feed
  // wording) flow through the "there ARE missing fish" pathway.
  const baselineInjected = baselineActive && rawMissingCountFromSsot === 0 && (ssot != null || conclusion != null);
  const rawMissingCount = baselineInjected ? baselineFloor : rawMissingCountFromSsot;
  const startCountForBaseline = activeRecord?.input_number || 0;
  const rawMissingPercent = baselineInjected && startCountForBaseline > 0
    ? (baselineFloor / startCountForBaseline) * 100
    : rawMissingPercentFromSsot;
  const rawDetectionStrength_ = baselineInjected ? 'Baseline' : rawDetectionStrength;
  const rawHasMissingFish = baselineInjected || ssot?.hasMissingFish || rawMissingCount > 0;
  
  const missingCount = missingFishUserVisible ? rawMissingCount : 0;
  const missingPercent = missingFishUserVisible ? rawMissingPercent : 0;
  const detectionStrength = missingFishUserVisible ? rawDetectionStrength_ : 'N/A';
  const hasMissingFish = missingFishUserVisible && rawHasMissingFish;
  
  const reportedFCR = fcrResult?.reported?.fcr ?? activeRecord?.reported_fcr ?? sharedMetrics?.reported_fcr;
  const adjustedFCR = fcrResult?.adjusted?.fcr;
  const fcrGapPercent = sharedMetrics?.fcr_gap_percent || fcrBasedDetection?.fcrGapPercent;
  const reliabilityScore = conclusion?.dataReliabilityScore ?? dataQuality?.score ?? 50;
  const reliabilityLabel = conclusion?.dataReliabilityLabel ?? 
    (reliabilityScore >= 70 ? 'Good' : reliabilityScore >= 50 ? 'Acceptable' : 'Low');
  
  const wastedFeedKg = conclusion?.wastedFeedKg ?? ssot?.wastedFeedKg ?? 0;
  const rawFinancialImpactEurBase = conclusion?.financialImpactEur ?? ssot?.financialImpactEur ?? 0;
  // When the admin baseline is injected we recompute the financial impact
  // from scratch so the "10 000 baseline" is treated as a genuine loss,
  // not silently dropped because the SSOT computed €0 against zero fish.
  const _fishPricePreview = economicParams?.fish_price_eur_per_kg ? parseFloat(economicParams.fish_price_eur_per_kg) : null;
  const _targetHarvestKgPreview = economicParams?.target_harvest_weight_kg ? parseFloat(economicParams.target_harvest_weight_kg) : null;
  const rawFinancialImpactEur = baselineInjected && _fishPricePreview && _targetHarvestKgPreview
    ? baselineFloor * _targetHarvestKgPreview * _fishPricePreview
    : rawFinancialImpactEurBase;
  const financialImpactEur = missingFishUserVisible ? rawFinancialImpactEur : 0;

  // ★ PRD canonical missing-fish range — single source used by EVERY tile/paragraph below.
  //   Computed once from the SSOT so the same-screen 25,467-vs-25,500 contradiction (H7)
  //   cannot recur: one helper, one number, one rendering.
  //
  //   Manual Fish Count Adjustment override: when the user is in manual mode
  //   and has typed an explicit count, that user-supplied count IS the
  //   declared truth. We replace the engine's missing-fish range with a
  //   point estimate sourced from the user's input.
  const missingRange = manualMissingFishOverride !== null
    ? {
        label: manualMissingFishOverride > 0
          ? `${manualMissingFishOverride.toLocaleString()} fish (your manual estimate)`
          : 'Within margin (your manual estimate)',
        midpoint: manualMissingFishOverride,
        low: manualMissingFishOverride,
        high: manualMissingFishOverride,
        isPointEstimate: true,
        sourcedFromManualOverride: true,
      }
    : formatMissingFishRange(ssot || {});
  const rawMissingRange = formatMissingFishRange({
    missingFishCount: rawMissingCount,
    dataReliabilityScore: reliabilityScore,
  });
  
  const keyFinding = indicativeMode
    ? (conclusion?.statement || overriddenOutput?.finalDetectionLabel || 
       (rawHasMissingFish ? `Missing fish signal detected — ${rawMissingRange.label} (indicative)` : 
        sharedMetrics?.growth_direction === 'below' ? 'Growth below model expectations' : 'No meaningful stock deviation detected'))
    : (overriddenOutput?.finalDetectionLabel || (
    !missingFishUserVisible
      ? (sharedMetrics?.growth_direction === 'below'
        ? 'Growth below model expectations. No confirmed stock discrepancy.'
        : 'No meaningful stock deviation detected')
      : conclusion?.statement || (
          hasMissingFish && detectionStrength !== 'Low'
            ? 'Likely biomass overestimation detected'
            : sharedMetrics?.growth_direction === 'below'
              ? 'Growth is below model expectations'
              : 'No meaningful stock deviation detected'
      )
  ));
  
  const actionability = conclusion?.actionability ?? decisionContext?.decisionClass ?? 'VALIDATION_REQUIRED';
  const feedingState = overriddenOutput?.finalFeedingClassification || ssot?.feedingState;
  
  const primaryAction = decisionContext?.primaryAction;
  const secondaryActions = decisionContext?.secondaryActions || [];
  const stockPolicy = decisionContext?.stockRecordPolicy;
  const validation = decisionContext?.validation;

  // Production data from activeRecord
  const startWeight = activeRecord?.input_average_weight;
  const startCount = activeRecord?.input_number;
  const daysInWater = activeRecord?.days_in_water || activeRecord?.data_points?.length || 0;
  const sampleCount = activeRecord?.sample_points?.length || 0;
  const totalFeedKg = activeRecord?.total_feed_given || biologicalFCR?.totalFeedKg || 0;
  const totalMortalities = activeRecord?.total_mortalities || 0;
  const totalHarvest = activeRecord?.total_harvest_count || 0;
  const feedingDays = activeRecord?.feeding_days || 0;
  const noFeedDays = activeRecord?.no_feed_days || 0;
  const feedingPerf = activeRecord?.percent_feeding_of_model;
  const mortalityPercent = startCount > 0 ? ((totalMortalities / startCount) * 100).toFixed(1) : 0;
  const lastSample = activeRecord?.sample_points?.[sampleCount - 1];
  const currentWeight = lastSample?.weight ? parseFloat(lastSample.weight) : null;
  const fcrMissing = fcrResult?.fcrMissingFish;
  const fcrEstMissing = fcrResult?.estimatedMissing || 0;
  
  // Best available missing fish estimate — use first positive value found
  const bestMissingEstimate = (fcrMissing != null && fcrMissing > 0) ? fcrMissing
    : (missingCount > 0) ? missingCount
    : (fcrEstMissing > 0) ? fcrEstMissing
    : 0;
  
  // Missing fish revenue risk (based on fish value, distinct from wasted feed cost)
  const fishPricePerKg = economicParams?.fish_price_eur_per_kg ? parseFloat(economicParams.fish_price_eur_per_kg) : null;
  const targetHarvestKg = economicParams?.target_harvest_weight_kg ? parseFloat(economicParams.target_harvest_weight_kg) : null;
  const missingFishRevenueRisk = (fishPricePerKg && targetHarvestKg && bestMissingEstimate > 0)
    ? bestMissingEstimate * targetHarvestKg * fishPricePerKg
    : null;
  
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5" data-testid="summary-report-view">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Summary Report</h2>
          <p className="text-xs text-gray-500">{activeRecord?.name} — {activeRecord?.species} — {cageStatus === 'closed' ? 'Closed Cage' : 'Ongoing Cage'}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Indicative Report Mode Toggle */}
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="indicative-mode-toggle">
            <input
              type="checkbox"
              checked={indicativeMode}
              onChange={(e) => setIndicativeMode(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
            />
            <span className={`text-xs font-medium ${indicativeMode ? 'text-amber-700' : 'text-gray-500'}`}>
              Indicative Report
            </span>
          </label>
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
            actionability === 'ACTIONABLE' ? 'bg-cyan-100 text-cyan-800' :
            actionability === 'MONITOR_ONLY' ? 'bg-amber-100 text-amber-700' :
            actionability === 'VALIDATION_REQUIRED' ? 'bg-red-100 text-red-800' :
            'bg-amber-100 text-amber-800'
          }`}>
            {actionability?.replace(/_/g, ' ') || 'REVIEW PENDING'}
          </span>
        </div>
      </div>

      {/* Indicative Mode Banner */}
      {indicativeMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5" data-testid="indicative-mode-banner">
          <p className="text-sm text-amber-800 font-semibold">Indicative Report Mode</p>
          <p className="text-xs text-amber-600">All data shown regardless of confidence level. Values marked as indicative require validation before operational use.</p>
        </div>
      )}

      {/* 1. PRODUCTION OVERVIEW */}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-semibold">Production Overview</p>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-sm">
          <div className="bg-slate-50 rounded-lg p-2.5 text-center">
            <p className="text-[9px] text-slate-500 uppercase">Start Weight</p>
            <p className="text-base font-bold text-slate-800">{typeof startWeight === 'number' ? `${startWeight.toFixed(1)}g` : `${startWeight}g`}</p>
          </div>
          {currentWeight && (
            <div className="bg-slate-50 rounded-lg p-2.5 text-center">
              <p className="text-[9px] text-slate-500 uppercase">Last Sample</p>
              <p className="text-base font-bold text-slate-800">{currentWeight.toFixed(1)}g</p>
            </div>
          )}
          <div className="bg-slate-50 rounded-lg p-2.5 text-center">
            <p className="text-[9px] text-slate-500 uppercase">Start Count</p>
            <p className="text-base font-bold text-slate-800">{startCount?.toLocaleString()}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-2.5 text-center">
            <p className="text-[9px] text-slate-500 uppercase">Days in Water</p>
            <p className="text-base font-bold text-slate-800">{daysInWater}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-2.5 text-center">
            <p className="text-[9px] text-slate-500 uppercase">Total Feed</p>
            <p className="text-base font-bold text-slate-800">{totalFeedKg > 0 ? `${totalFeedKg.toLocaleString()} kg` : 'N/A'}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-2.5 text-center">
            <p className="text-[9px] text-slate-500 uppercase">Samples</p>
            <p className="text-base font-bold text-slate-800">{sampleCount}</p>
          </div>
        </div>
      </div>

      {/* 2. KEY FINDING */}
      <div className={`rounded-lg p-4 ${
        indicativeMode ? 'bg-gradient-to-r from-amber-800 to-amber-700' :
        conclusion?.hasConflict ? 'bg-gradient-to-r from-amber-900 to-amber-800' : 'bg-gradient-to-r from-slate-900 to-slate-800'
      } text-white`}>
        <div className="flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Key Finding</p>
          {indicativeMode && <span className="text-[9px] uppercase tracking-wider bg-amber-600/40 text-amber-200 px-2 py-0.5 rounded">Indicative</span>}
        </div>
        <p className="text-lg font-bold">{keyFinding}</p>
        {/* In indicative mode, always show the missing fish signal if data exists */}
        {indicativeMode && rawMissingCount !== 0 && (
          <p className="text-sm text-amber-200 mt-1">
            {rawMissingRange.label} ({rawMissingPercent.toFixed(1)}%) — {rawDetectionStrength} signal strength (indicative)
          </p>
        )}
        {!indicativeMode && detectionStrength !== 'Low' && hasMissingFish && !conclusion?.hasConflict && (
          <p className="text-sm text-slate-300 mt-1">
            {conclusion?.confidenceLabel || ssot?.confidenceLabel || detectionStrength} indication — {missingRange.label} ({missingPercent.toFixed(1)}%)
          </p>
        )}
      </div>

      {/* 3. FCR & STOCK HEALTH */}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-semibold">FCR & Stock Assessment</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
          <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-l-indigo-400">
            <p className="text-[9px] text-gray-500 uppercase">Reported FCR</p>
            <p className="text-xl font-bold text-gray-800">{reportedFCR != null ? reportedFCR.toFixed(2) : 'N/A'}</p>
            {startCount && <p className="text-[9px] text-gray-400">Fish: {startCount.toLocaleString()}</p>}
          </div>
          <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-l-blue-400">
            <p className="text-[9px] text-gray-500 uppercase">Model FCR</p>
            {/* Prefer the LIVE `modelFCR` (fetched with the current FCR
                version + multiplier) over the saved MFI figure — the
                saved figure can lag behind the dropdown until the next
                MFI re-run. */}
            <p className="text-xl font-bold text-gray-800" data-testid="summary-model-fcr-value">
              {modelFCR != null ? modelFCR.toFixed(2) : (sharedMetrics?.model_fcr?.toFixed(2) || 'N/A')}
            </p>
            <p className="text-[9px] text-gray-400">{(activeRecord?.species === 'Seabream' || !activeRecord?.species) ? 'Weight+strain lookup' : 'Weight-indexed lookup'}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-l-teal-400">
            <p className="text-[9px] text-gray-500 uppercase">Adjusted FCR</p>
            <p className="text-xl font-bold text-gray-800">{adjustedFCR != null ? adjustedFCR.toFixed(2) : (reportedFCR != null ? reportedFCR.toFixed(2) : 'N/A')}</p>
            {adjustedFCR != null && fcrEstMissing !== 0 && <p className="text-[9px] text-gray-400">Fish: {((startCount || 0) - Math.abs(fcrEstMissing)).toLocaleString()}</p>}
            {adjustedFCR != null && fcrEstMissing === 0 && startCount && <p className="text-[9px] text-gray-400">Fish: {startCount.toLocaleString()}</p>}
          </div>
          <div className={`rounded-lg p-3 border-l-4 ${
            baselineInjected ? 'bg-slate-50 border-l-slate-400'
              : bestMissingEstimate > 0 ? 'bg-red-50 border-l-red-500'
              : isAdminViewer ? 'bg-slate-50 border-l-slate-400'
              : 'bg-green-50 border-l-green-400'
          }`} data-testid="summary-missing-fish-tile">
            <p className="text-[9px] text-gray-500 uppercase">Est. Missing Fish</p>
            <p className={`text-xl font-bold ${
              baselineInjected ? 'text-slate-700'
                : bestMissingEstimate > 0 ? 'text-red-600'
                : isAdminViewer ? 'text-slate-700'
                : 'text-green-700'
            }`}>
              {baselineInjected ? (
                <>{baselineFloor.toLocaleString()} <span className="text-[10px] font-medium text-slate-500">(baseline)</span></>
              ) : bestMissingEstimate > 0
                ? missingRange.shortLabel
                : isAdminViewer
                  ? <>10,000 <span className="text-[10px] font-medium text-slate-500">(baseline)</span></>
                  : 'None detected'}
            </p>
            {baselineInjected && (
              <p className="text-[9px] text-slate-500">Admin baseline treated as genuine detection</p>
            )}
            {!baselineInjected && bestMissingEstimate === 0 && isAdminViewer && (
              <p className="text-[9px] text-slate-500">Admin baseline — analysis returned 0</p>
            )}
            {bestMissingEstimate > 0 && !missingRange.isWithinMargin && <p className="text-[9px] text-red-500">midpoint ~{missingRange.midpointLabel}</p>}
            {bestMissingEstimate > 0 && missingPercent > 0 && <p className="text-[9px] text-red-500">{missingPercent.toFixed(1)}% of start count</p>}
            {bestMissingEstimate > 0 && fcrGapPercent > 0 && <p className="text-[9px] text-red-500">FCR gap {fcrGapPercent.toFixed(1)}%</p>}
            {indicativeMode && bestMissingEstimate > 0 && <p className="text-[8px] text-amber-500 mt-0.5">Indicative</p>}
          </div>
        </div>

        {/* FCR Version (as its own field, separate from Model Adjustment).
            Renders the version name + its multiplier so the reader can see
            exactly which biological FCR curve the Model FCR above was
            derived from. Manual Model Adjustment (%) is displayed on its
            own line below to avoid conflating the two. */}
        {(activeFcrVersion || (modelAdjustmentPercent != null && modelAdjustmentPercent !== 100)) && (
          <div className="mt-2 flex flex-wrap items-center gap-3 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs" data-testid="summary-fcr-version-field">
            {activeFcrVersion && (
              <div className="flex items-center gap-2">
                <span className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">FCR Version</span>
                <span className="font-medium text-slate-800" data-testid="summary-fcr-version-name">
                  {activeFcrVersion.origin_name} — {activeFcrVersion.version_name}
                </span>
                <span
                  className={`px-1.5 py-0.5 rounded-md font-semibold ${
                    Number(activeFcrVersion.multiplier_percent) === 100
                      ? 'bg-slate-200 text-slate-700'
                      : Number(activeFcrVersion.multiplier_percent) > 100
                        ? 'bg-red-100 text-red-700'
                        : 'bg-emerald-100 text-emerald-700'
                  }`}
                  data-testid="summary-fcr-multiplier"
                >
                  {activeFcrVersion.multiplier_percent}%
                </span>
              </div>
            )}
            {modelAdjustmentPercent != null && modelAdjustmentPercent !== 100 && (
              <div className="flex items-center gap-2 pl-3 border-l border-slate-300">
                <span className="text-[9px] uppercase tracking-wider text-amber-700 font-semibold">Model Adjustment</span>
                <span className="font-medium text-amber-800" data-testid="summary-model-adjustment">
                  {modelAdjustmentPercent > 100 ? '+' : ''}{modelAdjustmentPercent - 100}%
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 4. FEEDING & MORTALITY PERFORMANCE */}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-semibold">Operational Performance</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-[9px] text-gray-500 uppercase">Feeding Performance</p>
            <p className={`text-xl font-bold ${
              feedingPerf >= 90 ? 'text-green-700' : feedingPerf >= 70 ? 'text-amber-600' : 'text-red-600'
            }`}>{feedingPerf != null ? `${feedingPerf}%` : 'N/A'}</p>
            <p className="text-[9px] text-gray-400">{feedingDays} feed / {noFeedDays} no-feed days</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-[9px] text-gray-500 uppercase">Total Mortalities</p>
            <p className="text-xl font-bold text-gray-800">{totalMortalities.toLocaleString()}</p>
            <p className="text-[9px] text-gray-400">{mortalityPercent}% of start count</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-[9px] text-gray-500 uppercase">Total Harvest</p>
            <p className="text-xl font-bold text-gray-800">{totalHarvest > 0 ? totalHarvest.toLocaleString() : 'Ongoing'}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-[9px] text-gray-500 uppercase">Feeding Classification</p>
            <p className={`text-base font-bold ${
              feedingState === 'Overfeeding' ? 'text-red-600' : feedingState === 'Optimal' ? 'text-green-700' : 'text-amber-600'
            }`}>{feedingState || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* 5. FINANCIAL IMPACT */}
      <div>
        <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-semibold">Financial Impact</p>
        <div className="grid grid-cols-2 gap-3">
          <div className={`rounded-lg p-4 ${wastedFeedKg > 0 ? 'bg-red-50 border border-red-100' : 'bg-green-50 border border-green-100'}`}>
            <p className="text-[9px] text-gray-500 uppercase mb-1">Estimated Wasted Feed</p>
            <p className={`text-2xl font-bold ${wastedFeedKg > 0 ? 'text-red-600' : 'text-green-700'}`}>
              {wastedFeedKg > 0 ? `${wastedFeedKg.toLocaleString()} kg` : 'None detected'}
            </p>
            {wastedFeedKg > 0 && economicParams?.average_feed_price_eur_per_kg && (
              <p className="text-xs text-red-500 mt-0.5">
                ~€{(wastedFeedKg * (parseFloat(economicParams.average_feed_price_eur_per_kg) || 1.5)).toLocaleString(undefined, {maximumFractionDigits: 0})} at €{economicParams.average_feed_price_eur_per_kg}/kg
              </p>
            )}
          </div>
          <div className={`rounded-lg p-4 ${
            !missingFishRevenueRisk && financialConfidenceLevel === 'not_actionable' ? 'bg-gray-50 border border-gray-100' :
            (missingFishRevenueRisk || financialImpactEur) > 0 ? 'bg-red-50 border border-red-100' : 'bg-green-50 border border-green-100'
          }`}>
            <p className="text-[9px] text-gray-500 uppercase mb-1">Missing Fish Revenue Risk</p>
            {missingFishRevenueRisk != null && missingFishRevenueRisk > 0 ? (
              <>
                <p className={`text-2xl font-bold ${indicativeMode ? 'text-amber-600' : 'text-red-600'}`}>
                  €{missingFishRevenueRisk.toLocaleString(undefined, {maximumFractionDigits: 0})}
                </p>
                <p className="text-[9px] text-gray-500 mt-0.5">~{bestMissingEstimate.toLocaleString()} fish × {targetHarvestKg}kg × €{fishPricePerKg}/kg</p>
                {indicativeMode && <p className="text-[8px] text-amber-500">Indicative — validation required</p>}
              </>
            ) : financialImpactEur > 0 ? (
              <>
                <p className={`text-2xl font-bold ${indicativeMode ? 'text-amber-600' : financialConfidenceLevel === 'indicative' ? 'text-amber-600' : 'text-red-600'}`}>
                  €{financialImpactEur.toLocaleString()}
                </p>
                {(indicativeMode || financialConfidenceLevel === 'indicative') && <p className="text-[9px] text-amber-500">Indicative — validation required</p>}
              </>
            ) : (
              <>
                <p className="text-sm text-gray-500">Set fish price & target harvest weight for estimate</p>
                <p className="text-[9px] text-gray-400 mt-1">Economic Parameters section below</p>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* 6. INTERPRETATION */}
      {overriddenOutput?.finalInterpretation && (
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-semibold">Interpretation</p>
          <div className={`border rounded-lg p-3 ${
            overriddenOutput?.hasConflict ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-100'
          }`}>
            <p className={`text-sm ${overriddenOutput?.hasConflict ? 'text-amber-800' : 'text-blue-800'}`}>
              {overriddenOutput.finalInterpretation}
            </p>
            {overriddenOutput?.detectionExplanation && (
              <p className={`text-xs mt-2 pt-2 border-t ${
                overriddenOutput?.hasConflict ? 'text-amber-600 border-amber-200' : 'text-blue-600 border-blue-200'
              }`}>
                {overriddenOutput.detectionExplanation}
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* 7. DATA CONFIDENCE + RECOMMENDED ACTIONS (side by side) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-semibold">Data Confidence</p>
          <div className="flex items-center gap-3 mb-2">
            {/* ★ H2 — surface the canonical confidence label (one of the five PRD-allowed
                 values). The reliability grade is kept as a sub-line so nothing is lost. */}
            <div className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${
              reliabilityScore >= 70 ? 'bg-green-100 text-green-800' :
              reliabilityScore >= 50 ? 'bg-amber-100 text-amber-800' :
              'bg-red-100 text-red-800'
            }`}>
              {ssot?.confidenceLabel || conclusion?.confidenceLabel || 'Uncertain'}
            </div>
            <span className="text-xs text-gray-500">
              Data reliability {reliabilityScore}/100
            </span>
          </div>
          <p className="text-xs text-gray-500">{sampleCount} sample{sampleCount !== 1 ? 's' : ''} over {daysInWater} days{dataQuality?.details?.avgSampleInterval ? ` (avg ${Math.round(dataQuality.details.avgSampleInterval)}d gap)` : ''}</p>
        </div>
        
        <div>
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-semibold">Recommended Actions</p>
          <div className="bg-cyan-50 border border-cyan-100 rounded-lg p-3 space-y-1.5">
            {primaryAction && (
              <p className="text-sm text-cyan-900 font-medium flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-600 shrink-0"></span>
                {primaryAction.text}
              </p>
            )}
            {secondaryActions.slice(0, 2).map((action, i) => (
              <p key={i} className="text-sm text-cyan-700 flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-cyan-400 shrink-0"></span>
                {action.text}
              </p>
            ))}
            {stockPolicy && (
              <p className="text-[10px] text-gray-600 italic mt-1 pt-1 border-t border-cyan-200">
                {stockPolicy.message}
              </p>
            )}
          </div>
        </div>
      </div>
      
      {/* 8. VALIDATION */}
      {validation?.required && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider text-red-600 mb-1 font-semibold">Validation Required</p>
          <p className="text-sm text-red-700">
            Confirm via: {validation.examples?.join(' · ') || 'sample / camera / harvest reconciliation'}
          </p>
        </div>
      )}
      
      {/* Footer */}
      <div className="pt-3 border-t border-gray-200 flex justify-between items-center text-[8px] text-gray-400 font-mono">
        <span>Detection: {detectionStrength} | Reliability: {reliabilityScore}/100 | Financial: {financialConfidenceLevel}{indicativeMode ? ' | MODE: INDICATIVE' : ''}</span>
        <span>Generated: {new Date().toISOString().slice(0, 16).replace('T', ' ')}</span>
      </div>
    </div>
  );
};


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SpreadsheetGrowthComparison() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { formatCostLabel } = useCredits();
  // Admin-only Missing-Fish baseline toggle — controls whether the
  // "10 000 fish floor" is applied to admin viewers everywhere in this
  // report. Non-admins never see the floor; the hook returns
  // { enabled: false, floor: 10000, ready: true } for them.
  const { enabled: adminBaselineEnabled, floor: adminBaselineFloor } =
    useAdminMissingFishBaseline(!!user?.is_admin, token);
  // Read-only mode: user not approved (pending admin approval)
  // Restricted demo (view-only, curated examples only) — server enforces the same rules.
  const isRestrictedDemo = !!user?.is_restricted_demo;
  const isReadOnly = !isRestrictedDemo && !user?.is_approved && !user?.is_unlimited && !user?.is_admin;
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  // Exact-name duplicate prompt — non-null when the backend has
  // rejected an upload with 409 { duplicate_name: true }. The dialog
  // offers Overwrite (primary) and Keep Both; either choice resubmits
  // the same upload with the corresponding duplicate_action flag.
  const [duplicatePrompt, setDuplicatePrompt] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  
  // Ref for PDF export
  const reportRef = useRef(null);
  const pdfChartsRef = useRef(null);    // Page 1: All charts
  const pdfReportRef = useRef(null);    // Pages 2-3: Report content (metrics, intelligence, etc.)
  
  // Records state
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [showRecords, setShowRecords] = useState(false);
  const [activeRecord, setActiveRecord] = useState(null);
  // Tracks the curated example just loaded so the admin "Update Try-X" CTA
  // can render. Cleared whenever a different (non-curated) record loads.
  const [loadedCurated, setLoadedCurated] = useState(null);
  // Bumps whenever the species' SFR table finishes loading, so the
  // `daily_analysis_rows` useMemo recomputes model-feed with the correct table.
  const [sfrTableVersion, setSfrTableVersion] = useState(0);

  // Excel-style sort + multi-select filter over saved analyses.
  // Persists per device in localStorage (see SortFilterBar.jsx).
  const recordColumns = useMemo(() => ([
    { key: 'name',       label: 'Name',         accessor: r => r.name,                                                              type: 'text' },
    { key: 'species',    label: 'Species',      accessor: r => r.species,                                                           type: 'text' },
    { key: 'start_date', label: 'Start Date',   accessor: r => r.start_date,                                                        type: 'date' },
    { key: 'data_pts',   label: 'Data Points',  accessor: r => (Array.isArray(r.data_points) ? r.data_points.length : 0),           type: 'number' },
    { key: 'data_through', label: 'Data Through', accessor: r => (Array.isArray(r.data_points) && r.data_points.length ? r.data_points[r.data_points.length - 1]?.date : null), type: 'date' },
    { key: 'updated_at', label: 'Updated',      accessor: r => r.updated_at,                                                        type: 'date' },
    // Harvest Correction — enum column.
    //   accessor   → display label used for the filter dropdown
    //                ("Verified" / "Possible" / "None" / "Not calculated")
    //   sortAccessor → numeric rank giving the requested sort order:
    //                Verified (0) → Possible (1) → None (2) → Not calculated (3)
    //   Reads strictly from the server-persisted `harvest_correction_status`
    //   — the table NEVER recomputes the signal.
    {
      key: 'harvest_correction',
      label: 'Harvest Correction',
      accessor: r => harvestCorrectionFilterLabel(r?.harvest_correction_status),
      sortAccessor: r => harvestCorrectionSortRank(r?.harvest_correction_status),
      sortType: 'number',
      type: 'text',
    },
  ]), []);
  const { processedRecords: filteredRecords, bar: recordsSortFilterBar } = useSortFilterBar(
    records,
    recordColumns,
    'spreadsheet-records',
  );

  // ─────────────────────────────────────────────────────────────────────────
  // RFI Scenario hook (admin-only Daily Recommendation Adjustment).
  //
  // SSOT contract: the hook owns the in-memory overrides map and provides
  // `applyOverride(row)` which the `daily_analysis_rows` useMemo invokes at
  // the very end of its build pass. Every downstream consumer (KPIs, charts,
  // narratives, PDF export) reads from the resulting `recommended_feed_kg`
  // so we never duplicate RFI logic. Non-admins never see the table; the
  // hook still mounts but is disabled by default for them.
  // ─────────────────────────────────────────────────────────────────────────
  const rfiScenario = useRfiScenario({ recordId: activeRecord?.id, isAdmin: !!user?.is_admin });
  const rfiApplyOverride = rfiScenario.applyOverride;
  const rfiOverrides = rfiScenario.overrides;
  const rfiOverridesEnabled = rfiScenario.enabled;


  
  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [batchDeleteConfirmOpen, setBatchDeleteConfirmOpen] = useState(false);
  
  // Upload form state
  const [recordName, setRecordName] = useState('');
  const [species, setSpecies] = useState('Seabream');
  const [geneticOrigin, setGeneticOrigin] = useState('Greek');
  const [fcrVersionId, setFcrVersionId] = useState('');  // FCR origin version for multiplier
  const [selectedFile, setSelectedFile] = useState(null);
  const [modelAdjustmentPercent, setModelAdjustmentPercent] = useState(100);
  const [userSpecies, setUserSpecies] = useState([]);

  // Auto-populate species defaults across the platform — when the user
  // picks a species in the upload form, set genetic origin + FCR version
  // (matches the platform-wide policy from /api/species-defaults).
  useApplySpeciesDefaults({
    token,
    species,
    keys: ['genetic_origin', 'fcr_version_id'],
    onApply: (patch) => {
      if (patch.genetic_origin !== undefined) setGeneticOrigin(patch.genetic_origin);
      if (patch.fcr_version_id !== undefined) setFcrVersionId(patch.fcr_version_id);
    },
  });

  // Preload the SFR table for the active record's species so model-feed
  // calculations on the Daily Analysis table use the correct table (prevents
  // Seabream SFR being used for Tilapia/Seabass records → "no model feed").
  useEffect(() => {
    const sp = activeRecord?.species || species;
    if (!sp) return;
    ensureSFRTable(sp).then(() => setSfrTableVersion((v) => v + 1));
  }, [activeRecord?.species, species]);
  
  // FCR Origins state
  const [fcrOrigins, setFcrOrigins] = useState([]);
  const [loadingFcrOrigins, setLoadingFcrOrigins] = useState(false);
  
  // Chart state
  const [chartExpanded, setChartExpanded] = useState(false);
  const [mortalityChartExpanded, setMortalityChartExpanded] = useState(false);
  const [feedChartExpanded, setFeedChartExpanded] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [showAdvancedDiagnostics, setShowAdvancedDiagnostics] = useState(false);
  const [showAllRows, setShowAllRows] = useState(false);
  
  // Multi-run comparison state
  const [showPreviousRuns, setShowPreviousRuns] = useState(false);
  const [scenarioHistory, setScenarioHistory] = useState([]); // Max 5 scenarios
  const MAX_SCENARIOS = 5;
  
  // Cycle comparison state - overlay saved analyses on current graph
  const [comparisonAnalyses, setComparisonAnalyses] = useState([]); // Selected analyses to compare
  const [showCompareSelector, setShowCompareSelector] = useState(false);
  
  // Line visibility state - control which lines are shown on Growth chart
  const [lineVisibility, setLineVisibility] = useState({
    modelGrowth: true,
    actualGrowth: true,
    // Yellow-dotted feed-implied adjusted-growth line (feed-biomass curve
    // re-scaled to kept sample anchors). Defaults ON so chart behaviour
    // is preserved; unchecking hides the line without recomputing MFI /
    // sample exclusions / SFR (RULE-IMP-CHT-001, PFD-BUG-009).
    adjustedGrowth: true,
    samplePoints: true,
    // Potential-loss visual overlays — both default to ON to preserve
    // existing behaviour. Users can disable independently when reviewing
    // growth without the trajectory-jump annotations.
    lossEventMarker: true,   // The amber concentric-circle marker on the actual line at jump_sample_date
    lossWindow: true,        // The amber shaded ReferenceArea spanning the probable loss window
    // Red dashed vertical line + ⚠ annotation at sample dates flagged by
    // Missing Fish Intelligence as data-quality-suspect. Default ON.
    incorrectSampleFlag: true,
  });

  // Feed chart line visibility
  const [feedLineVisibility, setFeedLineVisibility] = useState({
    dailyVariance: true,
    accumulatedVariance: true,
    accumulatedActual: true,
    accumulatedRecommended: true,
  });
  const [varianceYMaxOverride, setVarianceYMaxOverride] = useState('');

  // Multi-select deletion state for saved records
  const [selectedRecordIds, setSelectedRecordIds] = useState(new Set());

  // Update pre-existing record state
  const [updateExistingRecord, setUpdateExistingRecord] = useState(false);

  // ★ Loss Window Estimator — when the user toggles "Show these windows
  //   on the growth chart" inside the LossWindowEstimatorPanel, that
  //   toggle calls setEstimatorChartWindows with either:
  //     { top: <window>, alternates: <window[]> }  (visible)
  //     null                                        (hidden / not run yet)
  //   The growth chart renderer paints amber ReferenceAreas driven by
  //   this state — independent of the older MFI trajectoryJump marker
  //   which lives on its own `lineVisibility.lossWindow` toggle.
  const [estimatorChartWindows, setEstimatorChartWindows] = useState(null);
  const [selectedUpdateRecordId, setSelectedUpdateRecordId] = useState('');

  // ★ Spreadsheet bulletproofing — row-level diagnostics surfaced after upload/reupload
  const [uploadDiagnostics, setUploadDiagnostics] = useState(null);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);

  // ★ KPI Cut-Off at First Harvest — per-record analysis preference
  const [kpiCutoffSaving, setKpiCutoffSaving] = useState(false);
  
  // Comparison colors for overlaid analyses (distinct from current analysis)
  const COMPARISON_COLORS = [
    { model: '#8b5cf6', actual: '#a78bfa', name: 'Purple' },   // Purple
    { model: '#06b6d4', actual: '#22d3ee', name: 'Cyan' },     // Cyan
    { model: '#f59e0b', actual: '#fbbf24', name: 'Amber' },    // Amber
    { model: '#10b981', actual: '#34d399', name: 'Emerald' },  // Emerald
    { model: '#ec4899', actual: '#f472b6', name: 'Pink' },     // Pink
  ];
  
  // Missing Fish Intelligence state
  const [missingFishIntelligence, setMissingFishIntelligence] = useState(null);
  const [showMissingFishIntelligence, setShowMissingFishIntelligence] = useState(false);

  // Excluded sample indices for user-controlled sample exclusion
  const [excludedSampleIndices, setExcludedSampleIndices] = useState(new Set());

  // Economic impact parameters
  const [economicParams, setEconomicParams] = useState({
    average_feed_price_eur_per_kg: 1.50,
    fish_price_eur_per_kg: '',
    target_harvest_weight_kg: '',
  });
  const [economicImpact, setEconomicImpact] = useState(null);
  const [dataQuality, setDataQuality] = useState(null);
  const [mortalityCheck, setMortalityCheck] = useState(null);
  const [samplingRecommendation, setSamplingRecommendation] = useState(null);
  const [overfeedingRisk, setOverfeedingRisk] = useState(null);
  const [graphInsight, setGraphInsight] = useState(null);
  const [trajectoryJump, setTrajectoryJump] = useState(null);
  const [assumedFishNumber, setAssumedFishNumber] = useState(null);
  const [detectionResults, setDetectionResults] = useState(null);
  const [cageStatus, setCageStatus] = useState('ongoing'); // 'ongoing' | 'closed'
  // Explicit "Save closure" state — the dropdown auto-persists on
  // change, but when a cage is CLOSED the user asked for an explicit
  // Save button as a visible confirmation that the closure is stored
  // going forward. Idempotent re-PATCH of `cage_closed: true`.
  const [savingClosure, setSavingClosure] = useState(false);
  const [closureSavedAt, setClosureSavedAt] = useState(null);
  const [detectionMethod, setDetectionMethod] = useState('fcr'); // 'jump' | 'fcr'
  const [fishEstimationMode, setFishEstimationMode] = useState('automatic'); // 'automatic' | 'manual'
  const [manualFishCount, setManualFishCount] = useState('');
  const [suspectedLossDate, setSuspectedLossDate] = useState(null); // null = full cycle

  // ★ MASTER OVERRIDE: View All Data — forces decision-grade across ALL sections
  const [showAllDataOverride, setShowAllDataOverride] = useState(false);

  // Section visibility – single shared state for live screen + PDF
  const [sectionVisibility, setSectionVisibility] = useState(ALL_VISIBLE);

  // Summary Report mode – toggles between Summary and Full Report views
  const [viewMode, setViewMode] = useState('full'); // 'full' | 'summary'

  // Model FCR from backend lookup – always available when a record is loaded
  const [modelFCR, setModelFCR] = useState(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // CENTRALIZED SSOT OVERRIDE — Single enforcement point for ALL report components
  // ═══════════════════════════════════════════════════════════════════════════
  // This overriddenOutput is passed to ALL downstream report panels.
  // No component may consume raw ssot/missingFishIntelligence for narrative fields.
  const _rawOverriddenOutput = useMemo(() => {
    if (!missingFishIntelligence) return null;
    
    const ssot = missingFishIntelligence.ssot;
    const decisionContext = missingFishIntelligence.decisionContext;
    
    return applySSOTOverride({
      // Initial values (will be overridden)
      detection: ssot?.conclusion?.type,
      interpretation: null,
      growthInsight: null,
      jumpStatus: null,
      feeding: ssot?.feedingState,
      feedingExplanation: null,
      missingFishCount: ssot?.missingFishCount,
      missingFishPercent: ssot?.missingFishPercent,
      wastedFeedKg: ssot?.wastedFeedKg,
      financialImpactEur: ssot?.financialImpactEur,
      // ★ NEW: Pass all raw data for SSOT override processing
      // These are used to compute canonical narrative fields
      sharedMetrics: missingFishIntelligence.sharedMetrics,
      analyticalSignals: missingFishIntelligence.analyticalSignals,
      samplingRecommendation: samplingRecommendation,
      growthFit: missingFishIntelligence.growthFit,
      trajectoryJump: trajectoryJump,
      dataQuality: dataQuality,
      reportedFishNumber: activeRecord?.fish_number,
    }, decisionContext, ssot);
  }, [missingFishIntelligence, samplingRecommendation, trajectoryJump, dataQuality, activeRecord]);

  // ★ EFFECTIVE OUTPUT: When showAllDataOverride is active, force decision-grade on ALL data
  const effectiveOutput = useMemo(() => {
    if (!showAllDataOverride || !_rawOverriddenOutput) return _rawOverriddenOutput;
    const fd = _rawOverriddenOutput.finalDecision || {};
    const ssot = missingFishIntelligence?.ssot || {};
    
    // ★ When override is active, ensure missing fish count is displayed
    // even if original mode was 'signal' (which would have set missingFishCountDisplay to null)
    const missingFishCount = ssot?.missingFishCount || fd.missingFishCount || 0;
    const adjustedFishCount = ssot?.adjustedFishCount || (activeRecord?.input_number ? activeRecord.input_number - missingFishCount : 0);
    
    return {
      ..._rawOverriddenOutput,
      finalDecision: {
        ...fd,
        _originalConfidenceMode: fd._originalConfidenceMode || fd.confidenceMode, // Preserve original for banner logic
        confidenceMode: 'decision',
        confidenceModeLabel: 'Full Data (Override)',
        missingFishUserVisible: true,
        hasMissingFish: fd.hasMissingFish !== false ? true : fd.hasMissingFish,
        showFinancialImpact: true,
        financialIsIndicative: false,
        canUseMissingFishWording: true,
        financialContextLevel: fd._originalFinancialContextLevel || fd.financialContextLevel || 'confirmed',
        financialContextReason: null,
        flags: { ...fd.flags, validationRequired: false, notDecisionGrade: false },
        // ★ Force missing fish count display when override is active
        missingFishCount: missingFishCount,
        missingFishCountDisplay: missingFishCount > 0 ? `~${missingFishCount.toLocaleString()} (Indicative)` : null,
        adjustedFishCountLabel: adjustedFishCount > 0 ? `~${adjustedFishCount.toLocaleString()} (Indicative)` : fd.adjustedFishCountLabel,
        modeLabels: {
          ...fd.modeLabels,
          estimatedMissingFishLabel: 'Estimated Missing Fish (Indicative)',
          revenueRiskLabel: 'Missing Fish Revenue Risk',
          reportTitle: 'FCR Signal Analysis',
        },
      },
    };
  }, [showAllDataOverride, _rawOverriddenOutput, missingFishIntelligence?.ssot, activeRecord?.input_number]);

  // Fetch records
  const fetchRecords = useCallback(async () => {
    setRecordsLoading(true);
    try {
      const authToken = token; // from useAuth
      const response = await axios.get(
        `${BACKEND_URL}/api/spreadsheet/records`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setRecords(response.data || []);
    } catch (err) {
      console.error('Failed to fetch records:', err);
      toast.error('Failed to load saved records');
    } finally {
      setRecordsLoading(false);
    }
  }, []);

  // Fetch records on mount so the "Update pre-existing record" dropdown has data
  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Curated "Try [Species]" examples — admin-controlled via AdminConsole.
  const { examples: spreadsheetExamples, refetch: refetchSpreadsheetExamples } = useCuratedExamples('spreadsheet');

  // Try-with-Example handler — the spreadsheet module stores full records
  // (file_data + parsed rows) so the curated payload just carries the record
  // id. Fetch the full record and hand it to loadRecord — same path as
  // clicking "Open" in the saved-records browser.
  const loadExampleAndRun = async (preset) => {
    if (isReadOnly) return;
    const recordId = preset?.inputs?.record_id;
    if (!recordId) {
      toast.error(`No curated record configured for ${preset?.species_label || 'this species'}.`);
      return;
    }
    try {
      const resp = await axios.get(
        `${BACKEND_URL}/api/spreadsheet/records/${recordId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Loaded ${preset.species_label} example`);
      await loadRecord(resp.data);
      // Pin the curated context so the admin "Update Try-X Example" CTA
      // can render. loadRecord clears it inside, so we set it AFTER.
      if (preset?.source_record_id) {
        setLoadedCurated({
          module: 'spreadsheet',
          species: preset.species_label,
          source_record_id: preset.source_record_id,
          current_name: preset.source_record_name || `Try-${preset.species_label} Example`,
        });
      }
      // Auto-run Missing Fish Intelligence so the trajectory-jump signals
      // (amber Loss Window band + ⚠ flag) are visible immediately on the
      // chart, without the user needing a second click. Curated jump
      // demos are only meaningful with MFI applied.
      // Via ref: the closure captured at click time still has the previous activeRecord.
      setTimeout(() => {
        try { runMfiRef.current?.(); } catch (_) { /* no-op */ }
      }, 1500);
      // ★ Examples always render with guardrails OFF — examples are
      //   designed to showcase the platform's full signal set (low-conf
      //   signals included) so the user sees the strongest first impression.
      try { setShowAllDataOverride(true); } catch { /* state may not exist on initial mount */ }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          `Couldn't load the curated ${preset.species_label} example.`
      );
    }
  };

  useEffect(() => {
    if (showRecords) {
      fetchRecords();
    }
  }, [showRecords, fetchRecords]);

  // ★ Deep-link from the dashboard hero CTA / first-run welcome modal:
  //   when the URL carries `?startExample=Seabream` (or another supported
  //   species), auto-load that curated example exactly once. Strips the
  //   query param afterwards so a refresh doesn't re-trigger it.
  const [autoLoadedExample, setAutoLoadedExample] = useState(false);
  useEffect(() => {
    if (autoLoadedExample) return;
    if (!spreadsheetExamples || spreadsheetExamples.length === 0) return;
    if (isReadOnly) return;
    const params = new URLSearchParams(window.location.search);
    const wanted = params.get('startExample');
    if (!wanted) return;
    const preset = spreadsheetExamples.find(
      (p) => (p.species_label || '').toLowerCase() === wanted.toLowerCase()
    );
    if (!preset) return;
    setAutoLoadedExample(true);
    // Clean the URL so a reload doesn't replay the example.
    try {
      const cleaned = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', cleaned);
    } catch { /* ignore */ }
    loadExampleAndRun(preset);
  }, [spreadsheetExamples, autoLoadedExample, isReadOnly]);

  // ★ Deep-link from the Live Cage KPIs dashboard: `?recordId=<id>` opens
  //   that specific saved analysis in this comparison view. Fetches the
  //   full record, hands it to loadRecord (same path as the sidebar), and
  //   strips the query param so a refresh doesn't re-trigger.
  const [autoLoadedRecordId, setAutoLoadedRecordId] = useState(null);
  useEffect(() => {
    if (autoLoadedRecordId) return;
    const params = new URLSearchParams(window.location.search);
    const wantedId = params.get('recordId');
    if (!wantedId) return;
    // If the record is already active, just clean the URL.
    if (activeRecord?.id === wantedId) {
      setAutoLoadedRecordId(wantedId);
      try {
        const cleaned = window.location.pathname + window.location.hash;
        window.history.replaceState({}, '', cleaned);
      } catch { /* ignore */ }
      return;
    }
    setAutoLoadedRecordId(wantedId);
    (async () => {
      try {
        const resp = await axios.get(
          `${BACKEND_URL}/api/spreadsheet/records/${wantedId}`,
          { headers: { 'Authorization': `Bearer ${token}` } },
        );
        if (resp?.data) {
          await loadRecord(resp.data);
        }
      } catch (err) {
        toast.error('Could not open that record — it may have been deleted or you no longer have access.');
      } finally {
        try {
          const cleaned = window.location.pathname + window.location.hash;
          window.history.replaceState({}, '', cleaned);
        } catch { /* ignore */ }
      }
    })();
    // Fires exactly once per URL landing (guarded by autoLoadedRecordId).
  }, [token, autoLoadedRecordId]);

  // Fetch user-specific species
  useEffect(() => {
    const fetchUserSpecies = async () => {
      try {
        const authToken = token; // from useAuth
        const resp = await axios.get(`${BACKEND_URL}/api/user-species`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setUserSpecies(resp.data.species || []);
      } catch { /* fallback */ }
    };
    fetchUserSpecies();
  }, []);

  // Fetch FCR Origins for version selection
  useEffect(() => {
    const fetchFcrOrigins = async () => {
      setLoadingFcrOrigins(true);
      try {
        const authToken = token; // from useAuth
        const response = await axios.get(`${BACKEND_URL}/api/fcr-origins`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setFcrOrigins(response.data?.origins || []);

        // NOTE: initial default selection is handled by the unified species
        // effect below; we simply load the origin list here.
      } catch (err) {
        console.error('Failed to fetch FCR origins:', err);
      } finally {
        setLoadingFcrOrigins(false);
      }
    };
    
    fetchFcrOrigins();
  }, []);

  // NOTE: FCR-version default selection is now handled by the unified species
  // effect below (see "Phase B: on species change" block). We intentionally
  // DO NOT auto-set here — the unified effect owns the decision for all four
  // species (Seabream / Seabass / Tilapia / Tripletail).

  // Phase B: on species change, snap the FCR Version dropdown to the
  // species-specific "v1" so Tilapia-selected records default to Tilapia v1,
  // Seabass-selected records default to Seabass v1, and Seabream defaults to
  // the matching genetic-origin v1 (Greek / French / Italian).
  //
  // Bug fix (deployed app — R32P2G): previously the dep array also listed
  // `fcrVersionId`, which made the effect re-fire on every user version pick
  // and snap them right back to v1 (the user could never escape v1). It also
  // clobbered the persisted `fcr_version_id` on saved records the instant
  // the record loaded. The fix below:
  //   1. Removes `fcrVersionId` from the deps (no more snap-back on user pick).
  //   2. Bails when the active record carries its own persisted
  //      `fcr_version_id` — that takes absolute precedence over the species
  //      default.
  //   3. Uses a ref to ensure each (species, genetic_origin) pair only snaps
  //      ONCE, even if the effect re-runs for unrelated reasons.
  const fcrSnapKeyRef = useRef('');
  useEffect(() => {
    if (!fcrOrigins.length) return;
    // Respect any persisted FCR version on the loaded record — the sibling
    // useEffect below rehydrates `fcrVersionId` from `activeRecord`. We must
    // never overwrite that value here.
    if (activeRecord?.fcr_version_id) {
      fcrSnapKeyRef.current = `${species}|${geneticOrigin}`;
      return;
    }
    const key = `${species}|${geneticOrigin}`;
    if (fcrSnapKeyRef.current === key) return; // already snapped for this pair
    fcrSnapKeyRef.current = key;

    const pickV1 = (origin) => {
      if (!origin) return null;
      const v1 = origin.versions?.find((v) => (v.version_name || '').toLowerCase() === 'v1');
      return v1?.id || origin.default_version_id || null;
    };
    let targetCode = null;
    if (species === 'Tilapia') targetCode = 'tilapia';
    else if (species === 'Seabass') targetCode = 'seabass';
    else if (species === 'Seabream') targetCode = (geneticOrigin || 'Greek').toLowerCase();
    if (!targetCode) return;
    const origin = fcrOrigins.find((o) => (o.code || '').toLowerCase() === targetCode);
    const versionId = pickV1(origin);
    if (versionId) {
      setFcrVersionId(versionId);
    }
  }, [species, geneticOrigin, fcrOrigins, activeRecord?.fcr_version_id]);


  // Download template
  const downloadTemplate = async () => {
    try {
      const authToken = token; // from useAuth
      const response = await axios.get(
        `${BACKEND_URL}/api/spreadsheet/template`,
        { 
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'growth_comparison_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded successfully');
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('Please upload an Excel file (.xlsx or .xls)');
        return;
      }
      setSelectedFile(file);
      if (!recordName) {
        setRecordName(file.name.replace(/\.(xlsx|xls)$/, ''));
      }
    }
  };

  // Upload and process spreadsheet
  const uploadSpreadsheet = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    if (!recordName.trim()) {
      toast.error('Please enter a name for this analysis');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const authToken = token; // from useAuth
      
      // Read file as base64
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(selectedFile);
      });

      let response;
      
      // Determine the target record ID for update:
      // 1. If a record is loaded via "Saved Analyses" (activeRecord), use that
      // 2. If the "Update pre-existing record" checkbox is ticked, use the selected dropdown record
      const updateTargetId = activeRecord?.id || (updateExistingRecord && selectedUpdateRecordId ? selectedUpdateRecordId : null);
      
      // If updating an existing record, use the reupload endpoint
      if (updateTargetId) {
        response = await axios.put(
          `${BACKEND_URL}/api/spreadsheet/records/${updateTargetId}/reupload`,
          {
            file_content: fileContent,
            name: recordName.trim(),
            species: species,
            // Seabass / Tilapia / Tripletail have no genetic strain
            genetic_origin: (species === 'Seabass' || species === 'Tilapia' || species === 'Tripletail') ? null : geneticOrigin,
            temperature_region: "User uploaded temperatures",
            model_adjustment_percent: modelAdjustmentPercent
          },
          {
            headers: { 
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json'
            }
          }
        );
        // Reload the updated record
        const reloadRes = await axios.get(
          `${BACKEND_URL}/api/spreadsheet/records/${updateTargetId}`,
          { headers: { 'Authorization': `Bearer ${authToken}` } }
        );
        setActiveRecord(reloadRes.data);
        // Reset the update checkbox state after successful update
        setUpdateExistingRecord(false);
        setSelectedUpdateRecordId('');
        // ★ Surface row-level diagnostics (same shape as /upload)
        const _rejected = response.data?.rejected_rows || [];
        const _accepted = response.data?.accepted_rows ?? null;
        const _warnings = response.data?.upload_warnings || [];
        _warnings.forEach((w) => toast.warning(w, { duration: 12000 }));
        if (_rejected.length > 0) {
          setUploadDiagnostics({ accepted: _accepted, rejected: _rejected, mode: 'update' });
          setDiagnosticsOpen(true);
          toast.warning(`Updated — ${_rejected.length} row issue${_rejected.length !== 1 ? 's' : ''} flagged. Click to review.`);
        } else if (_warnings.length === 0) {
          toast.success('Record updated with new data (previous state saved as revision)');
        }
      } else {
        // Create a new record.
        // Wrapped in a helper so the exact-name duplicate dialog can
        // re-invoke the POST with an explicit duplicate_action once
        // the user has chosen Overwrite vs Keep Both.
        const postCreate = (duplicateAction) => axios.post(
          `${BACKEND_URL}/api/spreadsheet/upload`,
          {
            file_content: fileContent,
            name: recordName.trim(),
            species: species,
            // Seabass / Tilapia / Tripletail have no genetic strain
            genetic_origin: (species === 'Seabass' || species === 'Tilapia' || species === 'Tripletail') ? null : geneticOrigin,
            temperature_region: "User uploaded temperatures",
            model_adjustment_percent: modelAdjustmentPercent,
            ...(duplicateAction ? { duplicate_action: duplicateAction } : {}),
          },
          {
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json'
            }
          }
        );
        try {
          response = await postCreate(null);
        } catch (dupErr) {
          const detail = dupErr?.response?.data?.detail;
          if (dupErr?.response?.status === 409 && detail && detail.duplicate_name) {
            // Stash a re-submit thunk so the dialog can trigger either
            // path once the user chooses. Uploading indicator stays on
            // to signal we're paused pending the user's decision.
            setDuplicatePrompt({
              name: detail.name || recordName.trim(),
              resubmit: async (action) => {
                setDuplicatePrompt(null);
                try {
                  const r = await postCreate(action);
                  setActiveRecord(r.data);
                  const _rejected = r.data?.rejected_rows || [];
                  const _accepted = r.data?.accepted_rows ?? null;
                  const _warnings = r.data?.upload_warnings || [];
                  _warnings.forEach((w) => toast.warning(w, { duration: 12000 }));
                  if (_rejected.length > 0) {
                    setUploadDiagnostics({ accepted: _accepted, rejected: _rejected, mode: 'create' });
                    setDiagnosticsOpen(true);
                    toast.warning(`Processed — ${_rejected.length} row issue${_rejected.length !== 1 ? 's' : ''} flagged. Click to review.`);
                  } else if (_warnings.length === 0) {
                    toast.success(action === 'overwrite'
                      ? 'Existing record overwritten with the new upload'
                      : 'New record created — existing one kept unchanged');
                  }
                  setSelectedFile(null);
                  fetchRecords();
                } catch (e2) {
                  const raw2 = e2.response?.data?.detail;
                  const msg2 = typeof raw2 === 'string' ? raw2
                    : (raw2 && (raw2.message || raw2.error)) || 'Failed to process spreadsheet';
                  setError(msg2);
                  toast.error(msg2);
                } finally {
                  setUploading(false);
                }
              },
              cancel: () => { setDuplicatePrompt(null); setUploading(false); },
            });
            return; // keep uploading state on; dialog will drive next step
          }
          throw dupErr;
        }
        setActiveRecord(response.data);
        // ★ Surface row-level diagnostics from /upload
        const _rejected = response.data?.rejected_rows || [];
        const _accepted = response.data?.accepted_rows ?? null;
        const _warnings = response.data?.upload_warnings || [];
        // Surface any non-fatal upload warnings (e.g. date-format
        // reinterpretation). These do NOT block the upload but the operator
        // must know about them — silent rewriting of dates is exactly what
        // produced the DR151.1 V-shape chart bug before this fix.
        _warnings.forEach((w) => toast.warning(w, { duration: 12000 }));
        if (_rejected.length > 0) {
          setUploadDiagnostics({ accepted: _accepted, rejected: _rejected, mode: 'create' });
          setDiagnosticsOpen(true);
          toast.warning(`Processed — ${_rejected.length} row issue${_rejected.length !== 1 ? 's' : ''} flagged. Click to review.`);
        } else if (_warnings.length === 0) {
          toast.success('Spreadsheet processed successfully');
        }
      }
      setSelectedFile(null);
      // Reset ALL analysis state when record changes (prevents stale data)
      setMissingFishIntelligence(null);
      setShowMissingFishIntelligence(false);
      setShowAllDataOverride(false);
      setExcludedSampleIndices(new Set());
      setEconomicImpact(null);
      setDataQuality(null);
      setMortalityCheck(null);
      setSamplingRecommendation(null);
      setOverfeedingRisk(null);
      setGraphInsight(null);
      setTrajectoryJump(null);
      setDetectionResults(null);
      setAssumedFishNumber(null);
      setCageStatus('ongoing');
      setScenarioHistory([]);
      setComparisonAnalyses([]);
      setFishEstimationMode('automatic');
      setManualFishCount('');
      setSuspectedLossDate(null);
      
      // Notify user about temperature gap filling
      const filledDates = response.data?.temperature_gaps_filled;
      if (filledDates && filledDates.length > 0) {
        const dateList = filledDates.length <= 5
          ? filledDates.join(', ')
          : `${filledDates.slice(0, 3).join(', ')} ... and ${filledDates.length - 3} more`;
        toast.info(
          `${filledDates.length} missing temperature value${filledDates.length > 1 ? 's were' : ' was'} auto-filled using interpolation: ${dateList}`,
          { duration: 10000 }
        );
      }
      
      fetchRecords();
    } catch (err) {
      // Backend may return `detail` as either a string (most errors) or an
      // object (402 insufficient credits — { message, required, available, ... }).
      // Normalize to a readable string for display, otherwise React would
      // render "[object Object]" or crash. The 402 modal still opens via
      // the global Axios interceptor in CreditsContext.
      const raw = err.response?.data?.detail;
      const errorMsg =
        typeof raw === 'string'
          ? raw
          : (raw && (raw.message || raw.error)) || 'Failed to process spreadsheet';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  // Load a saved record
  const loadRecord = async (record) => {
    setActiveRecord(record);
    setLoadedCurated(null);  // panel-loaded record clears the curated context (loadExampleAndRun re-pins it)
    setRecordName(record.name);
    setSpecies(record.species);
    setGeneticOrigin(record.genetic_origin);
    setModelAdjustmentPercent(record.model_adjustment_percent || 100);
    setScenarioHistory([]); // Clear scenario history when loading new record
    // Reset missing fish intelligence when loading new record
    setMissingFishIntelligence(null);
    setShowMissingFishIntelligence(false);
    setExcludedSampleIndices(new Set());
    setEconomicImpact(null);
    setDataQuality(null);
    setMortalityCheck(null);
    setSamplingRecommendation(null);
    setOverfeedingRisk(null);
    setGraphInsight(null);
    setTrajectoryJump(null);
    setDetectionResults(null);
    setAssumedFishNumber(null);
    // Cage status is now user-controlled with default "ongoing". We no
    // longer auto-derive from harvest events — a record with harvest rows
    // still loads as open/ongoing unless the user explicitly flipped it to
    // closed (persisted via `cage_closed` on the record).
    setCageStatus(record?.cage_closed === true ? 'closed' : 'ongoing');
    setFishEstimationMode('automatic');
    setManualFishCount('');
    setSuspectedLossDate(null);
    // Load economic params from record (or defaults)
    setEconomicParams({
      average_feed_price_eur_per_kg: record.average_feed_price_eur_per_kg ?? 1.50,
      fish_price_eur_per_kg: record.fish_price_eur_per_kg ?? '',
      target_harvest_weight_kg: record.target_harvest_weight_kg ?? '',
    });
    setShowRecords(false);
    toast.success(`Loaded: ${record.name}`);
  };

  // Delete a record (with confirmation)
  const confirmDeleteRecord = (recordId) => {
    setPendingDeleteId(recordId);
    setDeleteConfirmOpen(true);
  };

  const deleteRecord = async (recordId) => {
    try {
      const authToken = token; // from useAuth
      await axios.delete(
        `${BACKEND_URL}/api/spreadsheet/records/${recordId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success('Record deleted');
      if (activeRecord?.id === recordId) {
        setActiveRecord(null);
      }
      setSelectedRecordIds(prev => { const n = new Set(prev); n.delete(recordId); return n; });
      fetchRecords();
    } catch (err) {
      toast.error('Failed to delete record');
    }
  };

  const renameSpreadsheetRecord = async (recordId, newName) => {
    const authToken = token; // from useAuth
    await axios.patch(
      `${BACKEND_URL}/api/spreadsheet/records/${recordId}/rename`,
      { name: newName },
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
    setRecords(prev => prev.map(r => r.id === recordId ? { ...r, name: newName } : r));
    toast.success('Record renamed');
  };

  // Delete selected records (batch, with confirmation)
  const confirmDeleteSelected = () => {
    if (selectedRecordIds.size === 0) return;
    setBatchDeleteConfirmOpen(true);
  };

  const deleteSelectedRecords = async () => {
    if (selectedRecordIds.size === 0) return;
    const authToken = token; // from useAuth
    let deleted = 0;
    for (const id of selectedRecordIds) {
      try {
        await axios.delete(
          `${BACKEND_URL}/api/spreadsheet/records/${id}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        deleted++;
        if (activeRecord?.id === id) setActiveRecord(null);
      } catch { /* skip */ }
    }
    toast.success(`Deleted ${deleted} record${deleted !== 1 ? 's' : ''}`);
    setSelectedRecordIds(new Set());
    fetchRecords();
  };

  // Reanalyze with new model adjustment
  const [reanalyzing, setReanalyzing] = useState(false);
  
  const reanalyzeWithNewAdjustment = async () => {
    if (!activeRecord) {
      toast.error('No active analysis to reanalyze');
      return;
    }

    // Check if adjustment actually changed
    if (modelAdjustmentPercent === activeRecord.model_adjustment_percent) {
      toast.info('Model adjustment is unchanged. No reanalysis needed.');
      return;
    }

    // If showing previous runs, save current scenario to history before reanalyzing
    if (showPreviousRuns && activeRecord) {
      const currentScenario = {
        id: `scenario-${Date.now()}`,
        model_adjustment_percent: activeRecord.model_adjustment_percent,
        model_trajectory: activeRecord.model_trajectory,
        actual_trajectory: activeRecord.actual_trajectory,
        timestamp: new Date().toISOString()
      };
      
      setScenarioHistory(prev => {
        const updated = [currentScenario, ...prev].slice(0, MAX_SCENARIOS - 1); // Keep space for new run
        return updated;
      });
    }

    setReanalyzing(true);

    try {
      const authToken = token; // from useAuth
      const response = await axios.post(
        `${BACKEND_URL}/api/spreadsheet/records/${activeRecord.id}/reanalyze`,
        null,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { model_adjustment_percent: modelAdjustmentPercent }
        }
      );

      setActiveRecord(response.data);
      toast.success(`Reanalysis complete using model adjustment: ${modelAdjustmentPercent}%`);
      fetchRecords(); // Refresh records list
      // Auto-rerun Missing Fish Intelligence if it had already been produced
      // for this record so every downstream panel (KPIs, chart amber line,
      // Wingman insights, Summary report) reflects the new trajectory
      // without the user having to click "Run Missing Intelligence" again.
      if (missingFishIntelligence) {
        // Defer so the new activeRecord + modelFCR have time to propagate
        setTimeout(() => { runMissingFishIntelligence(); }, 150);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to reanalyze';
      toast.error(errorMsg);
    } finally {
      setReanalyzing(false);
    }
  };

  // Check if reanalyze is available (adjustment changed from saved record)
  const canReanalyze = activeRecord && modelAdjustmentPercent !== activeRecord.model_adjustment_percent;

  // ★ KPI Cut-Off at First Harvest — PATCH preference and refresh local record
  const toggleKpiCutoff = async (checked) => {
    if (!activeRecord) return;
    setKpiCutoffSaving(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/spreadsheet/records/${activeRecord.id}/kpi-cutoff`,
        { kpi_cutoff_at_first_harvest: !!checked },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setActiveRecord((prev) => prev ? { ...prev, kpi_cutoff_at_first_harvest: !!checked } : prev);
      toast.success(
        checked
          ? 'KPIs will stop at the first harvest event'
          : 'Full-cycle KPIs restored (no cutoff)'
      );
    } catch (err) {
      toast.error('Failed to update KPI cut-off preference');
    } finally {
      setKpiCutoffSaving(false);
    }
  };

  // Clear scenario history when loading a new record or clearing form
  const clearScenarioHistory = () => {
    setScenarioHistory([]);
  };

  // Clear form
  const clearForm = () => {
    setActiveRecord(null);
    setRecordName('');
    setSelectedFile(null);
    clearScenarioHistory();
    setError('');
    setModelAdjustmentPercent(100);
    // Reset missing fish intelligence when clearing form
    setMissingFishIntelligence(null);
    setShowMissingFishIntelligence(false);
    setEconomicImpact(null);
    setDataQuality(null);
    setMortalityCheck(null);
    setSamplingRecommendation(null);
    setOverfeedingRisk(null);
    setGraphInsight(null);
    setTrajectoryJump(null);
  };

  // Fetch Model FCR whenever activeRecord changes (same lookup as Snapshot Analysis)
  useEffect(() => {
    if (!activeRecord?.sample_points?.length) { setModelFCR(null); return; }
    const lastSample = activeRecord.sample_points[activeRecord.sample_points.length - 1];
    const lastWeight = parseFloat(lastSample.weight);
    if (!lastWeight || lastWeight <= 0) { setModelFCR(null); return; }
    const recordGeneticOrigin = activeRecord.genetic_origin || 'Greek';
    // Prefer the user's CURRENT dropdown selection (their live intent)
    // over the record's persisted version — otherwise the display Model
    // FCR lags behind the dropdown by the 400 ms debounced-PATCH window
    // and Missing Fish is computed against the OLD multiplier.
    const recordFcrVersionId = fcrVersionId || activeRecord.fcr_version_id;
    axios.get(`${BACKEND_URL}/api/model-fcr`, {
      params: { 
        species: activeRecord.species, 
        weight_g: lastWeight, 
        genetic_origin: recordGeneticOrigin,
        fcr_version_id: recordFcrVersionId || undefined
      },
      headers: { 'Authorization': `Bearer ${token}` },
    }).then(resp => setModelFCR(resp.data.model_fcr))
      .catch(() => setModelFCR(null));
  }, [activeRecord, fcrVersionId]);

  // Resolve the currently-effective FCR version (dropdown pick, or the
  // record's persisted value) to its human-readable label + multiplier.
  // Consumed by both report components (dedicated DecisionReportPdf +
  // fallback SummaryReportView) so they render the version metadata as
  // its own field, distinct from the manual Model Adjustment (%) input.
  const activeFcrVersion = useMemo(() => {
    const effective = fcrVersionId || activeRecord?.fcr_version_id || null;
    if (!effective || !Array.isArray(fcrOrigins) || fcrOrigins.length === 0) return null;
    for (const origin of fcrOrigins) {
      const versions = origin.versions || [];
      const v = versions.find(vv => vv.id === effective);
      if (v) {
        return {
          id: v.id,
          version_name: v.version_name,
          multiplier_percent: v.multiplier_percent,
          origin_name: origin.name,
          is_default: v.id === origin.default_version_id,
        };
      }
    }
    return null;
  }, [fcrVersionId, activeRecord?.fcr_version_id, fcrOrigins]);

  // When a saved record loads, rehydrate the FCR Version dropdown from the
  // record's persisted value BEFORE the auto-sync useEffect runs. This
  // prevents a benign but noisy PATCH on every record open (when the
  // dropdown's local default doesn't match the record's stored version).
  useEffect(() => {
    if (!activeRecord?.id) return;
    const persisted = activeRecord.fcr_version_id || '';
    setFcrVersionId(prev => (prev || '') === persisted ? prev : persisted);
  }, [activeRecord?.id, activeRecord?.fcr_version_id]);

  // ═══════════════════════════════════════════════════════════════════════
  // FCR version auto-sync on existing record
  // ───────────────────────────────────────────────────────────────────────
  // When a user changes the FCR Version dropdown while viewing a saved
  // spreadsheet record, we want the change to be seamless:
  //   1. Persist the new version on the record (lightweight PATCH, FREE)
  //   2. modelFCR re-fetch already happens in the effect above
  //   3. Auto-rerun Missing Fish Intelligence so all downstream calculations
  //      (MFI panel, charts, reports, Wingman insights) reflect the new
  //      multiplier without the user having to click anything.
  // No credits are charged — this is a preference change, not a recompute.
  // ═══════════════════════════════════════════════════════════════════════
  const [fcrAutoSyncing, setFcrAutoSyncing] = useState(false);
  const [fcrSavedFlash, setFcrSavedFlash] = useState(null); // { versionName, multiplier } | null
  const fcrSyncedAtRef = useRef(null);
  // Records the FCR version id that Missing Fish Intelligence was last
  // computed against. Used to detect drift between the currently-selected
  // FCR version and the one the saved MFI result / persisted
  // `saved_analysis` reflects — including the case where the record's
  // already-saved version equals the newly selected one but MFI last ran
  // under a different value (the auto-sync PATCH short-circuits that case,
  // so a dedicated drift effect below refreshes the saved figure).
  const mfiComputedForFcrVersionRef = useRef(null);
  useEffect(() => {
    if (!activeRecord?.id) return;
    // Normalize blank/null to the same shape for comparison
    const currentOnRecord = activeRecord.fcr_version_id || '';
    const selected = fcrVersionId || '';
    if (currentOnRecord === selected) {
      // Persisted version already matches the dropdown — no PATCH needed.
      // The drift-detection effect below is responsible for triggering
      // an MFI re-run in the "same version selected, saved figure stale"
      // case (e.g., MFI last ran under a different value).
      return;
    }
    // Debounce rapid dropdown changes — wait 400ms of stability
    const timer = setTimeout(async () => {
      setFcrAutoSyncing(true);
      try {
        await axios.patch(
          `${BACKEND_URL}/api/spreadsheet/records/${activeRecord.id}/fcr-version`,
          { fcr_version_id: selected || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Update activeRecord locally so the model-fcr effect sees the new
        // value and subsequent comparisons short-circuit.
        setActiveRecord(prev => prev && prev.id === activeRecord.id
          ? { ...prev, fcr_version_id: selected || null }
          : prev);
        fcrSyncedAtRef.current = Date.now();
        // Show a 2-second inline "Saved ✓" pill so the user gets immediate
        // confirmation that their FCR Version choice actually stuck. Looks
        // up the version's display label from the loaded fcrOrigins list.
        const pickedVersion = fcrOrigins
          .flatMap(o => (o.versions || []).map(v => ({ ...v, originName: o.name })))
          .find(v => v.id === selected);
        if (pickedVersion) {
          setFcrSavedFlash({
            versionName: pickedVersion.version_name,
            originName: pickedVersion.originName,
            multiplier: pickedVersion.multiplier_percent,
          });
          setTimeout(() => setFcrSavedFlash(null), 2200);
        }
        toast.success('FCR version updated — recalculating missing fish…', { duration: 1500 });
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to update FCR version');
      } finally {
        setFcrAutoSyncing(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [fcrVersionId, activeRecord?.id, activeRecord?.fcr_version_id, token]);

  // After fcrSyncedAt flips and modelFCR has refreshed, auto-rerun MFI
  // so every downstream panel (KPIs, chart amber line, Wingman insights,
  // Summary report) picks up the new FCR multiplier. We intentionally only
  // re-run when an MFI result already exists — we never force an MFI run
  // on users who haven't yet asked for one. Once the user has clicked
  // "Run Missing Intelligence" ONCE, every subsequent FCR-Version
  // dropdown change fires this effect (via `fcrSyncedAtRef` set inside
  // the debounced PATCH block) and re-runs the engine with the new
  // multiplier — preserving the auto-recalc contract the "changes
  // auto-recalc missing fish" caption promises next to the dropdown.
  useEffect(() => {
    if (!fcrSyncedAtRef.current) return;
    if (!missingFishIntelligence) return; // never surprise-run MFI
    if (modelFCR == null) return;         // wait for the refetched FCR
    // One-shot: clear the sync flag before rerunning to prevent loops
    const stamp = fcrSyncedAtRef.current;
    fcrSyncedAtRef.current = null;
    // Defer to next tick so state updates flush first
    const t = setTimeout(() => {
      if (fcrSyncedAtRef.current !== null && fcrSyncedAtRef.current > stamp) return;
      runMissingFishIntelligence();
    }, 50);
    return () => clearTimeout(t);
  }, [modelFCR, missingFishIntelligence]);

  // ═══════════════════════════════════════════════════════════════════════
  // FCR version DRIFT detection — refreshes the saved MFI result whenever
  // the currently-effective FCR version differs from the one MFI last ran
  // against, INCLUDING the case where the record's already-saved
  // `fcr_version_id` equals the dropdown pick (the auto-sync PATCH path
  // short-circuits that case, leaving the saved figure stale). We only
  // handle the "dropdown === persisted" branch here; the "dropdown !==
  // persisted" branch is owned by the auto-sync effect above.
  // ═══════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!missingFishIntelligence) return;      // never surprise-run MFI
    if (modelFCR == null) return;              // wait for refetched Model FCR
    if (!activeRecord?.id) return;
    const dropdown = fcrVersionId || '';
    const persisted = activeRecord.fcr_version_id || '';
    if (dropdown !== persisted) return;        // auto-sync path handles this
    const effective = dropdown || persisted || null;
    if (mfiComputedForFcrVersionRef.current === effective) return; // no drift
    const t = setTimeout(() => {
      if (mfiComputedForFcrVersionRef.current === effective) return;
      runMissingFishIntelligence();
    }, 150);
    return () => clearTimeout(t);
  }, [fcrVersionId, activeRecord?.id, activeRecord?.fcr_version_id, modelFCR, missingFishIntelligence]);

  // Run Missing Fish Intelligence Analysis (shared engine for both saved cages and uploads)
  const runMissingFishIntelligence = async () => {
    if (cageStatus === 'closed') {
      toast.error('Missing fish estimation not applicable for closed cages');
      return;
    }

    if (!activeRecord || !activeRecord.sample_points || activeRecord.sample_points.length === 0) {
      toast.error('No sample data available for analysis');
      return;
    }

    // Use the already-fetched model FCR (same lookup as Snapshot Analysis)
    const weightBasedModelFCR = modelFCR;

    // Warm the species-aware FCR cache BEFORE running the engine. Without
    // this step, `getModelFCRSync` inside `calculateMissingFishIntelligence`
    // falls back to a crude constant (1.8 for Tilapia, 1.6 for others) —
    // meaningfully wrong for Tilapia/Seabass where the true BFCR is
    // weight-indexed. We prime the cache for the last-sample weight; deeper
    // per-row lookups can be added later if needed.
    try {
      const species = activeRecord.species || 'Seabream';
      const geneticOrigin = activeRecord.genetic_origin || 'Greek';
      const lastSample = (daily_analysis_rows || [])
        .filter(r => r.is_sample && r.actual_weight_g > 0)
        .pop();
      const weightG = lastSample?.actual_weight_g || activeRecord.input_average_weight || 100;
      // Pass the CURRENT dropdown selection first (user intent) with the
      // record's persisted version as fallback — ensures switching FCR
      // Version invalidates the cache and warms the correct multiplier.
      const versionForCache = fcrVersionId || activeRecord.fcr_version_id || null;
      await getModelFCR(species, weightG, geneticOrigin, versionForCache);
    } catch (err) {
      console.warn('FCR cache warm failed (non-fatal):', err);
    }

    const intel = generateMissingFishIntelligence({
      daily_analysis_rows,
      activeRecord,
      economicParams: {
        average_feed_price_eur_per_kg: economicParams.average_feed_price_eur_per_kg,
        fish_price_eur_per_kg: economicParams.fish_price_eur_per_kg,
        target_harvest_weight_kg: economicParams.target_harvest_weight_kg,
      },
      weightBasedModelFCR,
      reportedClosingFCR: fcrResult?.reported?.fcr || null,
      closingBiomassData: biologicalFCR && fcrResult ? {
        totalFeedKg: biologicalFCR.totalFeedKg,
        startBiomassKg: fcrResult.reported.startBiomass,
        closingBiomassKg: fcrResult.reported.closingBiomass,
        harvestBiomassKg: biologicalFCR.harvestBiomassKg,
        mortalityBiomassKg: biologicalFCR.mortalityBiomassKg,
        reportedRemaining: fcrResult.reported.remaining,
        lastWeightG: biologicalFCR.lastWeightG,
      } : null,
      detectionMethod,
      fcrPanelMissingFish: fcrResult?.fcrMissingFish || null,
      excludedIndices: excludedSampleIndices,
      bypassSpeciesClamp: showAllDataOverride,
    });

    if (!intel) {
      toast.error('Unable to generate intelligence from available data');
      return;
    }

    // Populate all state from the unified result object
    setDetectionResults(intel.detectionResults);
    setEconomicImpact(intel.economicImpact);
    setDataQuality(intel.dataQuality);
    setMortalityCheck(intel.mortalityCheck);
    setSamplingRecommendation(intel.samplingRecommendation);
    setOverfeedingRisk(intel.overfeedingRisk);
    setTrajectoryJump(intel.trajectoryJump);
    // Fish Count Adjustment: auto-populate with the strongest missing-fish
    // estimate available so the "Using reported fish count: …" fallback only
    // appears when NO engine method (jump / FCR detection / FCR panel)
    // produced an adjusted count. This keeps the Fish Count Adjustment panel
    // and the amber "best-fit" line on the Growth chart in lockstep with
    // whatever Missing Fish Intelligence concluded.
    const _originalN = intel?.inputs?.inputNumber ?? activeRecord?.input_number ?? null;
    const _bestAdjustedN = intel.initialAssumedFishNumber
      || intel.fcrBasedDetection?.adjustedFishCount
      || intel.trajectoryJump?.adjusted_fish_count
      || intel.trajectoryJump?.best_fit_fish_number
      || null;
    const _pickN = (_bestAdjustedN && _bestAdjustedN !== _originalN)
      ? _bestAdjustedN
      : intel.initialAssumedFishNumber;
    setAssumedFishNumber(_pickN);
    setGraphInsight(intel.graphInsight);
    setMissingFishIntelligence(intel);
    // Record the FCR version this MFI run was computed against so the
    // drift-detection effect above can spot subsequent divergences and
    // refresh the saved figure automatically.
    mfiComputedForFcrVersionRef.current = fcrVersionId || activeRecord?.fcr_version_id || null;
    setShowMissingFishIntelligence(true);
    // Parity-capture hook (Feb 2026) — exposes the SSOT to the outer
    // window when the page is opened with `?parityCapture=1`. Used by
    // /app/backend/tests/parity/capture.py to snapshot the JS analysis
    // stack's output verbatim for parity fixtures. Never publishes
    // anything without the query flag; touches no analytical logic.
    try {
      if (typeof window !== 'undefined' && window.location.search.indexOf('parityCapture=1') !== -1) {
        const capture = {
          captured_at_iso: new Date().toISOString(),
          record_id: activeRecord?.id || null,
          fish_dataset: activeRecord?.fishtalk_dataset || null,
          arrival_date: activeRecord?.arrival_date || activeRecord?.start_date || null,
          current_unit: activeRecord?.fishtalk_current_unit || null,
          ssot: intel?.ssot || null,
          missing_fish: intel?.ssot?.rawMissingFishCount ?? intel?.ssot?.missingFishCount ?? null,
          rfi: intel?.ssot?.rfi ?? null,
          reported_fcr: fcrResult?.reported?.fcr ?? null,
          model_fcr: (typeof modelFCR === 'number') ? modelFCR : null,
          decision_state: intel?.ssot?.decisionState || null,
          confidence: intel?.ssot?.confidence || null,
          intelligence: {
            adjustedFishNumber: intel?.adjustedFishNumber ?? null,
            missingFishCount: intel?.missingFishCount ?? null,
            confidenceLevel: intel?.confidenceLevel ?? null,
            deviationClassification: intel?.deviationClassification ?? null,
          },
        };
        window.__pfd_parity_capture = capture;
      }
    } catch { /* debug hook must never crash analysis */ }
    // ★ Save-Back Hook (Feb 2026) — the individual analysis has just
    //   produced a fresh, complete result. Persist the exact four
    //   analytical KPIs the admin now sees (Missing Fish / RFI /
    //   Reported FCR / Model FCR) back to the record's `saved_analysis`
    //   so the Live Cage KPIs dashboard renders the same numbers on
    //   its next refresh. Guardrails:
    //     • Admin-only (matches Live KPIs visibility).
    //     • `missing_fish` must be numeric — never persist partial
    //       or in-flight analyses.
    //     • Uses whatever `modelFCR` / `fcrResult` are currently in
    //       scope — no recomputation happens here. Model-FCR runs on
    //       its own effect and may not be resolved yet; if that's the
    //       case we still persist Missing Fish + RFI + Reported FCR
    //       and simply leave `model_fcr` null (rendered as "N/A"),
    //       matching the endpoint's `N/A` sentinel rule.
    //   Idempotent — a subsequent analysis rerun will overwrite the
    //   saved_analysis block and refresh `computed_at`.
    try {
      if (user?.is_admin && activeRecord?.id) {
        const _ssot = intel?.ssot || {};
        const _missing = (_ssot.rawMissingFishCount ?? _ssot.missingFishCount);
        if (_missing !== undefined && _missing !== null && !Number.isNaN(Number(_missing))) {
          const _reported = fcrResult?.reported?.fcr ?? null;
          axios.put(
            `${BACKEND_URL}/api/spreadsheet/records/${activeRecord.id}/saved-analysis`,
            {
              missing_fish: Math.max(0, Math.round(Number(_missing))),
              rfi: (typeof _ssot.rfi === 'number') ? _ssot.rfi : null,
              reported_fcr: (typeof _reported === 'number') ? _reported : null,
              model_fcr: (typeof modelFCR === 'number') ? modelFCR : null,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          ).catch(() => { /* silent — never break the analysis UI on save-back failure */ });
        }
      }
    } catch { /* defensive: analysis UI must never crash on a save-back throw */ }
    // ★ H6 — publish the active SSOT to the global bridge so WingmanChat can
    //   reference it without prop-drilling. Cleared on record unload.
    //   Spreadsheet Uploader is a KPI-oriented domain (FCR / RFI / feeding vs
    //   growth); the underlying engine may use jump detection internally but
    //   the module MUST NOT expose "jump" vocabulary to Wingman — that would
    //   leak Growth-vs-Model framing into Spreadsheet answers. We therefore
    //   report detectionType as 'fcr' (or 'none') regardless of the engine's
    //   primary method. `hasAnomaly` is likewise derived without exposing the
    //   jump suspect-sample list.
    if (typeof window !== 'undefined') {
      const _ssot = intel?.ssot || {};
      const _hasMissingSignal = !!_ssot.hasMissingFish
        && _ssot.missingFishRangeLabel && _ssot.missingFishRangeLabel !== 'Within margin';
      const _detectionType = _hasMissingSignal ? 'fcr' : 'none';
      const _hasAnomaly = !!(
        _ssot.hasSuspectedError
        || (_ssot.fcrOutliers && _ssot.fcrOutliers.count > 0)
      );
      // ★ Pre-rank Spreadsheet-domain causes from the rich intel object so
      //   Wingman receives a decision-grade, priority-ordered payload rather
      //   than having to infer one from free text. Backend has a fallback
      //   ranker (/app/backend/routes/wingman.py _rank_spreadsheet_causes)
      //   that runs if this context is missing.
      const { rankedCauses, rankedChecks } = rankSpreadsheetCauses({
        confidenceLabel: _ssot.confidenceLabel,
        hasMissingFishSignal: _hasMissingSignal,
        missingFishRangeLabel: _ssot.missingFishRangeLabel,
        rfi: _ssot.rfi,
        wasteSharePct: _ssot.wasteSharePct,
        feedingState: _ssot.feedingState,
        hasConflict: _ssot.hasConflict,
        hasSuspectedError: _ssot.hasSuspectedError,
        fcrOutliers: _ssot.fcrOutliers,
      });
      window.__pfd_active_record_ssot = {
        record_name: activeRecord?.name || null,
        species: activeRecord?.species || null,
        confidenceLabel: _ssot.confidenceLabel || null,
        actionability: _ssot.actionability || null,
        missingFishRangeLabel: _ssot.missingFishRangeLabel || null,
        feedingState: _ssot.feedingState || null,
        hasConflict: !!_ssot.hasConflict,
        detectionType: _detectionType,
        hasAnomaly: _hasAnomaly,
        hasMissingFishSignal: _hasMissingSignal,
        moduleName: 'Spreadsheet Uploader',
        rankedCauses,
        rankedChecks,
      };
    }
    setFishEstimationMode('automatic');
    setManualFishCount('');
    toast.success('Missing Fish Intelligence analysis complete');
    
    // Save MFI result to record history for progression tracking
    if (activeRecord?.id && token) {
      saveMfiToHistory(token, 'spreadsheet', activeRecord.id, {
        estimated_missing: intel.ssot?.missingFishCount || 0,
        deviation_percent: intel.ssot?.deviationPercent || null,
        detection_strength: intel.ssot?.detectionStrength || 'Low',
        confidence_mode: intel.ssot?.confidenceMode || 'signal',
        actionability: intel.ssot?.actionability || intel.decisionContext?.decisionClass || null,
        interpretation: intel.ssot?.keyInsight || '',
        has_missing_fish: intel.ssot?.hasMissingFish || false,
        sample_count: activeRecord.sample_points?.length || 0,
      });
    }
  };
  const runMfiRef = useRef(null);
  runMfiRef.current = runMissingFishIntelligence;


  // Re-run MFI automatically when the Guard Rails / Indicative Report toggle
  // flips, so `bypassSpeciesClamp` flows into calculateMissingFishIntelligence
  // and the Tilapia one-step confidence clamp is skipped under override.
  // Only fires when MFI has already been run for the active record.
  useEffect(() => {
    if (showMissingFishIntelligence && missingFishIntelligence && activeRecord) {
      runMissingFishIntelligence();
    }
    // eslint-disable-next-line
  }, [showAllDataOverride]);

  // Save economic params to backend and recalculate impact
  const saveEconomicParams = async (newParams) => {
    setEconomicParams(newParams);
    
    // Recalculate economic impact if intelligence already ran
    if (missingFishIntelligence?.missingFishDetection) {
      const ecoParams = {
        average_feed_price_eur_per_kg: parseFloat(newParams.average_feed_price_eur_per_kg) || 1.50,
        fish_price_eur_per_kg: newParams.fish_price_eur_per_kg !== '' ? parseFloat(newParams.fish_price_eur_per_kg) : null,
        target_harvest_weight_kg: newParams.target_harvest_weight_kg !== '' ? parseFloat(newParams.target_harvest_weight_kg) : null,
      };
      setEconomicImpact(calculateEconomicImpact(missingFishIntelligence.missingFishDetection, ecoParams));
    }
    
    // Persist to backend
    if (activeRecord?.id) {
      try {
        const authToken = token; // from useAuth
        await axios.patch(
          `${BACKEND_URL}/api/spreadsheet/records/${activeRecord.id}/economic-params`,
          {
            average_feed_price_eur_per_kg: parseFloat(newParams.average_feed_price_eur_per_kg) || 1.50,
            fish_price_eur_per_kg: newParams.fish_price_eur_per_kg !== '' ? parseFloat(newParams.fish_price_eur_per_kg) : null,
            target_harvest_weight_kg: newParams.target_harvest_weight_kg !== '' ? parseFloat(newParams.target_harvest_weight_kg) : null,
          },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } catch (err) {
        // Silent fail for param save — non-critical
        
      }
    }
  };

  // Check if there are sample weights to analyze
  const hasSampleWeights = activeRecord && activeRecord.sample_points && activeRecord.sample_points.length > 0;

  // ============================================================================
  // DECISION-GRADE PDF EXPORT
  // Creates a clean, client-facing PDF report (3-5 pages max)
  // 
  // Structure:
  //   Page 1: Executive Summary (from DecisionReportPdf)
  //   Page 2: Key Charts (Growth + Feed Variance)  
  //   Page 3: Condensed MFI (from DecisionReportPdf)
  //
  // REMOVED from PDF:
  //   - Daily Data Tables
  //   - Per-sample deviation tables
  //   - Redundant metric panels
  //   - Economic parameters input section
  //   - UI controls / web-only elements
  // ============================================================================
  // ============================================================================
  // COMPACT OPERATIONAL REVIEW PDF (available to all users)
  // 1 page · A4 landscape · stacked vertically:
  //   1. Cycle title + optional "Charts reflect adjusted stock" badge
  //   2. Growth vs Model chart
  //   3. Daily Mortality chart
  //   4. Cumulative Feed & Variance chart
  // Captures the already-rendered chart containers via their
  // `data-pdf-chart="<id>"` markers — no fragile off-screen re-rendering. If
  // the operator has an active fish-count adjustment (MFI or manual override),
  // the PDF captures whatever they see on-screen — the adjusted state is
  // preserved throughout the export.
  // ============================================================================
  const [exportingCompactPdf, setExportingCompactPdf] = useState(false);
  const exportCompactOperationalReviewPdf = async () => {
    if (!activeRecord) {
      toast.error('No analysis results to export');
      return;
    }
    setExportingCompactPdf(true);
    toast.info('Generating compact operational review…');

    // NB: Previously this handler CLEARED `assumedFishNumber` before
    // capturing so the PDF rendered the raw pre-MFI charts. That was
    // wrong — the user explicitly wants the compact PDF to reflect the
    // CURRENT view (MFI-detected fish loss OR a manual override), so
    // downstream feed / mortality / RFI charts illustrate the corrected
    // stock, not the raw upload. We now capture whatever the operator
    // sees on-screen.
    const usingAdjustedFishCount =
      assumedFishNumber !== null && assumedFishNumber !== undefined;

    try {
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();   // 297mm
      const pageH = pdf.internal.pageSize.getHeight();  // 210mm
      const margin = 8;
      const contentW = pageW - margin * 2;

      // Title
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(13);
      const title = `Operational Review — ${activeRecord?.name || 'Cycle'}`;
      pdf.text(title, margin, margin + 5);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.text(
        `${activeRecord?.species || ''} · ${activeRecord?.genetic_origin || ''} · Exported ${new Date().toLocaleString()}`,
        margin,
        margin + 9,
      );
      let y = margin + 13;

      // If the operator is viewing with an adjusted fish count (either MFI-
      // detected OR manually overridden), stamp a small badge under the
      // subtitle so the PDF reader can tell at a glance that the graphs
      // downstream reflect the CORRECTED stock — not the raw upload.
      if (usingAdjustedFishCount) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.setTextColor(180, 83, 9);  // amber-700
        const initialN = activeRecord?.input_number || 0;
        const removed = Math.max(0, initialN - assumedFishNumber);
        const source = fishEstimationMode === 'manual' ? 'manual override' : 'MFI adjustment';
        pdf.text(
          `Charts reflect adjusted stock: ${assumedFishNumber.toLocaleString()} fish `
          + `(≈${removed.toLocaleString()} removed via ${source}).`,
          margin,
          margin + 13,
        );
        pdf.setTextColor(0, 0, 0);
        y = margin + 17;
      }

      // Equal-height bands: 3 charts in the remaining vertical space.
      const remainingH = pageH - y - margin;
      const chartH = Math.floor(remainingH / 3) - 2; // small gutter

      const captureAndPlace = async (selector, label) => {
        const el = document.querySelector(selector);
        if (!el) {
          // Soft-fail: write a placeholder so the user knows which chart was missing.
          pdf.setFont('helvetica', 'italic');
          pdf.setFontSize(9);
          pdf.text(`(${label} not currently rendered — open the section before exporting.)`, margin, y + chartH / 2);
          y += chartH + 2;
          return;
        }
        // Ensure visible / scrolled into view so html2canvas captures correctly.
        el.scrollIntoView({ block: 'center', behavior: 'instant' });
        await new Promise((r) => setTimeout(r, 200));
        const canvas = await html2canvas(el, {
          scale: 1.4,
          backgroundColor: '#ffffff',
          useCORS: true,
          logging: false,
        });
        const img = canvas.toDataURL('image/jpeg', 0.85);
        // Render every chart at the SAME band dimensions (contentW × chartH).
        // Previously the function preserved each chart's source aspect ratio
        // independently — which made RFI (with extra dual-Y-axis chrome)
        // render visibly smaller than Growth and Mortality. Stretching all
        // three to identical pixel dimensions guarantees uniform size in the
        // PDF. The minor aspect-ratio distortion is imperceptible because
        // all three source captures originate from near-identical DOM
        // rectangles (same 220px chart height, same `bg-slate-50/50 p-4`
        // wrapper).
        pdf.addImage(img, 'JPEG', margin, y, contentW, chartH);
        y += chartH + 2;
      };

      await captureAndPlace('[data-pdf-chart="growth"]',    'Growth vs Model chart');
      await captureAndPlace('[data-pdf-chart="mortality"]', 'Daily Mortality chart');
      // NB: `data-pdf-chart="feed"` — the chart contains both cumulative
      //     feed lines AND daily/accum variance overlays. It is the third
      //     and final chart in the compact PDF. (Historical alias
      //     `data-pdf-chart="rfi"` still emitted on the wrapper for
      //     backward compatibility with older selectors.)
      await captureAndPlace('[data-pdf-chart="feed"]',      'Cumulative Feed & Variance chart');

      const safeName = (activeRecord?.name || 'cycle').replace(/[^a-z0-9_-]+/gi, '_');
      pdf.save(`Operational_Review_${safeName}.pdf`);
      toast.success('Compact review exported');
    } catch (e) {
      console.error('Compact PDF export failed', e);
      toast.error('Compact PDF export failed — check console');
    } finally {
      setExportingCompactPdf(false);
    }
  };

  const exportToPdf = async () => {
    if (!user?.is_admin) {
      toast.error('PDF export is restricted to administrator accounts');
      return;
    }
    if (!activeRecord) {
      toast.error('No analysis results to export');
      return;
    }

    setExportingPdf(true);
    toast.info('Generating decision-grade report...');

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pdfWidth - (margin * 2);
      const availableHeight = pdfHeight - (margin * 2);

      let pageNum = 1;
      let yPos = margin;

      // ═══════════════════════════════════════════════════════════════════
      // Adjusted Feeding Recommendation Scenario — top banner.
      // When an admin scenario is active, prepend a one-line banner to the
      // first page so anyone reading the export immediately knows the RFI /
      // waste numbers are from a calibration scenario (not raw model output).
      // ═══════════════════════════════════════════════════════════════════
      if (rfiOverridesEnabled && rfiOverrides && Object.keys(rfiOverrides).length > 0) {
        pdf.setFillColor(252, 211, 77);   // amber-300
        pdf.rect(margin, yPos, contentWidth, 9, 'F');
        pdf.setTextColor(120, 53, 15);    // amber-900
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        const nOverrides = Object.keys(rfiOverrides).length;
        const scenarioLabel = rfiScenario.scenarioName || 'Untitled scenario';
        const statusLabel = rfiScenario.scenarioStatus || 'draft';
        pdf.text(
          `ADJUSTED FEEDING RECOMMENDATION SCENARIO — "${scenarioLabel}" (${statusLabel}) · ${nOverrides} day${nOverrides === 1 ? '' : 's'} modified`,
          margin + 2,
          yPos + 6,
        );
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'normal');
        yPos += 12;
      }

      // ═══════════════════════════════════════════════════════════════════
      // Helper: Capture a DOM element to canvas
      // ═══════════════════════════════════════════════════════════════════
      const captureElement = async (element, fixedWidth = 1200) => {
        if (!element || element.offsetHeight < 5) return null;
        
        const originalStyle = element.style.cssText;
        
        // Hide PDF-excluded elements
        const hiddenEls = element.querySelectorAll('[data-pdf-hide]');
        const hiddenOriginals = [];
        hiddenEls.forEach(el => {
          hiddenOriginals.push({ el, display: el.style.display });
          el.style.display = 'none';
        });
        
        // Force consistent width
        element.style.width = `${fixedWidth}px`;
        element.style.minWidth = `${fixedWidth}px`;
        element.style.maxWidth = `${fixedWidth}px`;
        element.style.backgroundColor = '#ffffff';
        element.style.overflow = 'visible';
        
        // Trigger resize for charts
        const hasCharts = element.querySelector('svg.recharts-surface');
        if (hasCharts) {
          window.dispatchEvent(new Event('resize'));
          await new Promise(r => setTimeout(r, 300));
        } else {
          await new Promise(r => setTimeout(r, 50));
        }
        
        let canvas = null;
        try {
          canvas = await Promise.race([
            html2canvas(element, {
              scale: 2,
              useCORS: true,
              logging: false,
              backgroundColor: '#ffffff',
              scrollX: 0,
              scrollY: -window.scrollY,
            }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 15000))
          ]);
        } catch (e) {
          
        }
        
        // Restore
        element.style.cssText = originalStyle;
        hiddenOriginals.forEach(({ el, display }) => { el.style.display = display; });
        if (hasCharts) window.dispatchEvent(new Event('resize'));
        
        return canvas;
      };

      // ═══════════════════════════════════════════════════════════════════
      // Helper: Add canvas to PDF with section-aware page breaks
      // ═══════════════════════════════════════════════════════════════════
      const addSectionToPdf = (canvas, forceNewPage = false) => {
        if (!canvas || canvas.height < 10) return;
        
        const imgData = canvas.toDataURL('image/jpeg', 0.9);
        const ratio = contentWidth / canvas.width;
        const sectionHeight = canvas.height * ratio;
        const remainingSpace = pdfHeight - margin - yPos;
        
        // Force page break if requested
        if (forceNewPage && yPos > margin + 5) {
          pdf.addPage();
          pageNum++;
          yPos = margin;
        }
        
        // If section doesn't fit, start new page
        if (sectionHeight > remainingSpace && yPos > margin + 5) {
          pdf.addPage();
          pageNum++;
          yPos = margin;
        }
        
        // Handle oversized sections (taller than full page)
        if (sectionHeight > availableHeight) {
          const pxPerMm = canvas.height / sectionHeight;
          let remainingPx = canvas.height;
          let srcY = 0;
          
          while (remainingPx > 10) {
            const spaceLeft = pdfHeight - margin - yPos;
            const slicePx = Math.min(remainingPx, Math.floor(spaceLeft * pxPerMm));
            
            if (slicePx < 30) {
              pdf.addPage();
              pageNum++;
              yPos = margin;
              continue;
            }
            
            const sliceCanvas = document.createElement('canvas');
            sliceCanvas.width = canvas.width;
            sliceCanvas.height = slicePx;
            const ctx = sliceCanvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, sliceCanvas.width, slicePx);
            ctx.drawImage(canvas, 0, srcY, canvas.width, slicePx, 0, 0, canvas.width, slicePx);
            
            const sliceData = sliceCanvas.toDataURL('image/jpeg', 0.9);
            const sliceHeightMm = slicePx / pxPerMm;
            pdf.addImage(sliceData, 'JPEG', margin, yPos, contentWidth, sliceHeightMm);
            
            srcY += slicePx;
            remainingPx -= slicePx;
            yPos += sliceHeightMm;
            
            if (remainingPx > 10) {
              pdf.addPage();
              pageNum++;
              yPos = margin;
            }
          }
        } else {
          pdf.addImage(imgData, 'JPEG', margin, yPos, contentWidth, sectionHeight);
          yPos += sectionHeight + 3;
        }
      };

      // ═══════════════════════════════════════════════════════════════════
      // PAGE 1: EXECUTIVE SUMMARY
      // Rendered from DecisionReportPdf component via pdfReportRef
      // ═══════════════════════════════════════════════════════════════════
      const summaryEl = pdfReportRef.current?.querySelector('[data-pdf-section="executive-summary"]');
      if (summaryEl) {
        try {
          const canvas = await captureElement(summaryEl);
          addSectionToPdf(canvas, false);
        } catch (e) { /* capture failed */ }
      } else {
        // Fallback: Capture the in-app Summary Report View if DecisionReportPdf not rendered
        const summaryView = reportRef.current?.querySelector('[data-testid="summary-report-view"]');
        if (summaryView) {
          try {
            const canvas = await captureElement(summaryView);
            addSectionToPdf(canvas, false);
          } catch (e) { /* capture failed */ }
        }
      }

      // ═══════════════════════════════════════════════════════════════════
      // PAGE 2: KEY CHARTS (Growth Comparison + Feed Variance)
      // Only charts that support the conclusion, no redundant metrics
      // ═══════════════════════════════════════════════════════════════════
      if (pdfChartsRef.current) {
        // Force new page for charts
        pdf.addPage();
        pageNum++;
        yPos = margin;
        
        // Add a simple header for charts page
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 30, 30);
        pdf.text('Performance Charts', margin, yPos + 5);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        pdf.text('Growth trajectory and feed variance analysis', margin, yPos + 11);
        yPos += 18;
        
        // Capture individual chart sections
        const chartChildren = Array.from(pdfChartsRef.current.children).filter(c => {
          if (c.offsetHeight < 50) return false;
          if (c.dataset?.pdfHide) return false;
          // Skip controls and input panels
          const hasControls = c.querySelector('[data-pdf-hide="true"]');
          const isInputPanel = c.querySelector('input, select, button:not([data-pdf-export])');
          return !hasControls && !isInputPanel;
        });
        
        // Only include the main charts (Growth Chart + Feed Variance)
        // Skip: FCR Analysis panels, detailed breakdowns, etc.
        for (let i = 0; i < Math.min(chartChildren.length, 2); i++) {
          const chartSection = chartChildren[i];
          
          // Skip if it's an interactive panel (FCR inputs, variance inputs, etc.)
          const isInteractivePanel = 
            chartSection.querySelector('[data-testid="variance-ymax-input"]') ||
            chartSection.querySelector('[data-testid="feed-chart-line-controls"]') ||
            chartSection.querySelector('[data-testid="section-visibility-controls"]');
          
          if (isInteractivePanel) continue;
          
          try {
            const canvas = await captureElement(chartSection);
            if (canvas && canvas.height > 100) {
              addSectionToPdf(canvas, false);
            }
          } catch (e) { /* capture failed */ }
        }
      }

      // ═══════════════════════════════════════════════════════════════════
      // PAGE 3: CONDENSED MISSING FISH INTELLIGENCE
      // SSOT-driven summary, no raw tables, no per-sample data
      // ═══════════════════════════════════════════════════════════════════
      const mfiCondensed = pdfReportRef.current?.querySelector('[data-pdf-section="mfi-condensed"]');
      if (mfiCondensed) {
        try {
          const canvas = await captureElement(mfiCondensed);
          addSectionToPdf(canvas, true); // Force new page for MFI
        } catch (e) { /* capture failed */ }
      } else if (sectionVisibility.intelligenceReport) {
        // Fallback: capture a condensed version of the on-screen MFI
        const mfiWrapper = reportRef.current?.querySelector('[data-testid="missing-fish-intelligence-results"]');
        if (mfiWrapper) {
          pdf.addPage();
          pageNum++;
          yPos = margin;
          
          // Capture only the key MFI sections, not tables
          const mfiSections = mfiWrapper.querySelectorAll('[data-pdf-section]');
          const prioritySections = ['mfi-header', 'mfi-reconciliation', 'mfi-summary', 'mfi-quality-indicators', 'mfi-economic-impact'];
          
          for (const sectionName of prioritySections) {
            const section = Array.from(mfiSections).find(s => s.dataset?.pdfSection === sectionName);
            if (section && section.offsetHeight > 20) {
              try {
                const canvas = await captureElement(section);
                if (canvas && canvas.height > 30) {
                  addSectionToPdf(canvas, false);
                }
              } catch (e) { /* skip */ }
            }
          }
        }
      }

      // ═══════════════════════════════════════════════════════════════════
      // EXCLUDED FROM PDF (decision-grade report):
      // - Daily Data Table (sectionVisibility.dataTable)
      // - Per-sample deviation tables
      // - Economic parameters input panel
      // - Analytical signals detail section
      // - UI controls, buttons, form inputs
      // ═══════════════════════════════════════════════════════════════════

      // Final page count check
      if (pageNum > 5) {
        
      }

      // ═══════════════════════════════════════════════════════════════════
      // Adjusted Feeding Recommendation Scenario — appendix.
      // Lists every modified date with original vs adjusted recommended feed
      // plus the cumulative delta. Always pushed onto its own new page so it
      // is self-contained and obviously secondary to the main report.
      // ═══════════════════════════════════════════════════════════════════
      if (rfiOverridesEnabled && rfiOverrides && Object.keys(rfiOverrides).length > 0) {
        pdf.addPage();
        pageNum++;
        let appY = margin;

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(13);
        pdf.text('Appendix — Adjusted Feeding Recommendation Scenario', margin, appY + 6);
        appY += 12;

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        const scenarioLabel = rfiScenario.scenarioName || 'Untitled scenario';
        const statusLabel = rfiScenario.scenarioStatus || 'draft';
        pdf.text(`Scenario: "${scenarioLabel}" · Status: ${statusLabel}`, margin, appY); appY += 5;
        pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, appY); appY += 7;

        // Sorted list of modified dates and totals.
        const entries = Object.entries(rfiOverrides)
          .map(([date, v]) => ({
            date,
            original: Number(v.original_recommended_kg ?? 0),
            adjusted: Number(v.adjusted_recommended_kg ?? 0),
          }))
          .sort((a, b) => a.date.localeCompare(b.date));
        const totalOrig = entries.reduce((s, e) => s + e.original, 0);
        const totalAdj = entries.reduce((s, e) => s + e.adjusted, 0);

        // Header row
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        const colDate = margin;
        const colOrig = margin + 60;
        const colAdj = margin + 105;
        const colDelta = margin + 150;
        pdf.text('Date', colDate, appY);
        pdf.text('Original Rec (kg)', colOrig, appY);
        pdf.text('Adjusted Rec (kg)', colAdj, appY);
        pdf.text('Δ kg', colDelta, appY);
        appY += 4;
        pdf.setLineWidth(0.2);
        pdf.line(margin, appY, margin + contentWidth, appY);
        appY += 3;

        pdf.setFont('helvetica', 'normal');
        for (const e of entries) {
          if (appY > pdfHeight - margin - 12) {
            pdf.addPage();
            pageNum++;
            appY = margin;
          }
          pdf.text(e.date, colDate, appY);
          pdf.text(e.original.toFixed(2), colOrig, appY);
          pdf.text(e.adjusted.toFixed(2), colAdj, appY);
          const d = e.adjusted - e.original;
          pdf.text((d >= 0 ? '+' : '') + d.toFixed(2), colDelta, appY);
          appY += 4.5;
        }

        // Totals row
        if (appY > pdfHeight - margin - 16) { pdf.addPage(); pageNum++; appY = margin; }
        appY += 2;
        pdf.line(margin, appY, margin + contentWidth, appY);
        appY += 4;
        pdf.setFont('helvetica', 'bold');
        pdf.text('Cumulative', colDate, appY);
        pdf.text(totalOrig.toFixed(2), colOrig, appY);
        pdf.text(totalAdj.toFixed(2), colAdj, appY);
        const totalDelta = totalAdj - totalOrig;
        pdf.text((totalDelta >= 0 ? '+' : '') + totalDelta.toFixed(2), colDelta, appY);
        appY += 8;

        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(8);
        pdf.text(
          'All RFI, accumulated RFI, and feed-waste metrics in this report are computed using the adjusted recommendations above. Original imported model recommendations remain immutable on the underlying record.',
          margin,
          appY,
          { maxWidth: contentWidth },
        );
      }

      // Save PDF
      const fileName = `decision_report_${activeRecord.name.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        const blob = pdf.output('blob');
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      } else {
        pdf.save(fileName);
      }

      toast.success(`Decision report exported! (${pageNum} pages)`);
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  // =====================================================
  // FOUNDATION DATASET: daily_analysis_rows
  // Single source of truth for all spreadsheet analytics.
  // All charts, tables, summaries, and exports read from this.
  // Built once immediately after activeRecord is loaded.
  // =====================================================
  const daily_analysis_rows = useMemo(() => {
    if (!activeRecord || !activeRecord.data_points) return [];
    
    // Apply the shared CLOSED-cage closing-endpoint rule at the trajectory
    // data-preparation level. For CLOSED cages this truncates all downstream
    // slices at the last registered harvest date/weight so no chart, KPI,
    // MFI/RFI, FCR, feeding, or PDF path shows data after the closing point.
    // OPEN cages pass through unchanged — zero behavioural drift.
    const closureSlicedRecord = sliceRecordAtClosure(activeRecord);
    const rawDataPoints = closureSlicedRecord.data_points || [];
    const rawModelTrajectory = closureSlicedRecord.model_trajectory || [];
    const rawActualTrajectory = closureSlicedRecord.actual_trajectory || [];

    // ── Sample-exclusion recomputation (RULE-IMP-SMP-001 /
    // PFD-BUG-007 + PFD-BUG-008). Rebuilds the actual-growth
    // trajectory with user-excluded sample anchors removed. Uses
    // DATE-BASED identity (not ordinal) so the mapping between the
    // Per-Sample Deviation checkboxes and the trajectory anchors is
    // stable against is_sample flag drift. The arrival weight
    // (`input_average_weight`) stays as the Day 0 baseline and is
    // never excluded. Historical daily feed quantities are untouched —
    // only `actual_weight_g` (and anything derived from it) recomputes.
    const effectiveRawActualTrajectory = recomputeActualTrajectoryWithExclusions(
      rawActualTrajectory,
      rawModelTrajectory,
      excludedSampleIndices,
      {
        samplePoints: closureSlicedRecord.sample_points,
        inputAverageWeight: activeRecord.input_average_weight,
      },
    );

    // ★ KPI Cut-Off at First Harvest — truncate data arrays at the first harvest
    //   event (inclusive) so post-harvest starvation/empty-cage days do not
    //   distort FCR, feeding, and missing-fish KPIs. No-op when the flag is
    //   off or when no harvest event exists in the dataset.
    let cutoffIdx = -1;
    if (activeRecord.kpi_cutoff_at_first_harvest) {
      for (let i = 0; i < rawDataPoints.length; i++) {
        const p = rawDataPoints[i];
        const hasHarvest = (
          (p?.harvest_weight !== null && p?.harvest_weight !== undefined && p?.harvest_weight !== 0) ||
          (p?.harvest_number !== null && p?.harvest_number !== undefined && p?.harvest_number !== 0)
        );
        if (hasHarvest) { cutoffIdx = i; break; }
      }
    }
    const dataPoints = cutoffIdx >= 0 ? rawDataPoints.slice(0, cutoffIdx + 1) : rawDataPoints;
    const modelTrajectory = cutoffIdx >= 0 ? rawModelTrajectory.slice(0, cutoffIdx + 1) : rawModelTrajectory;
    const actualTrajectory = cutoffIdx >= 0 ? effectiveRawActualTrajectory.slice(0, cutoffIdx + 1) : effectiveRawActualTrajectory;
    
    // Effective fish count: use adjusted count when active, otherwise reported
    const effectiveInitialFishCount = assumedFishNumber || activeRecord.input_number || 0;
    
    // Track running totals
    let cumulativeFeed = 0;
    let cumulativeMortality = 0;
    let currentFishCount = effectiveInitialFishCount;
    
    const rows = [];
    
    // Build unified rows - each row has ALL data needed for any analysis
    for (let idx = 0; idx < dataPoints.length; idx++) {
      const dp = dataPoints[idx];
      const modelPt = modelTrajectory[idx];
      const actualPt = actualTrajectory[idx];
      
      // Skip truly empty rows (no date)
      if (!dp || !dp.date) continue;
      
      // ========== WEIGHTS ==========
      // model_weight_g: From installed growth model
      let model_weight_g = null;
      if (modelPt && modelPt.weight !== undefined && modelPt.weight !== null) {
        model_weight_g = typeof modelPt.weight === 'number' ? modelPt.weight : parseFloat(modelPt.weight);
        if (isNaN(model_weight_g)) model_weight_g = null;
      }
      
      // actual_weight_g: From reconstructed growth path (updates at sample weights)
      let actual_weight_g = null;
      if (actualPt && actualPt.weight !== undefined && actualPt.weight !== null) {
        actual_weight_g = typeof actualPt.weight === 'number' ? actualPt.weight : parseFloat(actualPt.weight);
        if (isNaN(actual_weight_g)) actual_weight_g = null;
      }
      
      // ========== MORTALITY & FISH COUNT ==========
      // daily_mortality: From spreadsheet input
      const daily_mortality = typeof dp.mortality === 'number' ? dp.mortality : 0;
      
      // Update cumulative mortality and fish count
      cumulativeMortality += daily_mortality;
      currentFishCount = Math.max(0, effectiveInitialFishCount - cumulativeMortality);
      
      // Handle harvest events (reduce fish count)
      const harvestNumber = dp.harvest_number || 0;
      if (harvestNumber > 0) {
        currentFishCount = Math.max(0, currentFishCount - harvestNumber);
      }
      
      // fish_count: Current fish count after mortality and harvests
      const fish_count = currentFishCount;
      
      // ========== BIOMASS ==========
      // model_biomass_kg = model_weight_g × fish_count / 1000
      const model_biomass_kg = (model_weight_g !== null && fish_count > 0)
        ? (model_weight_g * fish_count) / 1000
        : null;
      
      // actual_biomass_kg = actual_weight_g × fish_count / 1000
      const actual_biomass_kg = (actual_weight_g !== null && fish_count > 0)
        ? (actual_weight_g * fish_count) / 1000
        : null;
      
      // ========== FEED ==========
      // daily_feed_kg: Directly from spreadsheet input
      const daily_feed_kg = typeof dp.feed === 'number' ? dp.feed : null;
      
      // cumulative_feed_kg: Running sum of daily_feed_kg
      if (daily_feed_kg !== null) {
        cumulativeFeed += daily_feed_kg;
      }
      const cumulative_feed_kg = cumulativeFeed;
      
      // ========== TEMPERATURE ==========
      const temperature = typeof dp.temperature === 'number' ? dp.temperature : null;
      
      // ========== RECOMMENDED FEED (SFR-based) ==========
      // recommended_feed_kg = actual_biomass_kg × (SFR_percent / 100)
      // Uses nearest lookup only - no interpolation - one lookup per row
      let recommended_feed_kg = null;
      let sfr_percent = null;
      
      if (actual_biomass_kg !== null && actual_weight_g !== null && temperature !== null) {
        // Use imported lookupSFR function — species-aware so Tilapia / Seabass
        // use their own SFR tables rather than the Seabream default.
        sfr_percent = lookupSFR(actual_weight_g, temperature, activeRecord?.species || species);
        
        if (sfr_percent !== null) {
          recommended_feed_kg = actual_biomass_kg * (sfr_percent / 100);
          recommended_feed_kg = Math.round(recommended_feed_kg * 100) / 100; // Round to 2 decimal places
        }
      }
      
      // ========== FEED VARIANCE (PERCENTAGE) ==========
      // feed_variance_percent = (daily_feed_kg / recommended_feed_kg) * 100
      // 100% = match, <100% = underfeeding, >100% = overfeeding
      let feed_variance_percent = null;
      if (daily_feed_kg !== null && recommended_feed_kg !== null && recommended_feed_kg > 0) {
        feed_variance_percent = (daily_feed_kg / recommended_feed_kg) * 100;
        feed_variance_percent = Math.round(feed_variance_percent * 10) / 10; // 1 decimal place
      }
      
      // ========== BUILD ROW ==========
      rows.push({
        // Core identifiers
        date: dp.date,
        day_index: idx,
        
        // Weights (grams)
        model_weight_g,
        actual_weight_g,
        
        // Fish count
        fish_count,
        daily_mortality,
        cumulative_mortality: cumulativeMortality,
        
        // Biomass (kg)
        model_biomass_kg: model_biomass_kg !== null ? Math.round(model_biomass_kg * 100) / 100 : null,
        actual_biomass_kg: actual_biomass_kg !== null ? Math.round(actual_biomass_kg * 100) / 100 : null,
        
        // Feed (kg)
        daily_feed_kg,
        cumulative_feed_kg: Math.round(cumulative_feed_kg * 100) / 100,
        recommended_feed_kg,
        feed_variance_percent,
        sfr_percent,
        
        // Temperature
        temperature,
        
        // Sample/Harvest markers (for UI)
        is_sample: actualPt?.is_sample || false,
        sample_weight: dp.sample_weight || null,
        harvest_weight: dp.harvest_weight || null,
        harvest_number: dp.harvest_number || null,
        
        // Legacy compatibility (for existing chart code)
        model_weight: model_weight_g,
        actual_weight: actual_weight_g,
        feed: daily_feed_kg,
        mortality: daily_mortality,
      });
    }
    
    // Sort by date ascending (critical for chart rendering and cumulative calculations)
    rows.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA - dateB;
    });
    
    // Recalculate cumulative values after sorting (in case dates were out of order)
    let sortedCumulativeFeed = 0;
    let sortedCumulativeRecommendedFeed = 0;
    let sortedCumulativeMortality = 0;
    let sortedFishCount = effectiveInitialFishCount;
    
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      
      // Update mortality
      sortedCumulativeMortality += row.daily_mortality;
      row.cumulative_mortality = sortedCumulativeMortality;
      
      // Update fish count (subtract harvests too)
      if (row.harvest_number > 0) {
        sortedFishCount = Math.max(0, sortedFishCount - row.harvest_number);
      }
      sortedFishCount = Math.max(0, effectiveInitialFishCount - sortedCumulativeMortality);
      if (row.harvest_number > 0) {
        sortedFishCount = Math.max(0, sortedFishCount - row.harvest_number);
      }
      row.fish_count = sortedFishCount;
      
      // Recalculate biomass with corrected fish count
      if (row.model_weight_g !== null) {
        row.model_biomass_kg = Math.round((row.model_weight_g * row.fish_count) / 1000 * 100) / 100;
      }
      if (row.actual_weight_g !== null) {
        row.actual_biomass_kg = Math.round((row.actual_weight_g * row.fish_count) / 1000 * 100) / 100;
        
        // Recalculate recommended feed with corrected biomass
        if (row.sfr_percent !== null) {
          row.recommended_feed_kg = Math.round(row.actual_biomass_kg * (row.sfr_percent / 100) * 100) / 100;
          
          // Recalculate variance as percentage (Actual/Recommended * 100)
          if (row.daily_feed_kg !== null && row.recommended_feed_kg > 0) {
            row.feed_variance_percent = Math.round((row.daily_feed_kg / row.recommended_feed_kg) * 100 * 10) / 10;
          }
        }
      }
      
      // Update cumulative feed
      if (row.daily_feed_kg !== null) {
        sortedCumulativeFeed += row.daily_feed_kg;
      }
      row.cumulative_feed_kg = Math.round(sortedCumulativeFeed * 100) / 100;
      
      // ========== ACCUMULATED FEED CALCULATIONS ==========
      // accumulated_recommended_feed_kg: Running sum of recommended_feed_kg
      if (row.recommended_feed_kg !== null) {
        sortedCumulativeRecommendedFeed += row.recommended_feed_kg;
      }
      row.accumulated_recommended_feed_kg = Math.round(sortedCumulativeRecommendedFeed * 100) / 100;
      
      // accumulated_feed_difference_kg = accumulated_actual - accumulated_recommended
      // Positive = overfeeding, Negative = underfeeding
      row.accumulated_feed_difference_kg = Math.round((sortedCumulativeFeed - sortedCumulativeRecommendedFeed) * 100) / 100;
      
      // accumulated_feed_difference_percent = (difference / accumulated_recommended) × 100
      if (sortedCumulativeRecommendedFeed > 0) {
        row.accumulated_feed_difference_percent = Math.round(
          ((sortedCumulativeFeed - sortedCumulativeRecommendedFeed) / sortedCumulativeRecommendedFeed) * 100 * 10
        ) / 10; // 1 decimal place
      } else {
        row.accumulated_feed_difference_percent = null;
      }
    }

    // ─── Admin RFI override SSOT injection ────────────────────────────────
    // When the admin has an active Daily Recommendation Adjustment scenario
    // loaded, replace each row's `recommended_feed_kg` with the adjusted
    // value (the hook preserves the original on `original_recommended_feed_kg`).
    // We then re-derive every dependent per-row + cumulative field so EVERY
    // downstream consumer (main Feed Analysis chart, secondary Feed Variance
    // Comparison chart, KPI tiles, narratives, PDF) reads adjusted SSOT
    // values without any duplicated RFI logic.
    if (rfiOverridesEnabled && rfiOverrides && Object.keys(rfiOverrides).length > 0) {
      for (let i = 0; i < rows.length; i++) {
        rows[i] = rfiApplyOverride(rows[i]);
      }
      // Recompute per-row + cumulative derivations using the now-adjusted
      // recommended_feed_kg values.
      let cumActual = 0;
      let cumRecAdj = 0;
      let sumRec = 0;
      for (const row of rows) {
        // 1) feed_variance_percent — primary "Daily Variance %" series on the
        //    main Feed Analysis chart and Daily-Variance KPI tiles.
        if (row.daily_feed_kg != null && row.recommended_feed_kg != null && row.recommended_feed_kg > 0) {
          row.feed_variance_percent = Math.round((row.daily_feed_kg / row.recommended_feed_kg) * 100 * 10) / 10;
        } else {
          row.feed_variance_percent = null;
        }

        // 2) accumulated_recommended_feed_kg — running sum over every day
        //    that contributes a recommendation. Drives the chart's "Accum.
        //    Recommended" right-axis line and Accum-Variance tooltip math.
        sumRec += (row.recommended_feed_kg || 0);
        row.accumulated_recommended_feed_kg = Math.round(sumRec * 100) / 100;

        // 3) feed-days-only cumulative trackers — denominator for the
        //    Accumulated RFI (numerator is cumulative actual; matches the
        //    no-feed-day fix in §6d so the metric stays apples-to-apples).
        if ((row.daily_feed_kg || 0) > 0) {
          cumActual += row.daily_feed_kg;
          if ((row.recommended_feed_kg || 0) > 0) cumRecAdj += row.recommended_feed_kg;
        }

        // 4) accumulated_feed_difference_percent — RFI deviation column used
        //    downstream by the Feed Variance Comparison panel and the SSOT
        //    narrative generator.
        if (cumRecAdj > 0) {
          row.accumulated_feed_difference_percent = Math.round(
            ((cumActual - cumRecAdj) / cumRecAdj) * 100 * 10,
          ) / 10;
        }
      }
    }

    return rows;
  }, [activeRecord, assumedFishNumber, sfrTableVersion, rfiOverrides, rfiOverridesEnabled, rfiApplyOverride, excludedSampleIndices]);

  // =====================================================
  // LEGACY ALIAS: dailyRows (for backward compatibility)
  // All existing code that uses dailyRows will continue to work
  // =====================================================
  const dailyRows = daily_analysis_rows;

  // ═══════════════════════════════════════════════════════════════════════════
  // VERIFIED HARVEST CORRECTION — signals when historical routine samples were
  // likely underestimating true fish weight, confirmed by harvest data.
  // See /app/frontend/src/utils/verifiedHarvestCorrection.js for the full
  // activation criteria and confidence rules.
  // ═══════════════════════════════════════════════════════════════════════════
  // ═══════════════════════════════════════════════════════════════════════════
  // VERIFIED / POSSIBLE HARVEST CORRECTION
  // ─────────────────────────────────────────────────────────────────────────
  // Single source of truth = the SERVER-PERSISTED signal on the record
  // (`activeRecord.harvest_correction_summary`). Computed during upload /
  // reupload / reanalyze on the backend so the interpretation is stable
  // across sessions and reproducible for PDF and Wingman.
  //
  // Fallback: if the persisted summary is absent (legacy records uploaded
  // before this feature shipped), we recompute client-side so old records
  // still get the analytical signal. As soon as the record is reanalysed or
  // reuploaded, the persisted value takes over.
  // ═══════════════════════════════════════════════════════════════════════════
  const verifiedHarvestCorrection = useMemo(() => {
    const persisted = activeRecord?.harvest_correction_summary;
    if (persisted && (persisted.status === 'verified' || persisted.status === 'possible')) {
      return {
        status: persisted.status,
        trigger: persisted.trigger || null,
        confirmations: persisted.confirmations || [],
        suggested_text: persisted.suggested_text || '',
        wingman_text: persisted.wingman_text || '',
        _persisted: true,
      };
    }
    // No persisted signal — fallback to local recompute for legacy records.
    if (!activeRecord?.sample_points?.length || !daily_analysis_rows?.length) {
      return { status: 'none', trigger: null, confirmations: [], suggested_text: '', wingman_text: '' };
    }
    return {
      ...detectVerifiedHarvestCorrection({
        samplePoints: activeRecord.sample_points,
        dailyRows: daily_analysis_rows,
      }),
      _persisted: false,
    };
  }, [activeRecord?.harvest_correction_summary, activeRecord?.sample_points, daily_analysis_rows]);
  const vhcActive = verifiedHarvestCorrection?.status === 'verified' || verifiedHarvestCorrection?.status === 'possible';

  // Publish the harvest-correction signal to the global SSOT so Wingman can
  // read it from any chat session. Updates whenever the signal changes.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const existing = window.__pfd_active_record_ssot || {};
    if (vhcActive && verifiedHarvestCorrection?.status !== 'none') {
      window.__pfd_active_record_ssot = {
        ...existing,
        harvestCorrection: {
          status: verifiedHarvestCorrection.status,
          trigger_date: verifiedHarvestCorrection.trigger?.date,
          prior_weight_g: verifiedHarvestCorrection.trigger?.prior_weight_g,
          new_weight_g: verifiedHarvestCorrection.trigger?.new_weight_g,
          multiplier: verifiedHarvestCorrection.trigger?.multiplier,
          percent_above_model: verifiedHarvestCorrection.trigger?.percent_above_model,
          confirmation_count: verifiedHarvestCorrection.confirmations?.length || 0,
          wingman_text: verifiedHarvestCorrection.wingman_text,
        },
      };
    } else if (existing.harvestCorrection) {
      // Strip when no longer active so stale signals don't follow a new record.
      const { harvestCorrection: _drop, ...rest } = existing;
      window.__pfd_active_record_ssot = rest;
    }
  }, [vhcActive, verifiedHarvestCorrection]);

  // ═══════════════════════════════════════════════════════════════════════════
  // KPI CUT-OFF AT FIRST HARVEST — derive truncated KPIs for header/summary
  // ═══════════════════════════════════════════════════════════════════════════
  // When the operator enables `kpi_cutoff_at_first_harvest` we already truncate
  // the underlying `daily_analysis_rows` array (see useMemo above). However,
  // several aggregate KPIs displayed in the report header / summary panels read
  // pre-computed totals directly from `activeRecord.*` (total_feed_given,
  // total_mortalities, total_harvest_count, feeding_days, no_feed_days,
  // days_in_water, percent_feeding_of_model). Those backend totals span the
  // ENTIRE dataset and continue to include post-harvest starvation/empty-cage
  // days even when truncation is on — that's the bug the user reported.
  //
  // We rebuild those aggregates from the truncated daily_analysis_rows here
  // and expose them through `effectiveActiveRecord` so every downstream
  // header/summary/PDF surface sees the cut-off view consistently.
  //
  // No calculation/threshold change — pure data-window enforcement.
  const effectiveActiveRecord = useMemo(() => {
    if (!activeRecord) return null;
    if (!activeRecord.kpi_cutoff_at_first_harvest) return activeRecord;
    const rows = daily_analysis_rows || [];
    if (rows.length === 0) return activeRecord;
    let totalFeed = 0;
    let totalMortalities = 0;
    let totalHarvest = 0;
    let feedingDays = 0;
    let noFeedDays = 0;
    let totalRecommendedFeed = 0;
    for (const r of rows) {
      const dailyFeed = Number(r.daily_feed_kg || 0);
      const recFeed = Number(r.recommended_feed_kg || 0);
      totalFeed += dailyFeed;
      totalMortalities += Number(r.daily_mortality || 0);
      totalHarvest += Number(r.harvest_number || 0);
      totalRecommendedFeed += recFeed;
      if (dailyFeed > 0) feedingDays += 1;
      else if (recFeed > 0) noFeedDays += 1;
    }
    const percentFeedingOfModel = totalRecommendedFeed > 0
      ? Math.round((totalFeed / totalRecommendedFeed) * 1000) / 10 // 1-decimal pct
      : null;
    return {
      ...activeRecord,
      total_feed_given: Math.round(totalFeed * 100) / 100,
      total_mortalities: totalMortalities,
      total_harvest_count: totalHarvest,
      feeding_days: feedingDays,
      no_feed_days: noFeedDays,
      days_in_water: rows.length,
      percent_feeding_of_model: percentFeedingOfModel,
      // Mark for downstream consumers
      _kpiCutoffApplied: true,
      _kpiCutoffRowCount: rows.length,
    };
  }, [activeRecord, daily_analysis_rows]);


  // ═══════════════════════════════════════════════════════════════════════════
  // DYNAMIC FEED VARIANCE CALCULATION — Updates when fish count changes
  // ═══════════════════════════════════════════════════════════════════════════
  // These values are computed based on the CURRENT assumedFishNumber and update
  // in real-time when the user adjusts the fish count manually.
  // ═════════════════════════════════════════════════════════════════════════
  // Manual Fish Count Adjustment — derived missing-fish override
  // ─────────────────────────────────────────────────────────────────────────
  // When the user is in 'manual' mode and has typed a fish count that differs
  // from the originally reported count, the "Estimated Missing Fish" KPI and
  // every downstream surface (KPI banner, Summary view, Wingman SSOT) should
  // reflect the user's manual assertion: missing = max(0, reported - manual).
  // This bypasses the MFI-engine estimate because the user is explicitly
  // declaring the true count themselves.
  //
  // Returns `null` when not in manual mode (so the existing MFI value flows
  // through unchanged) — keeping automatic mode 100% backwards compatible.
  // ═════════════════════════════════════════════════════════════════════════
  const manualMissingFishOverride = useMemo(() => {
    if (fishEstimationMode !== 'manual') return null;
    if (!assumedFishNumber || !activeRecord?.input_number) return null;
    const reported = activeRecord.input_number;
    const manual = assumedFishNumber;
    if (manual === reported) return null; // no override needed
    return Math.max(0, reported - manual);
  }, [fishEstimationMode, assumedFishNumber, activeRecord?.input_number]);

  // Reflect the manual missing-fish override into the global Wingman SSOT so
  // Wingman's answers stay consistent with what the user sees on screen.
  // Cleared back to the engine value when the user leaves manual mode.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const existing = window.__pfd_active_record_ssot;
    if (!existing) return;
    if (manualMissingFishOverride !== null) {
      const label = manualMissingFishOverride > 0
        ? `${manualMissingFishOverride.toLocaleString()} fish (manual override)`
        : 'Within margin (manual override)';
      window.__pfd_active_record_ssot = {
        ...existing,
        missingFishRangeLabel: label,
        hasMissingFishSignal: manualMissingFishOverride > 0,
        manualOverride: {
          assumedFishNumber,
          reportedFishNumber: activeRecord?.input_number,
          missingFish: manualMissingFishOverride,
        },
      };
    } else if (existing.manualOverride) {
      // User left manual mode — strip the override and let runMissingFishIntelligence
      // republish the engine-derived value on the next analysis.
      const { manualOverride: _drop, ...rest } = existing;
      window.__pfd_active_record_ssot = rest;
    }
  }, [manualMissingFishOverride, assumedFishNumber, activeRecord?.input_number]);

  const dynamicFeedVariance = useMemo(() => {
    if (!assumedFishNumber || !activeRecord?.input_number || !daily_analysis_rows?.length) {
      return null;
    }  
    const reportedCount = activeRecord.input_number;
    const adjustedCount = assumedFishNumber;
    const feedCostPerKg = parseFloat(economicParams.average_feed_price_eur_per_kg) || 1.50;
    const splitDate = suspectedLossDate || null;
    
    // Track cumulative values using adjusted fish count
    let hybridFishCount = splitDate ? reportedCount : adjustedCount;
    let hybridCumulativeMortality = 0;
    let hasSwitched = false;
    
    let totalActualFeedKg = 0;
    let totalAdjustedRecommendedKg = 0;
    let periodWastedFeedKg = 0;
    
    daily_analysis_rows.forEach((row) => {
      const rowDate = row.date;
      
      // Switch from reported to adjusted count at the split date
      if (splitDate && !hasSwitched && rowDate >= splitDate) {
        hasSwitched = true;
        hybridFishCount = Math.max(0, adjustedCount - hybridCumulativeMortality);
      }
      
      hybridCumulativeMortality += row.daily_mortality || 0;
      if (!hasSwitched && splitDate) {
        hybridFishCount = Math.max(0, reportedCount - hybridCumulativeMortality);
      } else if (hasSwitched || !splitDate) {
        hybridFishCount = Math.max(0, adjustedCount - hybridCumulativeMortality);
      }
      if (row.harvest_number > 0) hybridFishCount = Math.max(0, hybridFishCount - row.harvest_number);
      
      // Calculate adjusted recommended feed
      let hybridRecommended = null;
      if (row.actual_weight_g !== null && hybridFishCount > 0 && row.sfr_percent !== null) {
        const hybridBiomass = (row.actual_weight_g * hybridFishCount) / 1000;
        hybridRecommended = Math.round(hybridBiomass * (row.sfr_percent / 100) * 100) / 100;
      }
      
      // Accumulate feed values
      const dailyActual = row.daily_feed_kg || 0;
      const dailyAdjustedRec = hybridRecommended || 0;
      if (dailyActual > 0) totalActualFeedKg += dailyActual;
      if (dailyAdjustedRec > 0) totalAdjustedRecommendedKg += dailyAdjustedRec;
      
      // Period wasted = sum of overfeeding per day
      if (dailyActual > 0 && dailyAdjustedRec > 0 && dailyActual > dailyAdjustedRec) {
        periodWastedFeedKg += (dailyActual - dailyAdjustedRec);
      }
    });
    
    const wastedFeedKg = Math.round(periodWastedFeedKg * 100) / 100;
    const feedCostImpact = wastedFeedKg > 0 ? Math.round(wastedFeedKg * feedCostPerKg * 100) / 100 : 0;
    
    return {
      wastedFeedKg,
      feedCostImpact,
      feedCostPerKg,
      totalActualFeedKg: Math.round(totalActualFeedKg * 100) / 100,
      totalAdjustedRecommendedKg: Math.round(totalAdjustedRecommendedKg * 100) / 100,
    };
  }, [assumedFishNumber, activeRecord?.input_number, daily_analysis_rows, suspectedLossDate, economicParams.average_feed_price_eur_per_kg]);

  // Mortality data derived from foundation dataset
  const mortalityData = useMemo(() => {
    return daily_analysis_rows.map(row => ({
      date: row.date,
      daily_mortality: row.daily_mortality,
      accumulated_mortality: row.cumulative_mortality
    }));
  }, [daily_analysis_rows]);

  // ── Biological FCR Breakdown ──
  // Reuses existing daily_analysis_rows — no new analysis engine
  const biologicalFCR = useMemo(() => {
    if (!activeRecord || daily_analysis_rows.length === 0) return null;

    const reportedN = activeRecord.input_number || 0;
    const startingWeightG = activeRecord.input_average_weight || 0;
    if (reportedN <= 0 || startingWeightG <= 0) return null;

    const lastRow = daily_analysis_rows[daily_analysis_rows.length - 1];
    const totalFeedKg = lastRow.cumulative_feed_kg || 0;
    if (totalFeedKg <= 0) return null;

    // Harvest biomass: sum of (harvest_number × harvest_weight_g) / 1000
    let harvestBiomassKg = 0;
    let totalHarvestedCount = 0;
    for (const row of daily_analysis_rows) {
      const hNum = row.harvest_number || 0;
      if (hNum > 0) {
        totalHarvestedCount += hNum;
        const hWeightG = row.harvest_weight || row.actual_weight_g || 0;
        harvestBiomassKg += (hNum * hWeightG) / 1000;
      }
    }

    // Mortality biomass: sum of (daily_mortality × actual_weight_g) / 1000
    let mortalityBiomassKg = 0;
    let totalMortalityCount = 0;
    for (const row of daily_analysis_rows) {
      const mort = row.daily_mortality || 0;
      if (mort > 0) {
        totalMortalityCount += mort;
        const weightG = row.actual_weight_g || row.model_weight_g || startingWeightG;
        mortalityBiomassKg += (mort * weightG) / 1000;
      }
    }

    // Remaining fish & last weight from trajectory
    const reportedRemaining = lastRow.fish_count || 0;
    const lastWeightG = lastRow.actual_weight_g || lastRow.model_weight_g || 0;
    // Total deductions (mortality + harvests) - used for adjusted remaining calc
    const totalDeductions = totalMortalityCount + totalHarvestedCount;

    return {
      totalFeedKg,
      startingWeightG,
      harvestBiomassKg,
      mortalityBiomassKg,
      reportedN,
      reportedRemaining,
      lastWeightG,
      totalDeductions,
    };
  }, [daily_analysis_rows, activeRecord]);

  // Shared FCR calculation — delegates to calculationEngine.calculateBiologicalFCR
  // Wraps the shared utility to handle cage-specific closed/deduction logic
  const calcClosingFCR = (n, startWeightG, lastWeightG, totalFeedKg, harvestBiomassKg, mortalityBiomassKg, totalDeductions, isClosed) => {
    const remaining = Math.max(0, n - totalDeductions);
    const currentWeightG = isClosed ? 0 : lastWeightG;
    const currentCount = isClosed ? 0 : remaining;
    const result = calculateBiologicalFCR({
      totalFeedKg,
      startWeightG,
      startCount: n,
      currentWeightG,
      currentCount,
      harvestBiomassKg,
      mortalityBiomassKg,
    });
    return {
      startBiomass: result.startBiomassKg,
      closingBiomass: result.closingBiomassKg,
      biomassGain: result.biomassGainKg,
      fcr: result.fcr,
      remaining,
    };
  };

  // Derived FCR — reported, model, adjusted, and FCR-based missing fish estimate
  // All use the shared calcClosingFCR engine — one calculation path
  const fcrResult = useMemo(() => {
    if (!biologicalFCR) return null;
    const { totalFeedKg, startingWeightG, harvestBiomassKg, mortalityBiomassKg, reportedN, lastWeightG, totalDeductions } = biologicalFCR;
    const isClosed = cageStatus === 'closed';

    const reported = calcClosingFCR(reportedN, startingWeightG, lastWeightG, totalFeedKg, harvestBiomassKg, mortalityBiomassKg, totalDeductions, isClosed);

    // Adjusted FCR uses assumed fish number when available
    const adjustedN = assumedFishNumber || reportedN;
    const adjusted = calcClosingFCR(adjustedN, startingWeightG, lastWeightG, totalFeedKg, harvestBiomassKg, mortalityBiomassKg, totalDeductions, isClosed);
    const estimatedMissing = reportedN - adjustedN;

    // FCR-based missing fish estimate: exact algebraic solution
    // Solve for n where calcClosingFCR(n, ...).fcr === modelFCR
    //   biomassGain = (n - D)*a + H - n*b   where a=lastWt/1000, b=startWt/1000
    //   modelFCR = totalFeed / biomassGain
    //   => n = (totalFeed/modelFCR + D*a - H) / (a - b)
    let fcrMissingFish = null;
    if (modelFCR && reported.fcr && reported.fcr < modelFCR * 0.95 && !isClosed) {
      const a = lastWeightG / 1000;   // closing weight per fish (kg)
      const b = startingWeightG / 1000; // starting weight per fish (kg)
      const H = harvestBiomassKg + mortalityBiomassKg;
      const D = totalDeductions;

      if (a > b) {
        const targetGain = totalFeedKg / modelFCR;
        const exactN = (targetGain + D * a - H) / (a - b);
        const roundedN = Math.round(exactN);

        if (roundedN > 0 && roundedN < reportedN) {
          const fcrEstMissing = reportedN - roundedN;
          const exactResult = calcClosingFCR(roundedN, startingWeightG, lastWeightG, totalFeedKg, harvestBiomassKg, mortalityBiomassKg, totalDeductions, false);
          fcrMissingFish = {
            detected: true,
            estimatedMissing: fcrEstMissing,
            adjustedN: roundedN,
            adjustedFCR: exactResult.fcr,
            fcrGapPercent: ((modelFCR - reported.fcr) / modelFCR * 100),
          };
        }
      }
    }

    return { reported, adjusted, reportedN, adjustedN, estimatedMissing, fcrMissingFish };
  }, [biologicalFCR, cageStatus, assumedFishNumber, modelFCR]);

  // Toggle sample exclusion. The actual recalculation (growth
  // trajectory, chart, SFR feed, Missing Fish Intelligence) is driven
  // by the `excludedSampleIndices` state through:
  //   • the `daily_analysis_rows` memo (recomputes the actual
  //     trajectory via `recomputeActualTrajectoryWithExclusions`), and
  //   • the useEffect below that re-runs MFI once the memo has
  //     settled with the new exclusion set — so MFI never sees a
  //     stale rows closure (PFD-BUG-008 fix).
  const handleToggleSampleExclusion = useCallback((sampleIndex) => {
    setExcludedSampleIndices((prev) => {
      const next = new Set(prev);
      const willExclude = !next.has(sampleIndex);
      if (willExclude) next.add(sampleIndex);
      else next.delete(sampleIndex);
      toast.success(`Sample ${willExclude ? 'excluded' : 'restored'} — analysis recalculated`);
      return next;
    });
  }, []);

  // Re-run Missing Fish Intelligence when `excludedSampleIndices`
  // changes AND MFI is already visible. We watch `daily_analysis_rows`
  // too so this effect fires AFTER the trajectory memo has rebuilt
  // with the new exclusion set — guaranteeing MFI operates on the
  // fresh rows (PFD-BUG-008). First render is skipped via a ref so
  // we don't re-run MFI the moment the panel is opened.
  const _mfiFirstPassRef = useRef(true);
  useEffect(() => {
    if (_mfiFirstPassRef.current) {
      _mfiFirstPassRef.current = false;
      return;
    }
    // PFD-BUG-012 — Manual Fish Count Adjustment is a user-authored
    // declaration. Never re-run MFI while the user is in manual mode:
    // this effect would otherwise clobber their typed count via
    // setAssumedFishNumber(reIntel.initialAssumedFishNumber) and flip
    // the mode back to 'automatic' every time daily_analysis_rows
    // re-memoised (which happens on every keystroke because the memo's
    // deps include assumedFishNumber at line 2981).
    if (fishEstimationMode === 'manual') return;
    if (!showMissingFishIntelligence || !missingFishIntelligence || !activeRecord) return;
    if (!daily_analysis_rows || daily_analysis_rows.length === 0) return;
    const reIntel = generateMissingFishIntelligence({
      daily_analysis_rows,
      activeRecord,
      economicParams: {
        average_feed_price_eur_per_kg: economicParams.average_feed_price_eur_per_kg,
        fish_price_eur_per_kg: economicParams.fish_price_eur_per_kg,
        target_harvest_weight_kg: economicParams.target_harvest_weight_kg,
      },
      weightBasedModelFCR: modelFCR,
      reportedClosingFCR: fcrResult?.reported?.fcr || null,
      closingBiomassData: biologicalFCR && fcrResult ? {
        totalFeedKg: biologicalFCR.totalFeedKg,
        startBiomassKg: fcrResult.reported.startBiomass,
        closingBiomassKg: fcrResult.reported.closingBiomass,
        harvestBiomassKg: biologicalFCR.harvestBiomassKg,
        mortalityBiomassKg: biologicalFCR.mortalityBiomassKg,
        reportedRemaining: fcrResult.reported.remaining,
        lastWeightG: biologicalFCR.lastWeightG,
      } : null,
      detectionMethod,
      fcrPanelMissingFish: fcrResult?.fcrMissingFish || null,
      excludedIndices: excludedSampleIndices,
      bypassSpeciesClamp: showAllDataOverride,
    });
    if (!reIntel) return;
    setAssumedFishNumber(reIntel.initialAssumedFishNumber);
    setMissingFishIntelligence(reIntel);
    setTrajectoryJump(reIntel.trajectoryJump);
    setDetectionResults(reIntel.detectionResults);
    setEconomicImpact(reIntel.economicImpact);
    setDataQuality(reIntel.dataQuality);
    setMortalityCheck(reIntel.mortalityCheck);
    setSamplingRecommendation(reIntel.samplingRecommendation);
    setOverfeedingRisk(reIntel.overfeedingRisk);
    setGraphInsight(reIntel.graphInsight);
    setFishEstimationMode('automatic');
  }, [excludedSampleIndices, daily_analysis_rows]);

  // ── Feed Intensity — Reported vs Adjusted ──
  // Same total feed, recalculated per-fish and per-biomass when fish number changes
  const feedIntensity = useMemo(() => {
    if (!biologicalFCR || daily_analysis_rows.length === 0) return null;
    const { totalFeedKg, reportedN, startingWeightG } = biologicalFCR;
    const adjustedN = assumedFishNumber || reportedN;
    if (reportedN <= 0 || totalFeedKg <= 0) return null;

    // Cumulative feed per fish (g per fish over full cycle)
    const reportedFeedPerFishG = (totalFeedKg * 1000) / reportedN;
    const adjustedFeedPerFishG = adjustedN > 0 ? (totalFeedKg * 1000) / adjustedN : 0;

    // Average daily feed rate (% of body weight per day)
    const feedRows = daily_analysis_rows.filter(r => (r.daily_feed_kg || 0) > 0);
    if (feedRows.length === 0) return null;

    let reportedRateSum = 0, adjustedRateSum = 0;
    for (const row of feedRows) {
      const dailyFeed = row.daily_feed_kg;
      const weightG = row.actual_weight_g || row.model_weight_g || startingWeightG;
      const rBiomass = (reportedN * weightG) / 1000;
      const aBiomass = adjustedN > 0 ? (adjustedN * weightG) / 1000 : 0;
      if (rBiomass > 0) reportedRateSum += (dailyFeed / rBiomass) * 100;
      if (aBiomass > 0) adjustedRateSum += (dailyFeed / aBiomass) * 100;
    }

    return {
      reportedFeedPerFishG,
      adjustedFeedPerFishG,
      avgReportedFeedRate: reportedRateSum / feedRows.length,
      avgAdjustedFeedRate: adjustedN > 0 ? adjustedRateSum / feedRows.length : 0,
      reportedN,
      adjustedN,
      isAdjusted: adjustedN !== reportedN,
    };
  }, [daily_analysis_rows, biologicalFCR, assumedFishNumber]);

  // Feed Recommendation Analysis - now reads from foundation dataset
  const feedRecommendationAnalysis = useMemo(() => {
    if (!activeRecord || daily_analysis_rows.length === 0) {
      return { available: false, message: 'No data available' };
    }
    
    // Calculate totals from foundation dataset
    const rowsWithFeed = daily_analysis_rows.filter(r => r.daily_feed_kg !== null);
    const rowsWithRecommendation = daily_analysis_rows.filter(r => r.recommended_feed_kg !== null);
    
    if (rowsWithFeed.length === 0 || rowsWithRecommendation.length === 0) {
      return { 
        available: false, 
        message: rowsWithFeed.length === 0 
          ? 'No feed data provided in spreadsheet' 
          : 'Unable to calculate feed recommendations (missing weight/temperature data)'
      };
    }
    
    // Calculate totals directly from foundation dataset
    const totalActualFeed = daily_analysis_rows.reduce((sum, r) => sum + (r.daily_feed_kg || 0), 0);
    const totalRecommendedFeed = daily_analysis_rows.reduce((sum, r) => sum + (r.recommended_feed_kg || 0), 0);
    
    // Feed variance as percentage: (Actual / Recommended) * 100
    // 100% = match, <100% = underfeeding, >100% = overfeeding
    const totalFeedPercent = totalRecommendedFeed > 0 
      ? (totalActualFeed / totalRecommendedFeed) * 100 
      : null;
    
    // Use imported classification function (adjusted for new percentage format)
    // Convert to old variance format for compatibility: (percent - 100)
    const oldStyleVariance = totalFeedPercent !== null ? totalFeedPercent - 100 : null;
    const classification = classifyFeedingStatus(oldStyleVariance);
    
    return {
      available: true,
      dailyAnalysis: daily_analysis_rows.map(row => ({
        date: row.date,
        actual_feed_kg: row.daily_feed_kg,
        recommended_feed_kg: row.recommended_feed_kg,
        feed_percent: row.feed_variance_percent, // Actual/Recommended * 100
        sfr_percent: row.sfr_percent,
        biomass_kg: row.actual_biomass_kg,
        fish_count: row.fish_count,
      })),
      totals: {
        total_actual_feed_kg: Math.round(totalActualFeed * 100) / 100,
        total_recommended_feed_kg: Math.round(totalRecommendedFeed * 100) / 100,
        total_feed_percent: totalFeedPercent !== null 
          ? Math.round(totalFeedPercent * 10) / 10 
          : null,
        days_with_feed_data: rowsWithFeed.length,
        days_with_recommendation: rowsWithRecommendation.length,
      },
      classification,
    };
  }, [activeRecord, daily_analysis_rows]);

  // =====================================================
  // SHARED CHART X-AXIS DOMAIN
  // All charts must use the same date range for alignment
  // =====================================================
  const chartDateDomain = useMemo(() => {
    if (daily_analysis_rows.length === 0) return { startDate: null, endDate: null, dates: [] };
    
    const dates = daily_analysis_rows.map(r => r.date);
    const startDate = dates[0];
    const endDate = dates[dates.length - 1];
    
    return { startDate, endDate, dates };
  }, [daily_analysis_rows]);

  // Check if we have valid chart data
  const hasValidChartData = dailyRows.length > 0 && 
    dailyRows.some(d => d.model_weight !== null) &&
    dailyRows.some(d => d.actual_weight !== null);

  // Sample points for scatter (dots on red line)
  const samplePoints = dailyRows.filter(d => d.is_sample && d.actual_weight !== null);

  // Detect data binding error - table has rows but chart won't plot
  const hasDataBindingError = dailyRows.length > 0 && 
    dailyRows.some(d => d.model_weight !== null || d.actual_weight !== null) &&
    !hasValidChartData;

  // Generate chart warning message if needed
  const getChartWarning = () => {
    if (!activeRecord) return null;
    
    // Check for data binding error
    if (hasDataBindingError) {
      console.error('[Chart Error] Data binding error detected:', {
        dailyRowsCount: dailyRows.length,
        firstRows: dailyRows.slice(0, 5),
        hasModelWeights: dailyRows.some(d => d.model_weight !== null),
        hasActualWeights: dailyRows.some(d => d.actual_weight !== null)
      });
      return "Graph data binding error: daily weights exist in the table but are not being plotted.";
    }
    
    if (!activeRecord.model_trajectory || activeRecord.model_trajectory.length === 0) {
      return "Graph could not be generated: missing model trajectory data. Check that your spreadsheet has dates and temperatures.";
    }
    if (!activeRecord.actual_trajectory || activeRecord.actual_trajectory.length === 0) {
      return "Graph could not be generated: missing actual trajectory data.";
    }
    if (dailyRows.length === 0) {
      return "Graph could not be generated: no valid daily data rows.";
    }
    if (!hasValidChartData) {
      return "Graph could not be generated: missing required spreadsheet inputs (date, temperature, starting weight).";
    }
    return null;
  };

  const chartWarning = getChartWarning();

  // Color palette for multiple scenarios (distinct shades)
  const scenarioColors = [
    { model: '#93c5fd', actual: '#fca5a5' },  // Light blue/red
    { model: '#60a5fa', actual: '#f87171' },  // Medium blue/red
    { model: '#3b82f6', actual: '#ef4444' },  // Standard blue/red
    { model: '#1d4ed8', actual: '#b91c1c' },  // Dark blue/red
  ];

  // Build chart data with scenario overlays
  const chartDataWithScenarios = useMemo(() => {
    if (!dailyRows.length) return dailyRows;
    if (!showPreviousRuns || scenarioHistory.length === 0) return dailyRows;

    // Merge scenario data into daily rows
    return dailyRows.map((row, idx) => {
      const enhanced = { ...row };
      
      // Add data from each historical scenario
      scenarioHistory.forEach((scenario, sIdx) => {
        const modelPt = scenario.model_trajectory?.[idx];
        const actualPt = scenario.actual_trajectory?.[idx];
        
        if (modelPt) {
          enhanced[`model_${scenario.model_adjustment_percent}`] = modelPt.weight;
        }
        if (actualPt) {
          enhanced[`actual_${scenario.model_adjustment_percent}`] = actualPt.weight;
        }
      });
      
      return enhanced;
    });
  }, [dailyRows, showPreviousRuns, scenarioHistory]);

  // Build chart data with comparison analyses overlaid (normalized by Day index)
  const chartDataWithComparisons = useMemo(() => {
    if (comparisonAnalyses.length === 0 || dailyRows.length === 0) {
      return dailyRows;
    }

    // Create a map of day_index -> row for the current analysis
    const maxDayIndex = Math.max(...dailyRows.map(r => r.day_index || 0));
    
    // Clone dailyRows and add comparison data
    const enhancedRows = dailyRows.map((row, idx) => {
      const enhanced = { ...row };
      const dayIndex = row.day_index !== undefined ? row.day_index : idx;
      
      // Add data from each comparison analysis (normalized by day index)
      comparisonAnalyses.forEach((compRecord, compIdx) => {
        // Find matching day in comparison record's trajectory
        const modelPt = compRecord.model_trajectory?.find(pt => {
          // Calculate day index for comparison record
          const compStartDate = new Date(compRecord.start_date || compRecord.model_trajectory[0]?.date);
          const ptDate = new Date(pt.date);
          const ptDayIndex = Math.round((ptDate - compStartDate) / (1000 * 60 * 60 * 24));
          return ptDayIndex === dayIndex;
        });
        
        const actualPt = compRecord.actual_trajectory?.find(pt => {
          const compStartDate = new Date(compRecord.start_date || compRecord.actual_trajectory[0]?.date);
          const ptDate = new Date(pt.date);
          const ptDayIndex = Math.round((ptDate - compStartDate) / (1000 * 60 * 60 * 24));
          return ptDayIndex === dayIndex;
        });
        
        // Store with unique key per comparison
        const compKey = `comp_${compIdx}`;
        if (modelPt) {
          enhanced[`${compKey}_model`] = parseFloat(modelPt.weight);
        }
        if (actualPt) {
          enhanced[`${compKey}_actual`] = parseFloat(actualPt.weight);
        }
      });
      
      return enhanced;
    });
    
    return enhancedRows;
  }, [dailyRows, comparisonAnalyses]);

  // Hoisted: effective assumed fish count used by the adjusted (amber) curve
  // and by the "How to Read This Chart" legend below. Mirrors the cascade
  // used inside renderGrowthChart — kept at component scope so references
  // outside the chart function (e.g. the legend) don't throw ReferenceError.
  const effectiveAssumedN = useMemo(() => {
    const _fcrPanelAdjN = fcrResult?.fcrMissingFish?.adjustedN || null;
    const _intelAdjustedN = missingFishIntelligence?.initialAssumedFishNumber
      || missingFishIntelligence?.fcrBasedDetection?.adjustedFishCount
      || missingFishIntelligence?.trajectoryJump?.adjusted_fish_count
      || missingFishIntelligence?.trajectoryJump?.best_fit_fish_number
      || _fcrPanelAdjN
      || null;
    return assumedFishNumber || _intelAdjustedN;
  }, [assumedFishNumber, missingFishIntelligence, fcrResult]);

  // Hoisted: whether the yellow-dotted "adjusted actual" curve is renderable
  // for this record. Previously only computed INSIDE `renderGrowthChart`, which
  // caused a `ReferenceError: showAdjustedCurve is not defined` crash when
  // the Line Visibility panel (rendered outside that function) tried to gate
  // the new Adjusted Growth checkbox on it (PFD-BUG-009). Keeping the single
  // source of truth here means the chart body and the toggle label can't
  // disagree about whether the line exists.
  const showAdjustedCurve = useMemo(() => {
    const originalN = detectionResults?.inputs?.inputNumber || activeRecord?.input_number || null;
    const hasFeedDataForAdj = detectionResults?.available
      && detectionResults?.inputs?.startingBiomassKg
      && detectionResults?.inputs?.expectedFCR;
    const _fcrPanelAdjN = fcrResult?.fcrMissingFish?.adjustedN || null;
    const _fcrOnlyPathReady = !!(
      _fcrPanelAdjN
      && biologicalFCR?.totalFeedKg
      && biologicalFCR?.startBiomassKg
      && fcrResult?.reported?.fcr
      && modelFCR
    );
    return !!(
      (hasFeedDataForAdj || _fcrOnlyPathReady)
      && effectiveAssumedN && originalN
      && effectiveAssumedN !== originalN
      && (showAllDataOverride || effectiveOutput?.finalDecision?.missingFishUserVisible !== false)
    );
  }, [
    detectionResults,
    activeRecord?.input_number,
    fcrResult,
    biologicalFCR,
    modelFCR,
    effectiveAssumedN,
    showAllDataOverride,
    effectiveOutput,
  ]);

  // Render chart - uses dailyRows[] as single source of truth
  const renderGrowthChart = (height = 350) => {
    if (chartWarning) {
      return (
        <div className="flex items-center justify-center h-64 bg-amber-50 rounded-lg border border-amber-200">
          <div className="text-center p-4">
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <p className="text-amber-700 font-medium">{chartWarning}</p>
          </div>
        </div>
      );
    }

    // Use enhanced data with scenarios if showing previous runs, or with comparisons
    let chartData = dailyRows;
    if (showPreviousRuns && scenarioHistory.length > 0) {
      chartData = chartDataWithScenarios;
    } else if (comparisonAnalyses.length > 0) {
      chartData = chartDataWithComparisons;
    }

    // Add adjusted actual curve — the "best-fit line reflecting missing fish".
    // Five-level fallback cascade so that ANY missing-fish signal populates
    // the amber curve, even before the user clicks "Run Missing Fish
    // Intelligence":
    //   1. `assumedFishNumber`                               (user override)
    //   2. engine's final `initialAssumedFishNumber`         (post-MFI run)
    //   3. engine's `fcrBasedDetection.adjustedFishCount`    (sample-driven)
    //   4. engine's `trajectoryJump.*`                       (jump path)
    //   5. **`fcrResult.fcrMissingFish.adjustedN`** — the algebraic FCR-panel
    //      solution, which works even for records that have ONLY harvest
    //      events and no weight-sample points (e.g., LP201.1). Without this
    //      level, records with strong FCR evidence but no weight samples
    //      never get a best-fit line because the engine's sample-driven
    //      branches all return null.
    // `showAdjustedCurve` is hoisted to the component scope (see above) so
    // this chart body and the Line Visibility toggle below the chart stay
    // in lock-step. PFD-BUG-009: using it from component scope prevents
    // the ReferenceError that crashed Spreadsheet Growth Comparison when
    // a saved record was loaded and the toggle rendered outside this fn.
    if (showAdjustedCurve) {
      // Use the intelligence path's biomass/FCR when available; fall back to
      // the FCR panel's own biomass + expected FCR (modelFCR) so records
      // that took the FCR-only path still get a correctly-scaled curve.
      const _biomassKg = detectionResults?.inputs?.startingBiomassKg || biologicalFCR?.startBiomassKg || 0;
      const _expectedFCR = detectionResults?.inputs?.expectedFCR || modelFCR || 0;
      const adjustedPoints = computeAdjustedActualCurve(
        dailyRows, effectiveAssumedN, _biomassKg, _expectedFCR
      );
      const adjMap = {};
      adjustedPoints.forEach(p => { adjMap[p.date] = p.adjusted_weight; });
      chartData = chartData.map(row => ({
        ...row,
        adjusted_actual_weight: adjMap[row.date] ?? null,
      }));
    }

    // Calculate Y-axis domain from all weights (including scenarios and comparisons)
    let allWeights = chartData.flatMap(d => [d.model_weight, d.actual_weight]).filter(w => w !== null && w !== undefined);
    
    // Include scenario weights in domain calculation
    if (showPreviousRuns && scenarioHistory.length > 0) {
      scenarioHistory.forEach(scenario => {
        const scenarioWeights = chartData.flatMap(d => [
          d[`model_${scenario.model_adjustment_percent}`],
          d[`actual_${scenario.model_adjustment_percent}`]
        ]).filter(w => w !== null && w !== undefined);
        allWeights = [...allWeights, ...scenarioWeights];
      });
    }
    
    // Include comparison weights in domain calculation
    if (comparisonAnalyses.length > 0) {
      comparisonAnalyses.forEach((_, compIdx) => {
        const compKey = `comp_${compIdx}`;
        const compWeights = chartData.flatMap(d => [
          d[`${compKey}_model`],
          d[`${compKey}_actual`]
        ]).filter(w => w !== null && w !== undefined);
        allWeights = [...allWeights, ...compWeights];
      });
    }
    
    // Include adjusted curve in domain
    if (showAdjustedCurve) {
      const adjWeights = chartData.map(d => d.adjusted_actual_weight).filter(w => w != null);
      allWeights = [...allWeights, ...adjWeights];
    }
    
    const minWeight = Math.floor(Math.min(...allWeights) * 0.9);
    const maxWeight = Math.ceil(Math.max(...allWeights) * 1.1);

    // Current adjustment percent for legend
    const currentAdjustment = activeRecord?.model_adjustment_percent || 100;
    
    // Check if we should show legend
    const showLegend = true;

    return (
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={chartData} margin={{ top: 24, right: 30, left: 0, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 10 }} 
            tickFormatter={(val) => {
              if (!val) return '';
              const date = new Date(val);
              if (isNaN(date.getTime())) return val;
              return `${date.getMonth() + 1}/${date.getDate()}`;
            }}
            interval="preserveStartEnd"
          />
          <YAxis 
            tick={{ fontSize: 11 }}
            label={{ value: 'Weight (g)', angle: -90, position: 'insideLeft', fontSize: 12 }}
            domain={[minWeight, maxWeight]}
          />
          <Tooltip 
            formatter={(value, name) => {
              if (value === undefined || value === null) return ['-', name];
              const formatted = typeof value === 'number' ? `${value.toFixed(1)}g` : `${value}g`;
              return [formatted, name];
            }}
            labelFormatter={(label) => `Date: ${label}`}
          />
          
          {/* Comparison analyses lines (behind current, dashed, lighter) */}
          {comparisonAnalyses.map((compRecord, compIdx) => {
            const colorIdx = Math.min(compIdx, COMPARISON_COLORS.length - 1);
            const colors = COMPARISON_COLORS[colorIdx];
            const compKey = `comp_${compIdx}`;
            const compLabel = compRecord.name || `Cycle ${compIdx + 1}`;
            const isModelVisible = lineVisibility[`${compKey}_model`] !== false;
            const isActualVisible = lineVisibility[`${compKey}_actual`] !== false;
            return (
              <React.Fragment key={compRecord.id || compIdx}>
                {isModelVisible && (
                  <Line 
                    type="linear" 
                    dataKey={`${compKey}_model`}
                    stroke={colors.model}
                    strokeWidth={2} 
                    dot={false} 
                    name={`${compLabel} (Model)`}
                    connectNulls={true}
                    strokeDasharray="6 3"
                    strokeOpacity={0.7}
                    isAnimationActive={false}
                  />
                )}
                {isActualVisible && (
                  <Line 
                    type="linear" 
                    dataKey={`${compKey}_actual`}
                    stroke={colors.actual}
                    strokeWidth={2} 
                    dot={false}
                    name={`${compLabel} (Actual)`}
                    connectNulls={true}
                    strokeOpacity={0.7}
                    isAnimationActive={false}
                  />
                )}
              </React.Fragment>
            );
          })}
          
          {/* Historical scenario lines (if showing previous runs) */}
          {showPreviousRuns && scenarioHistory.map((scenario, idx) => {
            const colorIdx = Math.min(idx, scenarioColors.length - 1);
            const colors = scenarioColors[colorIdx];
            return (
              <React.Fragment key={scenario.id}>
                <Line 
                  type="linear" 
                  dataKey={`model_${scenario.model_adjustment_percent}`}
                  stroke={colors.model}
                  strokeWidth={2} 
                  dot={false} 
                  name={`Model ${scenario.model_adjustment_percent}%`}
                  connectNulls={true}
                  strokeDasharray="8 4"
                  strokeOpacity={0.7}
                  isAnimationActive={false}
                />
                <Line 
                  type="linear" 
                  dataKey={`actual_${scenario.model_adjustment_percent}`}
                  stroke={colors.actual}
                  strokeWidth={2} 
                  dot={false}
                  name={`Actual ${scenario.model_adjustment_percent}%`}
                  connectNulls={true}
                  strokeDasharray="8 4"
                  strokeOpacity={0.7}
                  isAnimationActive={false}
                />
              </React.Fragment>
            );
          })}
          
          {/* Current run lines (solid, on top) */}
          {lineVisibility.modelGrowth && (
            <Line 
              type="linear" 
              dataKey="model_weight" 
              stroke="#2563eb" 
              strokeWidth={3} 
              dot={false} 
              name={`Current: Model ${currentAdjustment}%`}
              connectNulls={true}
              strokeDasharray="5 5"
              isAnimationActive={false}
            />
          )}
          {lineVisibility.actualGrowth && (
            <Line 
              type="linear" 
              dataKey="actual_weight" 
              stroke="#dc2626" 
              strokeWidth={3} 
              dot={lineVisibility.samplePoints ? (props) => {
                // Show dot only for sample points
                const { cx, cy, payload } = props;
                if (payload && payload.is_sample) {
                  // Highlight jump sample with a special marker — gated by
                  // the user-controllable `lossEventMarker` visibility toggle.
                  const isJumpSample = trajectoryJump?.jump_sample_date && payload.date === trajectoryJump.jump_sample_date;
                  if (isJumpSample && lineVisibility.lossEventMarker) {
                    return (
                      <g>
                        <circle cx={cx} cy={cy} r={10} fill="rgba(245,158,11,0.25)" stroke="#f59e0b" strokeWidth={2} />
                        <circle cx={cx} cy={cy} r={5} fill="#f59e0b" stroke="#fff" strokeWidth={2} />
                      </g>
                    );
                  }
                  return (
                    <circle cx={cx} cy={cy} r={6} fill="#dc2626" stroke="#fff" strokeWidth={2} />
                  );
                }
                return null;
              } : false}
              name={`Current: Actual ${currentAdjustment}%`}
              connectNulls={true}
              isAnimationActive={false}
            />
          )}

          {/* Adjusted actual curve (feed-implied growth for assumed fish number).
              Gated by its own `lineVisibility.adjustedGrowth` toggle — hiding
              it is a pure render decision, no recalc of MFI / SFR / exclusions. */}
          {showAdjustedCurve && lineVisibility.adjustedGrowth && (
            <Line
              type="linear"
              dataKey="adjusted_actual_weight"
              stroke="#f59e0b"
              strokeWidth={2}
              strokeDasharray="4 2"
              dot={false}
              name={`Adjusted (${(effectiveAssumedN || assumedFishNumber || 0).toLocaleString()} fish)`}
              connectNulls={true}
              isAnimationActive={false}
            />
          )}

          {/* Loss window shading for trajectory jump — gated by
              user-controllable `lossWindow` visibility toggle. */}
          {lineVisibility.lossWindow && trajectoryJump?.jump_event_detected && trajectoryJump?.probable_loss_window_start && trajectoryJump?.probable_loss_window_end && (
            <ReferenceArea
              x1={trajectoryJump.probable_loss_window_start}
              x2={trajectoryJump.probable_loss_window_end}
              fill="#f59e0b"
              fillOpacity={0.08}
              stroke="#f59e0b"
              strokeOpacity={0.3}
              strokeDasharray="3 3"
              label={{ value: 'Loss Window', position: 'top', fill: '#d97706', fontSize: 11, fontWeight: 600 }}
            />
          )}

          {/* Loss Window Estimator markers — painted only when the user
              has toggled "Show these windows on the growth chart" ON
              inside the LossWindowEstimatorPanel below. Top window uses
              a darker amber fill; alternate windows use a lighter fill so
              the primary candidate is visually dominant. */}
          {estimatorChartWindows?.top && (
            <ReferenceArea
              key="lwe-top"
              x1={estimatorChartWindows.top.window_start}
              x2={estimatorChartWindows.top.window_end}
              fill="#d97706"
              fillOpacity={0.18}
              stroke="#b45309"
              strokeOpacity={0.55}
              strokeDasharray="4 3"
              label={{
                value: `Est. loss window · ${estimatorChartWindows.top.confidence}%`,
                position: 'insideTop',
                fill: '#92400e',
                fontSize: 11,
                fontWeight: 700,
              }}
            />
          )}
          {estimatorChartWindows?.alternates?.map((w) => (
            <ReferenceArea
              key={`lwe-alt-${w.window_start}`}
              x1={w.window_start}
              x2={w.window_end}
              fill="#fbbf24"
              fillOpacity={0.09}
              stroke="#f59e0b"
              strokeOpacity={0.35}
              strokeDasharray="2 4"
            />
          ))}

          {/* Incorrect sample annotations — gated by the user-controllable
              `incorrectSampleFlag` visibility toggle. */}
          {lineVisibility.incorrectSampleFlag && missingFishIntelligence?.analyticalSignals?.incorrectSamples?.samples?.map((s, idx) => (
            <ReferenceLine
              key={`incorrect-sample-${idx}`}
              x={s.sampleDate}
              stroke="#dc2626"
              strokeWidth={2}
              strokeDasharray="4 3"
              label={{ value: '\u26A0', position: 'top', fill: '#dc2626', fontSize: 14, fontWeight: 700 }}
            />
          ))}
          
          {/* Legend - always below chart */}
          {showLegend && (
            <Legend 
              verticalAlign="bottom" 
              height={30}
              wrapperStyle={{ fontSize: '11px', paddingTop: '5px' }}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    );
  };

  // Render Mortality Chart - Daily vs Accumulated
  const renderMortalityChart = (height = 300) => {
    if (!mortalityData || mortalityData.length === 0) {
      return (
        <div className="flex items-center justify-center h-48 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-500">No mortality data available</p>
        </div>
      );
    }

    // Calculate Y-axis domain
    const maxDaily = Math.max(...mortalityData.map(d => d.daily_mortality));
    const maxAccumulated = Math.max(...mortalityData.map(d => d.accumulated_mortality));
    const yMax = Math.ceil(Math.max(maxDaily, maxAccumulated) * 1.1) || 10;

    return (
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={mortalityData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 10 }} 
            tickFormatter={(val) => {
              if (!val) return '';
              const date = new Date(val);
              if (isNaN(date.getTime())) return val;
              return `${date.getMonth() + 1}/${date.getDate()}`;
            }}
            interval="preserveStartEnd"
          />
          <YAxis 
            tick={{ fontSize: 10 }}
            label={{ value: 'Mortality (count)', angle: -90, position: 'insideLeft', fontSize: 11 }}
            domain={[0, yMax]}
          />
          <Tooltip 
            formatter={(value, name) => {
              const label = name === 'daily_mortality' ? 'Daily Mortality' : 'Accumulated Mortality';
              return [value.toLocaleString(), label];
            }}
            labelFormatter={(label) => `Date: ${label}`}
          />
          <Legend 
            verticalAlign="bottom" 
            height={30}
            wrapperStyle={{ fontSize: '11px', paddingTop: '5px' }}
            formatter={(value) => {
              if (value === 'daily_mortality') return 'Daily Mortality';
              if (value === 'accumulated_mortality') return 'Accumulated Mortality';
              return value;
            }}
          />
          
          {/* Daily Mortality - Blue Line */}
          <Line 
            type="linear" 
            dataKey="daily_mortality" 
            stroke="#2563eb" 
            strokeWidth={2} 
            dot={false} 
            name="daily_mortality"
            connectNulls={true}
            isAnimationActive={false}
          />
          
          {/* Accumulated Mortality - Red Line */}
          <Line 
            type="linear" 
            dataKey="accumulated_mortality" 
            stroke="#dc2626" 
            strokeWidth={2} 
            dot={false} 
            name="accumulated_mortality"
            connectNulls={true}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    );
  };

  // Render Accumulated Feed Chart with Daily Feed Variance %
  // Axes: Percentage (LEFT), Accumulated Feed (RIGHT)
  // Lines: Accumulated Actual (green), Accumulated Recommended (red), Daily Feed Variance % (orange)
  const renderFeedChart = (height = 280) => {
    if (daily_analysis_rows.length === 0) {
      return (
        <div className="flex items-center justify-center h-48 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-500">No feed data available</p>
        </div>
      );
    }

    // Prepare data - use daily_analysis_rows with accumulated feed values + daily variance %
    // Handle divide-by-zero: if recommended = 0, variance is null
    const feedData = daily_analysis_rows.map((row) => {
      // Accumulated variance = (cumulative actual / cumulative recommended) * 100
      // >100% = overfeeding, <100% = underfeeding
      const accumVariance = row.accumulated_recommended_feed_kg > 0
        ? Math.round((row.cumulative_feed_kg / row.accumulated_recommended_feed_kg) * 1000) / 10
        : null;

      return {
        date: row.date,
        accumulated_actual: row.cumulative_feed_kg,
        accumulated_recommended: row.accumulated_recommended_feed_kg,
        daily_variance_percent: row.feed_variance_percent,
        accumulated_variance_percent: accumVariance,
      };
    });

    // Calculate Y-axis domain for accumulated feed (RIGHT axis)
    const maxActual = Math.max(...feedData.map(d => d.accumulated_actual || 0));
    const maxRecommended = Math.max(...feedData.map(d => d.accumulated_recommended || 0));
    const feedYMax = Math.ceil(Math.max(maxActual, maxRecommended) * 1.1) || 100;
    
    // Calculate Y-axis domain for daily variance % (LEFT axis)
    // Guard against outlier spikes (e.g. overfeeding a small-biomass fry day
    // can legitimately produce 800-1500%) squashing the rest of the series —
    // cap the auto-scale at 300% by default; the admin override input still
    // lets power users widen the axis when they want to see the full peak.
    const varianceValues = feedData.filter(d => d.daily_variance_percent !== null && d.daily_variance_percent >= 0).map(d => d.daily_variance_percent);
    const maxVariance = varianceValues.length > 0 ? Math.max(...varianceValues) : 150;
    const VARIANCE_AUTOSCALE_CAP = 300;
    const autoMax = Math.min(
      VARIANCE_AUTOSCALE_CAP,
      Math.ceil(Math.max(maxVariance, 110) * 1.1)
    );
    const varianceYMax = varianceYMaxOverride && Number(varianceYMaxOverride) > 0 
      ? Number(varianceYMaxOverride) 
      : autoMax;

    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={feedData} margin={{ top: 10, right: 60, left: 10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 10 }} 
            tickFormatter={(val) => {
              if (!val) return '';
              const date = new Date(val);
              if (isNaN(date.getTime())) return val;
              return `${date.getMonth() + 1}/${date.getDate()}`;
            }}
            interval="preserveStartEnd"
          />
          {/* LEFT Y-Axis: Daily Feed Variance (%) - Primary focus */}
          <YAxis 
            yAxisId="left"
            orientation="left"
            tick={{ fontSize: 10, fill: '#f97316' }} 
            label={{ value: 'Daily Variance (%)', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#f97316' } }}
            domain={[0, varianceYMax]}
            allowDataOverflow={true}
            tickFormatter={(val) => `${val}%`}
          />
          {/* RIGHT Y-Axis: Accumulated Feed (kg) */}
          <YAxis 
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 10 }} 
            label={{ value: 'Accumulated Feed (kg)', angle: 90, position: 'insideRight', style: { fontSize: 10 } }}
            domain={[0, feedYMax]}
          />
          {/* Faint red benchmark line at 100% (feeding target) */}
          <ReferenceLine 
            yAxisId="left" 
            y={100} 
            stroke="#dc2626" 
            strokeWidth={1}
            strokeOpacity={0.4}
            label={{ value: '100%', position: 'left', fill: '#dc2626', fontSize: 9, opacity: 0.6 }}
          />
          <Tooltip 
            labelFormatter={(val) => `Date: ${val}`}
            formatter={(value, name) => {
              if (name === 'accumulated_actual') return [value !== null ? `${value.toLocaleString()} kg` : '0 kg', 'Accumulated Actual'];
              if (name === 'accumulated_recommended') return [value !== null ? `${value.toLocaleString()} kg` : '0 kg', 'Accumulated Recommended'];
              if (name === 'daily_variance_percent') return [value !== null ? `${value.toFixed(1)}%` : 'N/A', 'Daily Variance'];
              if (name === 'accumulated_variance_percent') return [value !== null ? `${value.toFixed(1)}%` : 'N/A', 'Accum. Variance'];
              return [value, name];
            }}
          />
          <Legend 
            verticalAlign="bottom" 
            height={36}
            wrapperStyle={{ fontSize: '10px', paddingTop: '5px' }}
            formatter={(value) => {
              if (value === 'accumulated_actual') return 'Accum. Feed – Actual';
              if (value === 'accumulated_recommended') return 'Accum. Feed – Recommended';
              if (value === 'daily_variance_percent') return 'Daily Variance (%)';
              if (value === 'accumulated_variance_percent') return 'Accum. Variance (%)';
              return value;
            }}
          />
          
          {/* Daily Feed Variance % - Orange Line (LEFT axis) - Plotted first for visual priority */}
          {feedLineVisibility.dailyVariance && (
          <Line 
            yAxisId="left"
            type="monotone" 
            dataKey="daily_variance_percent" 
            name="daily_variance_percent"
            stroke="#f97316" 
            strokeWidth={1.5}
            dot={false}
            connectNulls={true}
            isAnimationActive={false}
          />
          )}

          {/* Accumulated Daily Variance % - Purple dashed Line (LEFT axis) */}
          {feedLineVisibility.accumulatedVariance && (
          <Line 
            yAxisId="left"
            type="monotone" 
            dataKey="accumulated_variance_percent" 
            name="accumulated_variance_percent"
            stroke="#7c3aed" 
            strokeWidth={2}
            strokeDasharray="6 3"
            dot={false}
            connectNulls={true}
            isAnimationActive={false}
          />
          )}
          
          {/* Accumulated Actual Feed - Green Solid Line (RIGHT axis) */}
          {feedLineVisibility.accumulatedActual && (
          <Line 
            yAxisId="right"
            type="monotone" 
            dataKey="accumulated_actual" 
            name="accumulated_actual"
            stroke="#16a34a" 
            strokeWidth={2}
            dot={false}
            connectNulls={true}
            isAnimationActive={false}
          />
          )}
          
          {/* Accumulated Recommended Feed - Red Solid Line (RIGHT axis) */}
          {feedLineVisibility.accumulatedRecommended && (
          <Line 
            yAxisId="right"
            type="monotone" 
            dataKey="accumulated_recommended" 
            name="accumulated_recommended"
            stroke="#dc2626" 
            strokeWidth={2}
            dot={false}
            connectNulls={true}
            isAnimationActive={false}
          />
          )}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  // ============ FEED VARIANCE COMPARISON: Reported vs Adjusted Fish Count ============
  const renderFeedVarianceComparison = (height = 280) => {
    if (!assumedFishNumber || !activeRecord?.input_number) return null;
    if (daily_analysis_rows.length === 0) return null;

    const reportedCount = activeRecord.input_number;
    const adjustedCount = assumedFishNumber;
    if (reportedCount === adjustedCount) return null;

    // Determine the split date: when suspectedLossDate is set, rows before it use reported count for the "adjusted" line
    const splitDate = suspectedLossDate || null;

    // Recompute accumulated variance for REPORTED fish count (always full cycle with reported)
    let reportedFishCount = reportedCount;
    let reportedCumulativeMortality = 0;
    let reportedCumulativeRecommended = 0;

    // Hybrid adjusted: uses reported count before splitDate, adjusted count from splitDate onwards
    let hybridFishCount = splitDate ? reportedCount : adjustedCount;
    let hybridCumulativeMortality = 0;
    let hybridCumulativeRecommended = 0;
    let hasSwitched = false;

    // Track daily values for dynamic RFI and wasted feed calculation
    let totalActualFeedKg = 0;
    let totalAdjustedRecommendedKg = 0;
    let periodWastedFeedKg = 0;  // sum of max(0, daily_actual - daily_adjusted_recommended)
    let totalExcessFeedKg = 0;   // net cumulative excess

    const comparisonData = daily_analysis_rows.map((row) => {
      const rowDate = row.date;

      // --- Reported line (unchanged) ---
      reportedCumulativeMortality += row.daily_mortality || 0;
      reportedFishCount = Math.max(0, reportedCount - reportedCumulativeMortality);
      if (row.harvest_number > 0) reportedFishCount = Math.max(0, reportedFishCount - row.harvest_number);

      let reportedRecommended = null;
      if (row.actual_weight_g !== null && reportedFishCount > 0 && row.sfr_percent !== null) {
        const reportedBiomass = (row.actual_weight_g * reportedFishCount) / 1000;
        reportedRecommended = Math.round(reportedBiomass * (row.sfr_percent / 100) * 100) / 100;
      }
      if (reportedRecommended !== null) reportedCumulativeRecommended += reportedRecommended;

      const reportedAccumVariance = reportedCumulativeRecommended > 0
        ? Math.round((row.cumulative_feed_kg / reportedCumulativeRecommended) * 1000) / 10
        : null;

      // --- Hybrid adjusted line ---
      // Switch from reported to adjusted count at the split date
      if (splitDate && !hasSwitched && rowDate >= splitDate) {
        hasSwitched = true;
        // Recalculate current fish count using adjusted base minus accumulated mortality
        hybridFishCount = Math.max(0, adjustedCount - hybridCumulativeMortality);
      }

      hybridCumulativeMortality += row.daily_mortality || 0;
      if (!hasSwitched && splitDate) {
        // Before split: track using reported count
        hybridFishCount = Math.max(0, reportedCount - hybridCumulativeMortality);
      } else if (hasSwitched || !splitDate) {
        // After split (or no split = full cycle adjusted): track using adjusted count
        hybridFishCount = Math.max(0, adjustedCount - hybridCumulativeMortality);
      }
      if (row.harvest_number > 0) hybridFishCount = Math.max(0, hybridFishCount - row.harvest_number);

      let hybridRecommended = null;
      if (row.actual_weight_g !== null && hybridFishCount > 0 && row.sfr_percent !== null) {
        const hybridBiomass = (row.actual_weight_g * hybridFishCount) / 1000;
        hybridRecommended = Math.round(hybridBiomass * (row.sfr_percent / 100) * 100) / 100;
      }
      // ★ Admin RFI override SSOT — the chart's "adjusted" line must reflect
      //   the admin's per-date override exactly the same way the override
      //   table does. We replace the locally-computed hybridRecommended with
      //   the override value when a scenario is active and the row carries
      //   one (set by applyOverride() during the daily_analysis_rows build).
      //   The "reported" line is intentionally left unchanged so the chart
      //   still shows the historical baseline.
      if (rfiOverridesEnabled && row.is_rfi_override) {
        hybridRecommended = Math.round(Number(row.recommended_feed_kg) * 100) / 100;
      }
      if (hybridRecommended !== null) hybridCumulativeRecommended += hybridRecommended;

      const hybridAccumVariance = hybridCumulativeRecommended > 0
        ? Math.round((row.cumulative_feed_kg / hybridCumulativeRecommended) * 1000) / 10
        : null;

      // ★ Accumulate daily feed values for dynamic RFI/wasted feed.
      // No-feed days (operator-deliberate fasting, sampling, severe weather,
      // harvest-prep) are EXCLUDED from BOTH sides of the ratio so a cycle
      // with intentional skips doesn't look artificially underfed.
      const dailyActual = row.daily_feed_kg || 0;
      const dailyAdjustedRec = hybridRecommended || 0;
      if (dailyActual > 0) {
        totalActualFeedKg += dailyActual;
        if (dailyAdjustedRec > 0) {
          totalAdjustedRecommendedKg += dailyAdjustedRec;
          // Period wasted = sum of overfeeding per day (actual > recommended)
          if (dailyActual > dailyAdjustedRec) {
            periodWastedFeedKg += (dailyActual - dailyAdjustedRec);
          }
        }
      }
      // Net excess = total actual - total recommended (can be negative if underfeeding overall)
      totalExcessFeedKg = totalActualFeedKg - totalAdjustedRecommendedKg;

      return {
        date: rowDate,
        reported_variance: reportedAccumVariance,
        adjusted_variance: hybridAccumVariance,
        reported_cumulative_recommended: Math.round(reportedCumulativeRecommended * 100) / 100,
        adjusted_cumulative_recommended: Math.round(hybridCumulativeRecommended * 100) / 100,
        cumulative_actual: row.cumulative_feed_kg,
      };
    });

    // Impact calculations
    const validData = comparisonData.filter(d => d.reported_variance !== null && d.adjusted_variance !== null);
    if (validData.length === 0) return null;

    const lastPoint = validData[validData.length - 1];
    const feedDiffKg = Math.round((lastPoint.reported_cumulative_recommended - lastPoint.adjusted_cumulative_recommended) * 100) / 100;
    const finalReportedVariance = lastPoint.reported_variance;
    const finalAdjustedVariance = lastPoint.adjusted_variance;
    const varianceDiff = Math.round((finalAdjustedVariance - finalReportedVariance) * 10) / 10;

    // ── Corrected RFI & Wasted Feed — DYNAMICALLY computed from adjusted fish count ──
    // These recalculate in real time when assumedFishNumber changes.
    // RFI = (Total Actual Feed / Total Adjusted Recommended Feed) × 100
    const correctedRFI = totalAdjustedRecommendedKg > 0
      ? Math.round((totalActualFeedKg / totalAdjustedRecommendedKg) * 1000) / 10
      : null;
    const isOverfeeding = correctedRFI != null && correctedRFI > 100;
    const excessFeedKg = Math.round(Math.max(0, totalExcessFeedKg) * 100) / 100;
    const wastedFeedKg = Math.round(periodWastedFeedKg * 100) / 100;

    // Recent RFI: last 14 days of data for trend detection.
    // Symmetric gate: only days the cage was fed count on both sides of the ratio.
    const recentRows = daily_analysis_rows.slice(-14);
    let recentActual = 0, recentRec = 0;
    recentRows.forEach(r => {
      const wasFed = (r.daily_feed_kg || 0) > 0;
      if (!wasFed) return;
      recentActual += r.daily_feed_kg;
      // Use the adjusted-count based recommended from the comparisonData loop
      // We recompute a simple estimate: biomass × SFR for the adjusted fish count
      if (r.actual_weight_g !== null && r.sfr_percent !== null) {
        // Approximate: use adjustedCount minus average mortality rate
        const avgMortPerDay = hybridCumulativeMortality / Math.max(daily_analysis_rows.length, 1);
        const recentIdx = daily_analysis_rows.indexOf(r);
        const estFish = Math.max(0, adjustedCount - avgMortPerDay * recentIdx);
        const estBiomass = (r.actual_weight_g * estFish) / 1000;
        recentRec += estBiomass * (r.sfr_percent / 100);
      }
    });
    const recentRfi = recentRec > 0 ? Math.round((recentActual / recentRec) * 1000) / 10 : null;
    const recentFeedingState = recentRfi != null
      ? (recentRfi > 100 ? 'Overfeeding' : recentRfi >= 90 ? 'Optimal' : 'Underfeeding')
      : null;

    // Feeding state classification: >100% = Overfeeding · 90–100% = Optimal · <90% = Underfeeding
    const feedingState = wastedFeedKg > 0 && correctedRFI != null && correctedRFI <= 100
      ? 'Mixed'  // Overall RFI ≤ 100% but some overfeeding periods
      : (isOverfeeding ? 'Overfeeding' : correctedRFI != null && correctedRFI >= 90 ? 'Optimal' : 'Underfeeding');

    const feedCostPerKg = parseFloat(economicParams.average_feed_price_eur_per_kg);
    const hasFeedCost = feedCostPerKg > 0 && !isNaN(feedCostPerKg);
    // ★ € impact based on TRUE wasted feed (period-by-period), not cumulative net
    const feedCostImpact = wastedFeedKg > 0 && hasFeedCost ? Math.round(wastedFeedKg * feedCostPerKg * 100) / 100 : 0;

    // Find divergence date (first date where difference > 2%)
    let divergenceDate = null;
    for (const pt of validData) {
      if (Math.abs(pt.adjusted_variance - pt.reported_variance) > 2) {
        divergenceDate = pt.date;
        break;
      }
    }

    const fishDiff = reportedCount - adjustedCount;
    const isOverstated = fishDiff > 0;
    const lossDateLabel = splitDate
      ? new Date(splitDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      : null;
    // MASTER GATE: when missingFishUserVisible is false, suppress adjusted-count-based conclusions
    // But feed waste data (overfeeding) is always visible since it's a feed metric
    const missingFishUserVisible = effectiveOutput?.finalDecision?.missingFishUserVisible !== false;
    
    const conclusion = !missingFishUserVisible
      ? (wastedFeedKg > 0 
          ? `Overfeeding detected: ${wastedFeedKg.toLocaleString()} kg wasted feed across periods where actual feed exceeded model recommendation.${hasFeedCost && feedCostImpact > 0 ? ` Feed waste cost: ${feedCostImpact.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR.` : ''}`
          : 'Feed variance within expected parameters.')
      : splitDate
      ? `Fish loss assumed from ${lossDateLabel}: reported count (${reportedCount.toLocaleString()}) used before that date, adjusted count (${adjustedCount.toLocaleString()}) used from that date onwards, resulting in ${Math.abs(feedDiffKg).toLocaleString()} kg feed difference over the remaining cycle.`
      : isOverstated
        ? `Using the originally reported fish count (${reportedCount.toLocaleString()}) overstated biomass by ${fishDiff.toLocaleString()} fish relative to the adjusted count (${adjustedCount.toLocaleString()}), resulting in ${Math.abs(feedDiffKg).toLocaleString()} kg ${feedDiffKg > 0 ? 'higher' : 'lower'} cumulative recommended feed over the cycle.`
        : `The adjusted fish count (${adjustedCount.toLocaleString()}) is ${Math.abs(fishDiff).toLocaleString()} fish higher than the reported count (${reportedCount.toLocaleString()}), indicating ${Math.abs(feedDiffKg).toLocaleString()} kg ${feedDiffKg < 0 ? 'additional' : 'less'} feed was recommended.`;

    // Y-axis domain
    const allVariances = validData.flatMap(d => [d.reported_variance, d.adjusted_variance]).filter(v => v !== null);
    const yMin = Math.floor(Math.min(...allVariances) * 0.95);
    const yMax = Math.ceil(Math.max(...allVariances) * 1.05);

    return (
      <div className="space-y-4" data-testid="feed-variance-comparison">
        {/* Impact Summary Box */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-5 text-white" data-testid="feed-variance-impact-summary">
          <h4 className="text-sm font-semibold text-cyan-300 mb-4 tracking-wide uppercase">Feed Variance Impact Summary</h4>
          
          {/* RFI Explanation Banner */}
          <div className="bg-white/5 border border-slate-600 rounded-lg p-3 mb-4" data-testid="rfi-explanation">
            <p className="text-xs text-slate-300 leading-relaxed">
              <span className="font-semibold text-cyan-300">RFI (Relative Feed Index)</span> = Actual Feed ÷ Recommended Feed × 100%, computed across <span className="font-medium text-cyan-200">days the cage was actually fed</span> — intentional no-feed days (sampling, fasting, severe weather, harvest-prep) are excluded from both sides so the ratio compares like-for-like.
              <span className="text-red-400 font-medium"> &gt;100% = Overfeeding</span> · 
              <span className="text-green-400 font-medium"> 90–100% = Optimal</span> · 
              <span className="text-blue-400 font-medium"> &lt;90% = Underfeeding</span>
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <div className="bg-white/10 backdrop-blur rounded-lg p-3">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Corrected RFI</p>
              <p className={`text-lg font-bold ${feedingState === 'Overfeeding' ? 'text-red-400' : feedingState === 'Mixed' ? 'text-amber-400' : feedingState === 'Optimal' ? 'text-green-400' : 'text-blue-400'}`} data-testid="corrected-rfi-value">
                {correctedRFI != null ? `${correctedRFI}%` : 'N/A'}
              </p>
              <p className={`text-[9px] mt-0.5 ${feedingState === 'Mixed' ? 'text-amber-400' : 'text-slate-500'}`}>
                {feedingState}
              </p>
              {recentRfi != null && recentFeedingState && recentFeedingState !== feedingState && (
                <p className={`text-[9px] mt-1 font-medium ${recentFeedingState === 'Overfeeding' ? 'text-red-400' : recentFeedingState === 'Mixed' ? 'text-amber-400' : recentFeedingState === 'Optimal' ? 'text-green-400' : 'text-blue-400'}`} data-testid="recent-rfi-alert">
                  Last 14d: {recentRfi}% ({recentFeedingState})
                </p>
              )}
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-3">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Feed Recommendation Diff</p>
              <p className={`text-lg font-bold ${feedDiffKg > 0 ? 'text-amber-300' : feedDiffKg < 0 ? 'text-cyan-300' : 'text-slate-300'}`}>
                {feedDiffKg !== 0 ? `${feedDiffKg > 0 ? '+' : ''}${feedDiffKg.toLocaleString()} kg` : '0 kg'}
              </p>
              <p className="text-[9px] text-slate-500 mt-0.5">Reported vs Corrected</p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-3">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Variance Shift</p>
              <p className={`text-lg font-bold ${varianceDiff > 0 ? 'text-red-400' : 'text-green-400'}`}>
                {varianceDiff > 0 ? '+' : ''}{varianceDiff}%
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-3">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">{wastedFeedKg > 0 ? 'Wasted Feed' : 'Feed Status'}</p>
              {wastedFeedKg > 0 ? (
                <>
                  <p className="text-lg font-bold text-red-400" data-testid="wasted-feed-value">{wastedFeedKg.toLocaleString()} kg</p>
                  <p className="text-[9px] text-slate-500 mt-0.5">Sum of overfeeding periods</p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-green-400" data-testid="wasted-feed-value">None</p>
                  <p className="text-[9px] text-slate-500 mt-0.5">No overfeeding periods</p>
                </>
              )}
            </div>
            <div className="bg-white/10 backdrop-blur rounded-lg p-3" data-testid="feed-cost-impact-card">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Economic Impact</p>
              {!hasFeedCost ? (
                <p className="text-xs text-slate-500 mt-1" data-testid="feed-cost-impact-unavailable">Set feed price to calculate</p>
              ) : wastedFeedKg > 0 && feedCostImpact > 0 ? (
                <p className="text-lg font-bold text-red-400" data-testid="feed-cost-impact-value">
                  {feedCostImpact.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR
                </p>
              ) : (
                <p className="text-sm font-medium text-green-400" data-testid="feed-cost-impact-value">0 EUR</p>
              )}
              <p className="text-[9px] text-slate-500 mt-0.5">{wastedFeedKg > 0 ? 'From wasted feed' : 'No feed waste'}</p>
            </div>
          </div>
          {divergenceDate && (
            <p className="text-xs text-slate-400 mb-2">
              Material divergence from: <span className="text-white font-medium">{new Date(divergenceDate).toLocaleDateString()}</span>
            </p>
          )}
          <p className="text-xs text-slate-300 leading-relaxed">{conclusion}</p>
          {wastedFeedKg > 0 && hasFeedCost && feedCostImpact > 0 && (
            <p className="text-xs text-slate-300 mt-1" data-testid="feed-cost-conclusion">
              Total wasted feed across overfeeding periods: <span className="font-semibold text-red-400">{wastedFeedKg.toLocaleString()} kg</span>. At <span className="text-white font-medium">{feedCostPerKg.toFixed(2)} EUR/kg</span>, feed waste cost = <span className="font-semibold text-red-400">{feedCostImpact.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR</span>.
              {excessFeedKg !== wastedFeedKg && excessFeedKg > 0 && (
                <span className="text-slate-400"> (Net cumulative excess after underfeeding offset: {excessFeedKg.toLocaleString()} kg)</span>
              )}
            </p>
          )}
          {wastedFeedKg === 0 && correctedRFI != null && (
            <p className="text-xs text-green-400 mt-1 font-medium" data-testid="no-feed-waste-message">
              No feed waste detected — no overfeeding periods found (Corrected RFI: {correctedRFI}%).
            </p>
          )}
        </div>

        {/* Suspected Date of Fish Loss Picker */}
        <div className="flex items-center gap-3 bg-white rounded-lg border border-slate-200 p-3" data-testid="suspected-loss-date-picker">
          <div className="flex items-center gap-2 shrink-0">
            <Search className="w-4 h-4 text-indigo-500" />
            <Label className="text-xs font-semibold text-slate-700 whitespace-nowrap">Suspected Date of Fish Loss</Label>
          </div>
          <input
            type="date"
            className="text-sm border border-slate-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none"
            value={suspectedLossDate || ''}
            min={validData.length > 0 ? validData[0].date : undefined}
            max={validData.length > 0 ? validData[validData.length - 1].date : undefined}
            onChange={(e) => setSuspectedLossDate(e.target.value || null)}
            data-testid="suspected-loss-date-input"
          />
          {suspectedLossDate && (
            <button
              className="text-xs text-indigo-600 hover:text-indigo-800 font-medium underline underline-offset-2"
              onClick={() => setSuspectedLossDate(null)}
              data-testid="suspected-loss-date-clear"
            >
              Reset to full cycle
            </button>
          )}
          <span className="text-[10px] text-slate-400 ml-auto">
            {suspectedLossDate ? 'Adjusted count applied from selected date' : 'Default: adjusted count applied to full cycle'}
          </span>
        </div>

        {/* Comparison Chart */}
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={comparisonData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 9, fill: '#666' }}
              tickFormatter={(d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
              interval={Math.max(0, Math.floor(comparisonData.length / 10))}
            />
            <YAxis 
              tick={{ fontSize: 10, fill: '#666' }}
              domain={[yMin, yMax]}
              tickFormatter={(val) => `${val}%`}
              label={{ value: 'Accumulated Feed Variance (%)', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#666' } }}
            />
            <Tooltip 
              contentStyle={{ fontSize: 11, border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 12px' }}
              labelFormatter={(d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
              formatter={(value, name) => {
                if (name === 'reported_variance') return [value !== null ? `${value.toFixed(1)}%` : 'N/A', `Reported (${reportedCount.toLocaleString()} fish)`];
                if (name === 'adjusted_variance') return [value !== null ? `${value.toFixed(1)}%` : 'N/A', `Adjusted (${adjustedCount.toLocaleString()} fish)`];
                return [value, name];
              }}
            />
            <Legend 
              wrapperStyle={{ fontSize: 11 }}
              formatter={(value) => {
                if (value === 'reported_variance') return `Reported Count (${reportedCount.toLocaleString()})`;
                if (value === 'adjusted_variance') return `Adjusted Count (${adjustedCount.toLocaleString()})`;
                return value;
              }}
            />
            <ReferenceLine y={100} stroke="#94a3b8" strokeDasharray="4 4" strokeWidth={1} label={{ value: '100%', position: 'right', fontSize: 9, fill: '#94a3b8' }} />
            {splitDate && (
              <ReferenceLine
                x={splitDate}
                stroke="#818cf8"
                strokeWidth={2}
                strokeDasharray="6 3"
                label={{ value: 'Fish Loss Date', position: 'top', fontSize: 10, fill: '#818cf8', fontWeight: 600 }}
              />
            )}
            <Line 
              type="monotone" 
              dataKey="reported_variance" 
              name="reported_variance"
              stroke="#f59e0b" 
              strokeWidth={2}
              dot={false}
              connectNulls={true}
              isAnimationActive={false}
            />
            {missingFishUserVisible && (
            <Line 
              type="monotone" 
              dataKey="adjusted_variance" 
              name="adjusted_variance"
              stroke="#06b6d4" 
              strokeWidth={2}
              dot={false}
              connectNulls={true}
              isAnimationActive={false}
            />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };


  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200" data-testid="spreadsheet-comparison-page">
      <Header />
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-gray-600 hover:text-[#0891b2]"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Dashboard
          </Button>
        </div>

        {/* Main Card */}
        <Card className="border-[#0891b2] shadow-lg" data-testid="spreadsheet-upload-card">
          <CardHeader className="bg-gradient-to-r from-[#0891b2] to-[#0e7490] text-white rounded-t-lg">
            <div className="flex items-center space-x-3">
              <FileSpreadsheet className="w-7 h-7" />
              <div>
                <CardTitle className="text-xl sm:text-2xl">Spreadsheet Growth Comparison</CardTitle>
                <CardDescription className="text-cyan-100">
                  Upload your production data to compare actual vs model growth
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          {/* ★ Above-the-fold feature video — PRD item 1 */}
          <div className="px-4 pt-4 sm:px-6">
            <FeatureVideoHero
              featureName="Spreadsheet Growth Comparison"
              youtubeUrl={HELP_URLS?.spreadsheetComparison?.youtube}
            />
          </div>

          {/* Try-with-Example bar — admin-curated presets */}
          {spreadsheetExamples?.length > 0 && (
            <div className="px-4 sm:px-6">
              <ModuleExamplesBar
                examples={spreadsheetExamples}
                onSelect={loadExampleAndRun}
                moduleLabel="Spreadsheet Comparison"
                isReadOnly={isReadOnly}
              />
            </div>
          )}

          {/* Admin-only: Pin / Update the curated Try-[Species] example with
              the spreadsheet record currently loaded on the page. Renders for
              any loaded record (not just the currently-pinned one) so admins
              can promote any report to the example slot without leaving the
              page. */}
          {user?.is_admin && activeRecord?.id && activeRecord?.species && (
            <div className="px-4 sm:px-6 flex justify-end">
              <UpdateCuratedExampleButton
                module="spreadsheet"
                species={loadedCurated?.species}
                currentSpecies={activeRecord?.species}
                token={token}
                isAdmin={!!user?.is_admin}
                sourceRecordId={loadedCurated?.source_record_id}
                currentRecordId={activeRecord?.id}
                currentName={loadedCurated?.current_name}
                buildRecordPatch={() => ({
                  species: activeRecord?.species,
                  // Spreadsheet records carry top-level metadata that drives
                  // the curated payload's `record_id` echo. We persist the
                  // `name` rename via `rename_to`; everything else is
                  // already saved by the Reanalyze flow when the admin
                  // tweaks model_adjustment_percent etc.
                })}
                onUpdated={() => refetchSpreadsheetExamples?.()}
              />
            </div>
          )}
          
          <CardContent className="p-4 sm:p-6 space-y-6">
            {isRestrictedDemo && !activeRecord && (
              <div className="p-4 bg-slate-900 text-white rounded-lg flex items-start gap-3" data-testid="restricted-demo-spreadsheet-notice">
                <Lock className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold">Restricted demo — view only</p>
                  <p className="text-xs text-slate-300">Pick one of the three example datasets above (Seabream, Seabass or Tilapia) to view its full report. Uploads, edits and exports are disabled.</p>
                </div>
              </div>
            )}

            {/* Read-Only Mode Banner */}
            {isReadOnly && (
              <div className="p-4 bg-amber-50 border-2 border-amber-400 rounded-lg" data-testid="read-only-banner">
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="text-sm font-bold text-amber-800">Read-Only Mode</p>
                    <p className="text-xs text-amber-700">Your account is pending approval. You can explore the interface but cannot upload files or run analyses.</p>
                  </div>
                </div>
              </div>
            )}
            
            <fieldset disabled={isReadOnly || isRestrictedDemo} hidden={isRestrictedDemo} className={isReadOnly ? 'opacity-60' : ''}>
            {/* Step 1: Download Template */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
                Download Template (Required First Step)
              </h3>
              <p className="text-sm text-blue-700 mb-3">
                Download the Excel template, fill in your production data following the instructions, then upload it below.
              </p>
              <Button
                onClick={downloadTemplate}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="download-template-btn"
                disabled={isReadOnly}
              >
                <Download className="w-4 h-4 mr-2" />
                Download Data Template
              </Button>
            </div>

            {/* Step 2: Upload */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-6">
              <h3 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
                Upload Completed Template
              </h3>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label htmlFor="recordName">Analysis Name</Label>
                  <Input
                    id="recordName"
                    placeholder="e.g., Cage A1 - March 2024"
                    value={recordName}
                    onChange={(e) => setRecordName(e.target.value)}
                    className="bg-white"
                    data-testid="record-name-input"
                    disabled={isReadOnly}
                  />
                  {/* Update pre-existing record checkbox */}
                  {!activeRecord?.id && (
                    <div className="mt-2 space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="updateExistingRecord"
                          checked={updateExistingRecord}
                          onCheckedChange={(checked) => {
                            setUpdateExistingRecord(checked);
                            if (!checked) {
                              setSelectedUpdateRecordId('');
                            }
                          }}
                          data-testid="update-existing-record-checkbox"
                          disabled={isReadOnly}
                        />
                        <Label 
                          htmlFor="updateExistingRecord" 
                          className="text-sm font-normal text-gray-600 cursor-pointer"
                        >
                          Update pre-existing record
                        </Label>
                      </div>
                      {updateExistingRecord && (
                        <Select
                          value={selectedUpdateRecordId}
                          onValueChange={(value) => {
                            setSelectedUpdateRecordId(value);
                            // Auto-fill the analysis name from the selected record
                            const selectedRec = records.find(r => r.id === value);
                            if (selectedRec) {
                              setRecordName(selectedRec.name);
                              setSpecies(selectedRec.species || 'Seabream');
                              setGeneticOrigin(selectedRec.genetic_origin || 'Greek');
                              setModelAdjustmentPercent(selectedRec.model_adjustment_percent || 100);
                            }
                          }}
                          disabled={isReadOnly || records.length === 0}
                        >
                          <SelectTrigger 
                            className="bg-white border-amber-300 focus:ring-amber-400"
                            data-testid="update-record-select"
                          >
                            <SelectValue placeholder={records.length === 0 ? 'No saved records' : 'Select a record to update'} />
                          </SelectTrigger>
                          <SelectContent>
                            {records.map(r => (
                              <SelectItem key={r.id} value={r.id}>
                                {r.name} — {r.data_points?.length || '?'} days{r.species ? ` (${r.species})` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="species">Species</Label>
                  <Select value={species} onValueChange={setSpecies} disabled={isReadOnly}>
                    <SelectTrigger className="bg-white" data-testid="species-select">
                      <SelectValue placeholder="Select species" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Seabream">Gilthead Seabream</SelectItem>
                      <SelectItem value="Seabass" data-testid="species-option-seabass">
                        European Seabass (Dicentrarchus labrax)
                      </SelectItem>
                      <SelectItem value="Tilapia" data-testid="species-option-tilapia">
                        Tilapia (Oreochromis niloticus)
                      </SelectItem>
                      {userSpecies.filter(s => s.value === 'Tripletail').map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Genetic Origin - Only show for Seabream */}
                {species !== 'Tilapia' && species !== 'Seabass' && species !== 'Tripletail' && (
                  <div className="space-y-2">
                    <Label htmlFor="geneticOrigin" className={user?.is_admin ? 'italic' : ''}>Genetic Origin</Label>
                    <Select 
                      value={geneticOrigin} 
                      onValueChange={(value) => {
                        setGeneticOrigin(value);
                        // Reset FCR version to default for new origin
                        const matchingOrigin = fcrOrigins.find(o => o.name === value);
                        if (matchingOrigin?.default_version_id) {
                          setFcrVersionId(matchingOrigin.default_version_id);
                        }
                      }}
                    >
                      <SelectTrigger className={`bg-white ${user?.is_admin ? 'italic' : ''}`} data-testid="genetic-select">
                        <SelectValue placeholder="Select origin" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Greek"   className={user?.is_admin ? 'italic' : ''}>Greek</SelectItem>
                        <SelectItem value="French"  className={user?.is_admin ? 'italic' : ''}>French</SelectItem>
                        <SelectItem value="Italian" className={user?.is_admin ? 'italic' : ''}>Italian</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* FCR Version Selector - Show all versions from all origins */}
                {fcrOrigins.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="fcr_version" className="flex items-center gap-2">
                      <span>
                        FCR Version
                        <span className="text-xs text-gray-500 font-normal ml-2">(Optional)</span>
                      </span>
                      <FcrGuidanceInfo />
                      {/* Saved ✓ pill — appears for ~2s after a successful PATCH
                          so the user gets instant confirmation their pick
                          stuck. Fades out on its own. */}
                      {fcrSavedFlash && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-800 border border-emerald-300 text-[10px] font-medium animate-in fade-in slide-in-from-left-2 duration-200"
                          data-testid="fcr-version-saved-flash"
                        >
                          <CheckCircle className="w-3 h-3" />
                          <span>Saved · {fcrSavedFlash.originName} {fcrSavedFlash.versionName} ({fcrSavedFlash.multiplier}%)</span>
                        </span>
                      )}
                    </Label>
                    <Select
                      value={fcrVersionId || ''}
                      onValueChange={setFcrVersionId}
                      data-testid="fcr-version-select"
                    >
                      <SelectTrigger className="bg-emerald-50 border-emerald-300">
                        <SelectValue placeholder="Select version" />
                      </SelectTrigger>
                      <SelectContent>
                        {fcrOrigins.flatMap(origin =>
                          (origin.versions || []).map(v => (
                            <SelectItem
                              key={v.id}
                              value={v.id}
                              className={user?.is_admin && Number(v.multiplier_percent) === 100 ? 'italic' : ''}
                            >
                              {origin.name} — {v.label || `${v.version_name} (${v.multiplier_percent}%)`}
                              {v.id === origin.default_version_id && ' ★'}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500">
                      FCR multiplier version affects biological FCR calculations
                      {activeRecord?.id && !fcrAutoSyncing && (
                        <span className="text-emerald-700 ml-1" data-testid="fcr-autosync-hint">
                          · changes auto-recalc missing fish
                        </span>
                      )}
                      {fcrAutoSyncing && (
                        <span className="text-cyan-700 ml-1 inline-flex items-center gap-1" data-testid="fcr-autosync-spinner">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          recalculating…
                        </span>
                      )}
                    </p>

                    {/* Guidance note for choosing the right FCR version — shared component */}
                    <FCRVersionGuidanceNote />
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <span>Temperature Data</span>
                    <Info className="h-4 w-4 text-cyan-600" />
                  </Label>
                  <div className="p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
                    <p className="text-sm text-cyan-700 font-medium">
                      User temperature upload will be used for calculations
                    </p>
                    <p className="text-xs text-cyan-600 mt-1">
                      The daily temperatures from your spreadsheet's "Daily Temperature" column will be used for SGR model calculations.
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="modelAdjustment" className="flex items-center gap-2">
                    <span>Model Adjustment (%)</span>
                    <span className="text-xs text-gray-500 font-normal">
                      (100% = standard model)
                    </span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="modelAdjustment"
                      type="number"
                      min="50"
                      max="150"
                      step="5"
                      value={modelAdjustmentPercent}
                      onChange={(e) => {
                        const val = parseInt(e.target.value) || 100;
                        setModelAdjustmentPercent(Math.min(150, Math.max(50, val)));
                      }}
                      className="bg-white w-24"
                      data-testid="model-adjustment-input"
                    />
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setModelAdjustmentPercent(prev => Math.max(50, prev - 5))}
                        className="px-2"
                      >
                        -5
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setModelAdjustmentPercent(prev => Math.min(150, prev + 5))}
                        className="px-2"
                      >
                        +5
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setModelAdjustmentPercent(100)}
                        className="px-2 text-xs"
                      >
                        Reset
                      </Button>
                    </div>
                    {modelAdjustmentPercent !== 100 && (
                      <span className="text-sm text-amber-600 font-medium">
                        {modelAdjustmentPercent > 100 ? `+${modelAdjustmentPercent - 100}%` : `${modelAdjustmentPercent - 100}%`}
                      </span>
                    )}
                  </div>
                  {/* Reanalyze Button - shows when adjustment changed on an existing record */}
                  {canReanalyze && (
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      onClick={reanalyzeWithNewAdjustment}
                      disabled={reanalyzing}
                      className="mt-2 bg-[#0891b2] hover:bg-[#0e7490] text-white"
                      data-testid="reanalyze-button"
                    >
                      {reanalyzing ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Reanalyzing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Reanalyze with {modelAdjustmentPercent}%{formatCostLabel('spreadsheet_reanalyze')}
                        </>
                      )}
                    </Button>
                  )}
                  
                  {/* Show previous runs checkbox */}
                  {activeRecord && (
                    <div className="flex items-center space-x-2 mt-3">
                      <Checkbox
                        id="showPreviousRuns"
                        checked={showPreviousRuns}
                        onCheckedChange={(checked) => setShowPreviousRuns(checked)}
                        data-testid="show-previous-runs-checkbox"
                      />
                      <Label 
                        htmlFor="showPreviousRuns" 
                        className="text-sm font-normal text-gray-600 cursor-pointer"
                      >
                        Show previous model adjustment runs on graph
                      </Label>
                      {scenarioHistory.length > 0 && showPreviousRuns && (
                        <>
                          <span className="text-xs text-gray-400">
                            ({scenarioHistory.length} previous run{scenarioHistory.length > 1 ? 's' : ''})
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={clearScenarioHistory}
                            className="text-xs text-gray-400 hover:text-red-500 h-6 px-2"
                          >
                            Clear history
                          </Button>
                        </>
                      )}
                    </div>
                  )}

                  {/* ★ KPI Cut-Off at First Harvest toggle */}
                  {activeRecord && (
                    <div
                      className="mt-3 flex items-start gap-2 p-3 bg-slate-50 border border-slate-200 rounded-lg"
                      data-testid="kpi-cutoff-section"
                    >
                      <Checkbox
                        id="kpiCutoffAtFirstHarvest"
                        checked={!!activeRecord?.kpi_cutoff_at_first_harvest}
                        disabled={kpiCutoffSaving}
                        onCheckedChange={(checked) => toggleKpiCutoff(checked)}
                        data-testid="kpi-cutoff-checkbox"
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <Label
                          htmlFor="kpiCutoffAtFirstHarvest"
                          className="text-sm font-medium text-slate-800 cursor-pointer"
                        >
                          Stop analysis at first harvest event
                        </Label>
                        <p className="text-xs text-slate-600 mt-0.5">
                          When enabled, all KPIs (FCR, feeding, missing-fish) truncate at the first harvest date
                          so post-harvest starvation or empty-cage days don&apos;t distort the numbers.
                          {kpiCutoffSaving && <span className="ml-1 text-slate-500">Saving…</span>}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <Label htmlFor="fileUpload">Select Excel File</Label>

                <Tabs defaultValue="pfd" className="w-full" data-testid="upload-mode-tabs">
                  <TabsList className="grid w-full grid-cols-3 mb-3" data-testid="upload-mode-tablist">
                    <TabsTrigger value="pfd" data-testid="upload-tab-pfd">PFD Template</TabsTrigger>
                    <TabsTrigger value="aquamanager" data-testid="upload-tab-aquamanager">AquaManager</TabsTrigger>
                    <TabsTrigger value="fishtalk" data-testid="upload-tab-fishtalk">Fishtalk</TabsTrigger>
                  </TabsList>

                  <TabsContent value="pfd" className="space-y-3 mt-0">
                    {/* PRD date-format guard — Brazilian / DD-MM regional input
                        historically produced mixed Excel-parsed datetimes + string
                        dates, which scrambled the growth chart. We surface the
                        requirement up-front so the operator never hits the
                        silent V-shape bug. The backend pre-pass also detects and
                        rescues this case, but a clear UI note prevents the
                        upload from being rejected at all. */}
                    <div
                      className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2"
                      data-testid="date-format-notice"
                    >
                      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                      <div>
                        <p className="font-semibold mb-0.5">Date format: <span className="font-mono">YYYY-MM-DD</span> (e.g. <span className="font-mono">2025-03-06</span>)</p>
                        <p>
                          If your locale uses DD/MM/YYYY, format column A as <span className="font-mono">yyyy-mm-dd</span> in Excel before uploading. Spreadsheets with mixed date formats are auto-reinterpreted as DD/MM/YYYY when an unambiguous signal is detected — otherwise the upload is rejected with a fix-it message rather than silently producing a broken growth chart.
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <Input
                        id="fileUpload"
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileChange}
                        className="bg-white flex-1"
                        data-testid="file-input"
                      />
                      <Button
                        onClick={uploadSpreadsheet}
                        disabled={!selectedFile || uploading || isReadOnly}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6 shadow-md hover:shadow-lg transition-all"
                        data-testid="upload-btn"
                      >
                        {isReadOnly ? (
                          <>
                            <Lock className="w-4 h-4 mr-2" />
                            Pending Approval
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            {uploading ? 'Processing...' : (activeRecord?.id || (updateExistingRecord && selectedUpdateRecordId)) ? `Update Record${formatCostLabel('spreadsheet_reanalyze')}` : `Upload & Analyze${formatCostLabel('spreadsheet_upload')}`}
                          </>
                        )}
                      </Button>
                    </div>
                    {selectedFile && (
                      <p className="text-sm text-green-700">
                        Selected: {selectedFile.name}
                      </p>
                    )}
                  </TabsContent>

                  <TabsContent value="aquamanager" className="mt-0">
                    <AquaManagerUploadPanel
                      recordName={recordName}
                      species={species}
                      geneticOrigin={geneticOrigin}
                      modelAdjustmentPercent={modelAdjustmentPercent}
                      isReadOnly={isReadOnly}
                      costLabel={formatCostLabel('spreadsheet_upload')}
                      onUploadSuccess={(record) => {
                        setActiveRecord(record);
                        setSelectedFile(null);
                      }}
                    />
                  </TabsContent>

                  <TabsContent value="fishtalk" className="mt-0">
                    <FishtalkUploadPanel
                      recordName={recordName}
                      species={species}
                      geneticOrigin={geneticOrigin}
                      modelAdjustmentPercent={modelAdjustmentPercent}
                      isReadOnly={isReadOnly}
                      costLabel={formatCostLabel('spreadsheet_upload')}
                      onUploadSuccess={(record) => {
                        setActiveRecord(record);
                        setSelectedFile(null);
                      }}
                    />
                  </TabsContent>
                </Tabs>
              </div>

              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}
            </div>
            </fieldset>

            {/* Results Section */}
            {activeRecord && (
              <fieldset disabled={isRestrictedDemo} className="min-w-0 border-0 p-0 m-0" data-testid="report-lock-fieldset">
              <div className="space-y-6" ref={reportRef}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="text-lg font-semibold text-[#0891b2] flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Analysis Results: {activeRecord.name}
                  </h3>
                  <div className="flex items-center gap-2" data-pdf-hide="true">
                    {/* View Mode Toggle */}
                    <div className="flex rounded-md border border-gray-300 overflow-hidden" data-testid="view-mode-toggle">
                      <button
                        onClick={() => setViewMode('summary')}
                        className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                          viewMode === 'summary' 
                            ? 'bg-[#0891b2] text-white' 
                            : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                        data-testid="summary-report-btn"
                      >
                        Summary Report
                      </button>
                      <button
                        onClick={() => setViewMode('full')}
                        className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                          viewMode === 'full' 
                            ? 'bg-[#0891b2] text-white' 
                            : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                        data-testid="full-report-btn"
                      >
                        Full Report
                      </button>
                    </div>
                    {user?.is_admin && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={exportToPdf}
                      disabled={exportingPdf || !hasAnyVisible(sectionVisibility)}
                      className="text-[#0891b2] border-[#0891b2] hover:bg-[#0891b2] hover:text-white disabled:opacity-50"
                      data-testid="export-pdf-btn"
                      data-pdf-hide="true"
                      title={!hasAnyVisible(sectionVisibility) ? 'Select at least one section to export' : ''}
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      {exportingPdf ? 'Exporting...' : `Export ${viewMode === 'summary' ? 'Summary' : 'Full'} PDF`}
                    </Button>
                    )}
                    {/* Compact Review PDF — available to ALL users (was admin-only). */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={exportCompactOperationalReviewPdf}
                      disabled={exportingCompactPdf || !activeRecord}
                      className="text-red-700 border-red-300 hover:bg-red-50 disabled:opacity-50"
                      data-testid="export-compact-pdf-btn"
                      data-pdf-hide="true"
                      title="1-page A4 landscape: Growth / Mortality / RFI charts only (pre-MFI view)"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      {exportingCompactPdf ? 'Compact…' : 'Compact Review PDF'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearForm}
                      className="text-gray-600"
                      data-pdf-hide="true"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Clear
                    </Button>
                  </div>
                </div>

                {/* Admin-only Daily Model Recommendation Adjustment table.
                    Mounted at the TOP of the Analysis Results — OUTSIDE the
                    Summary/Full view-mode toggle and OUTSIDE every
                    sectionVisibility gate — so admins always see it the
                    moment a record loads. When the master toggle is on and
                    overrides exist, the SSOT injector inside the
                    `daily_analysis_rows` useMemo rewrites
                    `recommended_feed_kg` per-date and every downstream
                    KPI/chart/narrative/PDF re-reads from there.
                    Non-admins do not see this block in the DOM. */}
                {user?.is_admin && Array.isArray(daily_analysis_rows) && daily_analysis_rows.length > 0 && (
                  <div data-pdf-hide="true">
                    <RFIAdjustmentTable
                      rfiScenario={rfiScenario}
                      processedDailyRows={daily_analysis_rows}
                    />
                  </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════════════
                    VERIFIED HARVEST CORRECTION — Top-of-report banner. Surfaces
                    when a harvest-derived sample produced a >=2x / +20% upward
                    correction confirmed by >=2 subsequent harvest samples.
                    Read-only analytical signal — does not mutate any underlying
                    data. Hidden from PDF export per current scope.
                    ═══════════════════════════════════════════════════════════════════════ */}
                {vhcActive && (
                  <div data-pdf-hide="true">
                    <VerifiedHarvestCorrectionCard signal={verifiedHarvestCorrection} />
                  </div>
                )}

                {/* ═══════════════════════════════════════════════════════════════════════
                    SUMMARY REPORT VIEW — Simplified client-facing decision report
                    ═══════════════════════════════════════════════════════════════════════ */}
                {viewMode === 'summary' && missingFishIntelligence && (
                  <SummaryReportView
                    activeRecord={effectiveActiveRecord || activeRecord}
                    missingFishIntelligence={missingFishIntelligence}
                    economicParams={economicParams}
                    dataQuality={dataQuality}
                    modelFCR={modelFCR}
                    fcrResult={fcrResult}
                    biologicalFCR={biologicalFCR}
                    cageStatus={cageStatus}
                    manualMissingFishOverride={manualMissingFishOverride}
                    manualAssumedFishNumber={fishEstimationMode === 'manual' ? assumedFishNumber : null}
                    activeFcrVersion={activeFcrVersion}
                    modelAdjustmentPercent={modelAdjustmentPercent}
                  />
                )}

                {/* ═══════════════════════════════════════════════════════════════════════
                    FULL REPORT VIEW — Complete analysis with all diagnostics
                    ═══════════════════════════════════════════════════════════════════════ */}
                {viewMode === 'full' && (
                <>
                {/* Summary KPIs */}
                {sectionVisibility.sampleMetrics && (
                <>
                {/* ── Estimated Missing Fish — Hero KPI ── */}
                {missingFishIntelligence && (() => {
                  const ssotMissing = missingFishIntelligence?.ssot?.rawMissingFishCount ?? missingFishIntelligence?.ssot?.missingFishCount ?? 0;
                  const fcrPanelMissing = fcrResult?.fcrMissingFish;
                  const fcrEstMissing = fcrResult?.estimatedMissing || 0;
                  // Manual Fish Count Adjustment override:
                  // When user is in 'manual' mode and has set an explicit
                  // count, surface (reported - manual) as the authoritative
                  // missing-fish estimate, overriding the MFI-engine value.
                  const heroMissing = manualMissingFishOverride !== null
                    ? manualMissingFishOverride
                    : (fcrPanelMissing != null && fcrPanelMissing > 0) ? fcrPanelMissing : (ssotMissing > 0) ? ssotMissing : (fcrEstMissing > 0) ? fcrEstMissing : 0;
                  const heroPercent = activeRecord?.input_number > 0 && heroMissing > 0 ? ((heroMissing / activeRecord.input_number) * 100).toFixed(1) : 0;
                  const heroSource = manualMissingFishOverride !== null ? 'manual' : 'engine';
                  // Admin-only baseline floor: real production cycles almost never
                  // end with zero unaccounted fish, so admins see a conservative
                  // baseline floor with a distinct grey badge instead of the
                  // emerald "No deviation detected" state. Gated by the site-wide
                  // toggle in Admin Console.
                  const showAdminBaseline = !!user?.is_admin && adminBaselineEnabled && heroMissing === 0;
                  const baselineDisplayFloor = adminBaselineFloor || 10000;
                  return (
                <div className={`rounded-xl p-5 border-2 shadow-md ${
                  heroMissing > 0
                    ? 'bg-red-950/[0.03] border-red-700/40'
                    : showAdminBaseline
                      ? 'bg-slate-100 border-slate-300'
                      : 'bg-emerald-950/[0.03] border-emerald-600/30'
                }`} data-testid="missing-fish-hero-kpi">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${
                        heroMissing > 0 ? 'text-red-700' : showAdminBaseline ? 'text-slate-600' : 'text-emerald-700'
                      }`}>
                        Estimated Missing Fish
                      </p>
                      {showAdminBaseline ? (
                        <p className="text-4xl font-extrabold tracking-tight text-slate-700" data-testid="hero-missing-fish-admin-baseline">
                          {baselineDisplayFloor.toLocaleString()} <span className="text-base font-semibold text-slate-500 align-middle">(baseline)</span>
                        </p>
                      ) : (
                        <p className={`text-4xl font-extrabold tracking-tight ${heroMissing > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                          {heroMissing > 0 ? `~${heroMissing.toLocaleString()}` : 'No deviation detected'}
                        </p>
                      )}
                      {heroMissing > 0 && heroPercent > 0 && (
                        <p className="text-sm text-red-600/80 mt-1 font-medium">{heroPercent}% of starting stock unaccounted for</p>
                      )}
                      {heroMissing === 0 && !showAdminBaseline && (
                        <p className="text-sm text-emerald-600/70 mt-1">Stock appears consistent with reported numbers</p>
                      )}
                      {showAdminBaseline && (
                        <p className="text-sm text-slate-500 mt-1">Admin baseline — analysis returned 0, but production cycles rarely have zero unaccounted fish. Not a genuine detection.</p>
                      )}
                      {heroSource === 'manual' && assumedFishNumber && (
                        <p className="text-[11px] text-purple-700 mt-1.5 font-semibold" data-testid="hero-missing-fish-source-manual">
                          Based on your manual fish count adjustment ({assumedFishNumber.toLocaleString()})
                        </p>
                      )}
                    </div>
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                      heroMissing > 0 ? 'bg-red-700/10' : showAdminBaseline ? 'bg-slate-200' : 'bg-emerald-600/10'
                    }`}>
                      <AlertTriangle className={`w-7 h-7 ${heroMissing > 0 ? 'text-red-700' : showAdminBaseline ? 'text-slate-500' : 'text-emerald-600'}`} />
                    </div>
                  </div>
                </div>
                  );
                })()}

                {/* ── Operator (AquaManager / Fishtalk) vs PFD Best-Fit Fish Number ──
                    Only renders when the record was imported via a direct
                    upload AND MFI has produced a best-fit count. Validation
                    signal only — neither number is overwritten. */}
                {(activeRecord?.aquamanager_fish_number != null || activeRecord?.fishtalk_fish_number != null) && assumedFishNumber != null && (
                  <AquaManagerFishComparison
                    aquamanagerFishNumber={activeRecord.aquamanager_fish_number}
                    aquamanagerFishNumberDate={activeRecord.aquamanager_fish_number_date}
                    fishtalkFishNumber={activeRecord.fishtalk_fish_number}
                    fishtalkFishNumberDate={activeRecord.fishtalk_fish_number_date}
                    pfdBestFitFishNumber={assumedFishNumber}
                  />
                )}

                {/* ── Core Production KPIs — Professional Grid ── */}
                <div className="grid sm:grid-cols-5 gap-3 text-sm">
                  <div className="bg-white rounded-lg p-3.5 text-center border border-slate-200 shadow-sm">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1">Start Weight</p>
                    <p className="text-lg font-bold text-slate-900">
                      {typeof activeRecord.input_average_weight === 'number'
                        ? `${activeRecord.input_average_weight.toFixed(1)}g`
                        : `${activeRecord.input_average_weight}g`}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-3.5 text-center border border-slate-200 shadow-sm">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1">Start Count</p>
                    <p className="text-lg font-bold text-slate-900">{activeRecord.input_number?.toLocaleString()}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3.5 text-center border border-slate-200 shadow-sm">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1">Days in Water</p>
                    <p className="text-lg font-bold text-slate-900">{effectiveActiveRecord?.days_in_water || activeRecord.days_in_water || activeRecord.data_points?.length || 0}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3.5 text-center border border-slate-200 shadow-sm">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1">Sample Updates</p>
                    <p className="text-lg font-bold text-slate-900">{activeRecord.sample_points?.length || 0}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3.5 text-center border border-emerald-200 shadow-sm">
                    <p className="text-[10px] text-emerald-700 uppercase tracking-wider font-semibold mb-1">Total Harvest</p>
                    <p className="text-lg font-bold text-slate-900">{(effectiveActiveRecord?.total_harvest_count || activeRecord.total_harvest_count || 0).toLocaleString()}</p>
                  </div>
                </div>
                </>
                )}

                {/* Feeding & Mortality Metrics Row — Professional styling */}
                {sectionVisibility.feedingMetrics && (
                <div className="grid sm:grid-cols-3 md:grid-cols-6 gap-3 text-sm" data-testid="feeding-mortality-metrics">
                  <div className="bg-white rounded-lg p-3.5 text-center border border-slate-200 shadow-sm">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1">Feeding Days</p>
                    <p className="text-lg font-bold text-slate-900">{effectiveActiveRecord?.feeding_days ?? activeRecord.feeding_days ?? 0}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3.5 text-center border border-slate-200 shadow-sm">
                    <p className="text-[10px] text-amber-700 uppercase tracking-wider font-semibold mb-1">Misfeeding Days</p>
                    <p className="text-lg font-bold text-slate-900">{effectiveActiveRecord?.no_feed_days ?? activeRecord.no_feed_days ?? 0}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3.5 text-center border border-slate-200 shadow-sm">
                    <p className="text-[10px] text-indigo-700 uppercase tracking-wider font-semibold mb-1">Feeding Performance</p>
                    <p className="text-lg font-bold text-slate-900">
                      {(effectiveActiveRecord?.percent_feeding_of_model ?? activeRecord.percent_feeding_of_model) !== null && (effectiveActiveRecord?.percent_feeding_of_model ?? activeRecord.percent_feeding_of_model) !== undefined 
                        ? `${effectiveActiveRecord?.percent_feeding_of_model ?? activeRecord.percent_feeding_of_model}%` 
                        : 'N/A'}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-3.5 text-center border border-slate-200 shadow-sm">
                    <p className="text-[10px] text-red-700 uppercase tracking-wider font-semibold mb-1">Total Mortalities</p>
                    <p className="text-lg font-bold text-slate-900">{(effectiveActiveRecord?.total_mortalities || activeRecord.total_mortalities || 0).toLocaleString()}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3.5 text-center border border-slate-200 shadow-sm">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1">Total Feed Given</p>
                    <p className="text-lg font-bold text-slate-900">{(effectiveActiveRecord?.total_feed_given || activeRecord.total_feed_given || 0).toLocaleString()} kg</p>
                  </div>
                  {activeRecord.model_adjustment_percent && activeRecord.model_adjustment_percent !== 100 && (
                    <div className="bg-white rounded-lg p-3.5 text-center border border-slate-200 shadow-sm">
                      <p className="text-[10px] text-amber-700 uppercase tracking-wider font-semibold mb-1">Model Adjustment</p>
                      <p className="text-lg font-bold text-slate-900">{activeRecord.model_adjustment_percent}%</p>
                    </div>
                  )}
                </div>
                )}

                {/* ====== CAGE STATUS + BIOLOGICAL FCR ====== */}
                {sectionVisibility.biologicalFCR && biologicalFCR && fcrResult && (
                  <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4" data-testid="biological-fcr-panel">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Scale className="w-5 h-5 text-indigo-600" />
                        <h3 className="text-sm font-bold text-slate-800">Closing Biological FCR</h3>
                      </div>
                      {/* Cage Status Selector */}
                      <div className="flex items-center gap-2" data-testid="cage-status-selector" data-pdf-hide="true">
                        <label className="text-xs text-gray-500 font-medium">Cage Status:</label>
                        <Select
                          value={cageStatus}
                          onValueChange={async (val) => {
                            setCageStatus(val);
                            // Persist the user's choice so it survives reload.
                            // Default is 'ongoing'; only a user-set 'closed'
                            // persists on the record via `cage_closed: true`.
                            if (!activeRecord?.id) return;
                            try {
                              await axios.patch(
                                `${BACKEND_URL}/api/spreadsheet/records/${activeRecord.id}/cage-status`,
                                { cage_closed: val === 'closed' },
                                { headers: { Authorization: `Bearer ${token}` } },
                              );
                              // Reflect persisted flag locally so subsequent
                              // reloads render the same status without flicker.
                              setActiveRecord((r) => (r ? { ...r, cage_closed: val === 'closed' } : r));
                            } catch (err) {
                              console.error('Failed to persist cage status:', err);
                              toast.error('Failed to save cage status');
                            }
                          }}
                        >
                          <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="cage-status-trigger">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ongoing" data-testid="cage-status-ongoing">Ongoing Cage</SelectItem>
                            <SelectItem value="closed" data-testid="cage-status-closed">Closed Cage</SelectItem>
                          </SelectContent>
                        </Select>
                        {/* SAVE CLOSURE — only visible when Closed is selected.
                            The dropdown already auto-persists on change; this
                            button lets the user explicitly re-save + shows a
                            confirmed "Saved" pill so they can be certain the
                            record will be treated as closed going forward. */}
                        {cageStatus === 'closed' && (
                          <button
                            type="button"
                            disabled={savingClosure || !activeRecord?.id}
                            data-testid="save-closure-btn"
                            aria-label="Save cage as closed"
                            onClick={async () => {
                              if (!activeRecord?.id) return;
                              setSavingClosure(true);
                              try {
                                await axios.patch(
                                  `${BACKEND_URL}/api/spreadsheet/records/${activeRecord.id}/cage-status`,
                                  { cage_closed: true },
                                  { headers: { Authorization: `Bearer ${token}` } },
                                );
                                setActiveRecord((r) => (r ? { ...r, cage_closed: true } : r));
                                setClosureSavedAt(Date.now());
                                toast.success('Cage saved as closed');
                                // Clear the pill after a few seconds so the
                                // UI returns to its resting state.
                                setTimeout(() => setClosureSavedAt(null), 3500);
                              } catch (err) {
                                console.error('Failed to save closure:', err);
                                toast.error('Failed to save closure');
                              } finally {
                                setSavingClosure(false);
                              }
                            }}
                            className={`inline-flex items-center gap-1 h-8 px-2.5 text-xs rounded-md border transition-colors ${
                              closureSavedAt
                                ? 'bg-emerald-600 border-emerald-600 text-white'
                                : 'bg-white border-emerald-500 text-emerald-700 hover:bg-emerald-50'
                            } ${savingClosure ? 'opacity-70 cursor-wait' : ''}`}
                          >
                            {savingClosure ? (
                              <>
                                <Loader2 className="w-3 h-3 animate-spin" /> Saving…
                              </>
                            ) : closureSavedAt ? (
                              <>
                                <Check className="w-3.5 h-3.5" /> Saved
                              </>
                            ) : (
                              <>
                                <Save className="w-3.5 h-3.5" /> Save
                              </>
                            )}
                          </button>
                        )}
                        {/* ★ H11 — warn the operator when the status is 'ongoing' despite
                             harvest events existing. Purely advisory; does not block the
                             selection in case of partial harvests. */}
                        {cageStatus === 'ongoing' && (activeRecord?.total_harvest_count || 0) > 0 && (
                          <span
                            className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5"
                            title={`This record has ${(activeRecord?.total_harvest_count || 0).toLocaleString()} harvested fish. Consider switching to 'Closed Cage'.`}
                            data-testid="cage-status-harvest-warning"
                          >
                            Harvest data present — consider Closed
                          </span>
                        )}
                      </div>
                    </div>

                    {/* FCR Display — Reported, Model, Adjusted + Missing Fish Estimate */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      {/* Reported FCR */}
                      <div className="bg-white rounded-lg p-4 border-l-4 border-l-slate-400 border border-slate-100 shadow-md text-center" data-testid="fcr-reported-section">
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium mb-2">Reported FCR</p>
                        {fcrResult.reported.fcr !== null ? (
                          <p className="text-2xl font-bold text-slate-800" data-testid="fcr-reported-value">{fcrResult.reported.fcr.toFixed(2)}</p>
                        ) : (
                          <p className="text-sm text-gray-400 italic" data-testid="fcr-reported-value">N/A</p>
                        )}
                        <p className="text-[10px] text-gray-400 mt-0.5">Fish: <span className="font-semibold text-gray-600" data-testid="fcr-reported-fish-count">{fcrResult.reportedN.toLocaleString()}</span></p>
                      </div>

                      {/* Model FCR */}
                      <div className="bg-white rounded-lg p-4 border-l-4 border-l-indigo-400 border border-indigo-50 shadow-md text-center" data-testid="fcr-model-section">
                        <p className="text-[10px] text-indigo-400 uppercase tracking-wider font-medium mb-2">Model FCR</p>
                        {modelFCR ? (
                          <p className="text-2xl font-bold text-indigo-800" data-testid="fcr-model-value">{modelFCR.toFixed(2)}</p>
                        ) : (
                          <p className="text-sm text-gray-400 italic" data-testid="fcr-model-value">N/A</p>
                        )}
                        <p className="text-[10px] text-indigo-400 mt-0.5">{(activeRecord?.species === 'Seabream' || !activeRecord?.species) ? 'Weight+strain lookup' : 'Weight-indexed lookup'}</p>
                      </div>

                      {/* Adjusted FCR */}
                      <div className={`rounded-lg p-4 border-l-4 shadow-md text-center ${fcrResult.estimatedMissing > 0 ? 'bg-white border-l-amber-400 border border-amber-50' : 'bg-white border-l-slate-400 border border-slate-100'}`} data-testid="fcr-adjusted-section">
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium mb-2">Adjusted FCR</p>
                        {fcrResult.adjusted.fcr !== null ? (
                          <p className={`text-2xl font-bold ${fcrResult.estimatedMissing > 0 ? 'text-amber-700' : 'text-slate-800'}`} data-testid="fcr-adjusted-value">{fcrResult.adjusted.fcr.toFixed(2)}</p>
                        ) : (
                          <p className="text-sm text-gray-400 italic" data-testid="fcr-adjusted-value">N/A</p>
                        )}
                        <p className="text-[10px] text-gray-400 mt-0.5">Fish: <span className="font-semibold text-gray-600" data-testid="fcr-adjusted-fish-count">{fcrResult.adjustedN.toLocaleString()}</span></p>
                      </div>

                      {/* FCR-Based Missing Fish Estimate */}
                      <div className={`rounded-lg p-4 border-l-4 shadow-md text-center ${
                        fcrResult.fcrMissingFish?.detected
                          ? 'bg-white border-l-rose-500 border border-rose-50'
                          : 'bg-white border-l-emerald-500 border border-emerald-50'
                      }`} data-testid="fcr-missing-fish-section">
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium mb-2">Est. Missing (FCR)</p>
                        {fcrResult.fcrMissingFish?.detected ? (() => {
                          // ★ PRD range format. Reliability sourced from SSOT; fallback to 50.
                          const _rel = missingFishIntelligence?.ssot?.dataReliabilityScore ?? 50;
                          const _range = formatMissingFishRange({
                            missingFishCount: fcrResult.fcrMissingFish.estimatedMissing,
                            dataReliabilityScore: _rel,
                          });
                          return (
                            <>
                              <p className="text-2xl font-bold text-red-700" data-testid="fcr-missing-fish-value">
                                {_range.shortLabel}
                              </p>
                              {!_range.isWithinMargin && (
                                <p className="text-[10px] text-red-500 mt-0.5">
                                  midpoint ~{_range.midpointLabel}
                                </p>
                              )}
                              <p className="text-[10px] text-red-500 mt-0.5">
                                FCR gap {fcrResult.fcrMissingFish.fcrGapPercent.toFixed(1)}%
                              </p>
                            </>
                          );
                        })() : (
                          <>
                            <p className="text-lg font-bold text-green-700" data-testid="fcr-missing-fish-value">None</p>
                            <p className="text-[10px] text-green-500 mt-0.5">FCR within model range</p>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Missing fish evidence flag — ★ finalDecision ONLY */}
                    {effectiveOutput?.finalDecision?.topLevelSignalMessage && (
                      <div className={`flex items-center gap-2 mb-3 p-2 rounded-lg border ${
                        effectiveOutput?.finalDecision?.flags?.hasConflict 
                          ? 'bg-amber-50 border-amber-200' 
                          : 'bg-red-50 border-red-200'
                      }`} data-testid="fcr-strong-evidence-flag">
                        <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${
                          effectiveOutput?.finalDecision?.flags?.hasConflict ? 'text-amber-600' : 'text-red-600'
                        }`} />
                        <p className={`text-xs ${effectiveOutput?.finalDecision?.flags?.hasConflict ? 'text-amber-700' : 'text-red-700'}`}>
                          {effectiveOutput?.finalDecision?.topLevelSignalMessage && (
                            <span className="font-bold">{effectiveOutput.finalDecision.topLevelSignalMessage}: </span>
                          )}
                          {effectiveOutput?.finalDecision?.flags?.hasConflict 
                            ? effectiveOutput?.finalDecision?.conflictMessage
                            : `Reported FCR (${fcrResult.reported.fcr?.toFixed(2)}) is ${fcrResult.fcrMissingFish?.fcrGapPercent?.toFixed(1) || '—'}% below model FCR (${modelFCR?.toFixed(2)}). Adjusting to ${fcrResult.fcrMissingFish?.adjustedN?.toLocaleString() || '—'} fish yields FCR ${fcrResult.fcrMissingFish?.adjustedFCR?.toFixed(2) || '—'}.`
                          }
                        </p>
                      </div>
                    )}

                    {/* Breakdown Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                      <div className="bg-white rounded-lg p-3 border-l-4 border-l-teal-400 border border-teal-50 shadow-sm text-center">
                        <p className="text-[10px] text-teal-400 uppercase tracking-wider font-medium mb-1">Feed Used</p>
                        <p className="text-base font-bold text-slate-800" data-testid="fcr-feed-used">
                          {biologicalFCR.totalFeedKg.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border-l-4 border-l-slate-300 border border-slate-50 shadow-sm text-center">
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider font-medium mb-1">Starting Biomass</p>
                        <p className="text-base font-bold text-slate-800" data-testid="fcr-starting-biomass">
                          {fcrResult.reported.startBiomass.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border-l-4 border-l-emerald-400 border border-emerald-50 shadow-sm text-center">
                        <p className="text-[10px] text-emerald-400 uppercase tracking-wider font-medium mb-1">Harvest Biomass</p>
                        <p className="text-base font-bold text-slate-800" data-testid="fcr-harvest-biomass">
                          {biologicalFCR.harvestBiomassKg.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg
                        </p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border-l-4 border-l-rose-400 border border-rose-50 shadow-sm text-center">
                        <p className="text-[10px] text-rose-400 uppercase tracking-wider font-medium mb-1">Mortality Biomass</p>
                        <p className="text-base font-bold text-slate-800" data-testid="fcr-mortality-biomass">
                          {biologicalFCR.mortalityBiomassKg.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg
                        </p>
                      </div>
                      <div className={`bg-white rounded-lg p-3 border-l-4 shadow-sm text-center ${cageStatus === 'closed' ? 'border-l-amber-400 border border-amber-50' : 'border-l-indigo-400 border border-indigo-50'}`}>
                        <p className={`text-[10px] uppercase tracking-wider font-medium mb-1 ${cageStatus === 'closed' ? 'text-amber-400' : 'text-indigo-400'}`}>Closing Biomass</p>
                        <p className={`text-base font-bold text-slate-800`} data-testid="fcr-closing-biomass">
                          {fcrResult.reported.closingBiomass.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg
                        </p>
                      </div>
                    </div>

                    {/* Closed cage note */}
                    {cageStatus === 'closed' && (
                      <p className="text-xs text-amber-700 mt-2 bg-amber-50 rounded p-2 border border-amber-200" data-testid="closed-cage-note">
                        Remaining fish ({biologicalFCR.reportedRemaining.toLocaleString()}) treated as missing fish. Closing biomass set to zero.
                      </p>
                    )}

                    {/* ── Feed Intensity — Note only (detailed cards removed) ── */}
                    {feedIntensity && feedIntensity.isAdjusted && (
                      <div className="mt-3 pt-3 border-t border-slate-200" data-testid="feed-intensity-section">
                        <p className="text-[10px] text-slate-500 italic" data-testid="feed-intensity-note">
                          Historical feed delivered is unchanged. Under the adjusted fish count, feed per fish and biomass-relative feeding rate increase. The same historical feed distributed across fewer fish results in a higher biological feeding rate.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Chart */}
                {/* Run Missing Fish Intelligence — prominent top position */}
                <div className="flex flex-col items-center gap-3 py-2" data-pdf-hide="true">
                  <Button
                    onClick={runMissingFishIntelligence}
                    disabled={!hasSampleWeights || cageStatus === 'closed'}
                    className="bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 px-8 py-3 text-base rounded-full shadow-lg hover:shadow-xl transition-all"
                    data-testid="run-missing-fish-intelligence-btn"
                  >
                    <Brain className="w-5 h-5 mr-2" />
                    Run Missing Intelligence
                  </Button>

                  {/* Guard Rails / Indicative Report toggle — visible whenever
                      MFI has run AND there is a computable adjusted fish number,
                      regardless of confidenceMode or growth direction. This keeps
                      Seabass / Tilapia / Seabream behaviour identical under the
                      toggle so users can always reveal the amber adjusted line
                      when an FCR or jump signal exists. */}
                  {showMissingFishIntelligence && missingFishIntelligence && effectiveOutput?.finalDecision && effectiveAssumedN && activeRecord?.input_number && effectiveAssumedN !== activeRecord.input_number && (
                    <div className="w-full max-w-3xl" data-testid="view-all-data-section">
                      <GuardrailsToggle
                        active={showAllDataOverride}
                        onChange={setShowAllDataOverride}
                        ariaLabel="Toggle Indicative Report Mode (bypass confidence guardrails)"
                        testId="view-all-data-toggle"
                      />
                    </div>
                  )}
                </div>

                {/* ★ PLANNING ESTIMATE SUMMARY — Single headline answer */}
                {showMissingFishIntelligence && missingFishIntelligence && (
                  <PlanningEstimateSummary
                    ssot={missingFishIntelligence?.ssot}
                    finalDecision={effectiveOutput?.finalDecision}
                    methodResolution={missingFishIntelligence?.methodResolution}
                    decisionContext={missingFishIntelligence?.decisionContext}
                    guardrailsOff={showAllDataOverride}
                  />
                )}

                {/* Advanced Diagnostics — secondary scoring (Stock Integrity, etc.)
                    PRD UX simplification: keeps the top decision flow focused on
                    Data Reliability / Detection Confidence / Operational Decision
                    State and tucks the secondary scoring systems into a
                    collapsible block to reduce cognitive overload. Collapsed by
                    default. Calculations & component output unchanged. */}
                {sectionVisibility.stockIntegrity && showMissingFishIntelligence && missingFishIntelligence && (
                  <div className="bg-white rounded-lg border border-slate-200 shadow-sm" data-testid="advanced-diagnostics-card">
                    <button
                      type="button"
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-slate-50 transition-colors"
                      onClick={() => setShowAdvancedDiagnostics(!showAdvancedDiagnostics)}
                      data-testid="toggle-advanced-diagnostics-btn"
                      aria-expanded={showAdvancedDiagnostics}
                    >
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-slate-700">Advanced Diagnostics</h3>
                        <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">Secondary scores</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">{showAdvancedDiagnostics ? 'Click to minimize' : 'Click to expand'}</span>
                        {showAdvancedDiagnostics ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </button>
                    {showAdvancedDiagnostics && (
                      <div className="border-t border-slate-200 p-3" data-testid="advanced-diagnostics-content">
                        <p className="text-[11px] text-slate-500 mb-3 leading-relaxed">
                          The Stock Integrity Score is a secondary diagnostic. The primary decision signals you should rely on are <strong>Data Reliability</strong>, <strong>Detection Confidence</strong>, and the <strong>Operational Decision State</strong> shown above.
                        </p>
                        <SpreadsheetStockIntegrity 
                          missingFishIntelligence={missingFishIntelligence}
                          overriddenOutput={effectiveOutput}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* ====== PDF PAGE 1: All Charts ====== */}
                <div ref={pdfChartsRef} className="space-y-4 bg-white">
                  {/* ====== CHART 1: Growth vs Model ====== */}
                  {sectionVisibility.growthChart && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-gray-800">Growth Comparison Chart</h3>
                        {activeRecord.model_adjustment_percent && activeRecord.model_adjustment_percent !== 100 && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                            Model adjusted to {activeRecord.model_adjustment_percent}%
                          </span>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setChartExpanded(true)}
                        className="text-[#0891b2]"
                        data-testid="expand-chart-btn"
                        data-pdf-hide="true"
                      >
                        <Maximize2 className="w-4 h-4 mr-1" />
                        Expand
                      </Button>
                    </div>
                    
                    
                    {/* Graph Insight — ★ finalDecision ONLY */}
                    {effectiveOutput?.finalDecision?.keyInsight && (
                      <div className={`mb-3 px-3 py-2 border-l-4 rounded-r-md ${
                        effectiveOutput?.finalDecision?.flags?.hasConflict ? 'bg-amber-50 border-amber-500' : 'bg-cyan-50 border-cyan-500'
                      }`} data-testid="graph-insight-growth">
                        <p className={`text-[10px] font-semibold uppercase tracking-wider mb-0.5 ${
                          effectiveOutput?.finalDecision?.flags?.hasConflict ? 'text-amber-700' : 'text-cyan-700'
                        }`}>Key Insight</p>
                        <p className={`text-xs ${effectiveOutput?.finalDecision?.flags?.hasConflict ? 'text-amber-800' : 'text-cyan-800'}`}>
                          {effectiveOutput.finalDecision.keyInsight}
                        </p>
                      </div>
                    )}

                    {/* Legend */}
                    <div className="flex flex-wrap gap-4 mb-4 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-0.5 bg-blue-600" style={{borderTop: '3px dashed #2563eb'}}></div>
                        <span className="text-gray-600">
                          {activeRecord.model_adjustment_percent && activeRecord.model_adjustment_percent !== 100
                            ? `Model Growth (${activeRecord.model_adjustment_percent}%)`
                            : (activeRecord.species === 'Tilapia' || activeRecord.species === 'Tripletail')
                              ? 'Model Growth (static curve)'
                              : 'Model Growth (SGR-based)'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-0.5 bg-red-600" style={{height: '3px'}}></div>
                        <span className="text-gray-600">Actual Growth (SGR-corrected)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-600"></div>
                        <span className="text-gray-600">Sample Points</span>
                      </div>
                    </div>
                    
                    {/* ====== CYCLE COMPARISON SELECTOR (Admin Only) ====== */}
                    {user?.is_admin && sectionVisibility.cycleComparison && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200" data-pdf-hide="true">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                          <GitCompareArrows className="w-4 h-4 text-purple-600" />
                          Compare with saved analyses
                        </span>
                        {comparisonAnalyses.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setComparisonAnalyses([])}
                            className="text-gray-500 hover:text-red-600 h-6 px-2"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Clear
                          </Button>
                        )}
                      </div>

                      {/* Searchable multi-select popover — every saved analysis
                          (excluding the currently-open record) is available for
                          comparison, not just the first few. Pick up to 5. */}
                      <ComparePicker
                        allRecords={records}
                        activeRecordId={activeRecord?.id}
                        selected={comparisonAnalyses}
                        setSelected={setComparisonAnalyses}
                      />

                      {/* Currently-selected chips with colour dot per record. */}
                      {comparisonAnalyses.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {comparisonAnalyses.map((rec, idx) => {
                            const c = COMPARISON_COLORS[idx % COMPARISON_COLORS.length];
                            return (
                              <span
                                key={rec.id}
                                className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border border-purple-300 bg-purple-50 text-purple-800"
                                data-testid={`compare-selected-chip-${rec.id}`}
                              >
                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.actual }} />
                                <span className="truncate max-w-[180px]">{rec.name}</span>
                                <button
                                  type="button"
                                  onClick={() => setComparisonAnalyses(prev => prev.filter(r => r.id !== rec.id))}
                                  className="ml-0.5 text-purple-500 hover:text-rose-600"
                                  aria-label={`Remove ${rec.name}`}
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {/* Comparison info */}
                      {comparisonAnalyses.length > 0 && (
                        <p className="text-xs text-purple-600 mt-2">
                          Comparing {comparisonAnalyses.length} cycle{comparisonAnalyses.length > 1 ? 's' : ''} (normalized by Day 0){comparisonAnalyses.length === 5 ? ' · maximum of 5 reached' : ''}
                        </p>
                      )}
                    </div>
                    )}

                    <div>
                      <div className="bg-slate-50/50 rounded-xl p-4 border border-slate-100" data-pdf-chart="growth">
                        {renderGrowthChart(220)}
                      </div>

                      {/* Chart Reading Guide */}
                      <div className="mt-3 px-3 py-2.5 bg-gray-50 rounded-lg border border-gray-200" data-testid="chart-reading-guide">
                        <h4 className="text-xs font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                          <Info className="w-3.5 h-3.5 text-slate-500" />
                          How to Read This Chart
                        </h4>
                        <ul className="space-y-1 text-[11px] text-gray-600 leading-relaxed">
                          <li className="flex items-start gap-2">
                            <span className="w-4 h-0.5 mt-1.5 shrink-0 bg-blue-600" style={{borderTop: '2px dashed #2563eb'}}></span>
                            <span><strong className="text-gray-700">Model Growth (dashed blue line)</strong> — {(activeRecord?.species === 'Tilapia' || activeRecord?.species === 'Tripletail') ? 'The theoretical expected growth from the static day-indexed curve (temperature-independent for this species).' : 'The theoretical expected growth based on the SGR × temperature model.'} This is where fish weight should be if growth follows the standard curve.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="w-4 h-0.5 mt-1.5 shrink-0 bg-red-600" style={{height: '2px'}}></span>
                            <span><strong className="text-gray-700">Actual Growth (solid red line)</strong> — The reconstructed growth path based on daily feed data. Between sample points, growth is projected using feed-implied SGR calculations.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="w-2.5 h-2.5 mt-1 shrink-0 rounded-full bg-red-600"></span>
                            <span><strong className="text-gray-700">Sample Points (red dots)</strong> — Actual weight measurements from fish sampling. When a sample is recorded, the actual growth line recalibrates to the measured weight. A visible direction change or "kink" at a sample point shows the correction between projected and real weight — a large jump may indicate unrecorded mortality or feeding discrepancies.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="w-4 h-0.5 mt-1.5 shrink-0 bg-amber-500" style={{borderTop: '2px dashed #f59e0b'}}></span>
                            <span><strong className="text-gray-700">Adjusted Line (dashed amber line)</strong> — Best-fit trajectory through the sample weights, showing what each fish's weight would average if the population were <em>{effectiveAssumedN && effectiveAssumedN !== (activeRecord?.input_number || 0) ? effectiveAssumedN.toLocaleString() : 'the adjusted count'}</em> instead of the reported {(activeRecord?.input_number || 0).toLocaleString()}. The curve anchors exactly to every red sample dot and interpolates between them using the feed-biomass balance — divergence from the red actual line is the visual proof of the missing-fish signal. Only visible when Missing Fish Intelligence produces an adjusted fish count.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="relative w-4 h-4 mt-0.5 shrink-0 flex items-start justify-center">
                              <span className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-0.5" style={{borderLeft: '2px dashed #dc2626'}}></span>
                              <span className="relative text-[10px] leading-none text-red-600 font-bold">⚠</span>
                            </span>
                            <span><strong className="text-gray-700">Incorrect-Sample Flag (red dashed vertical line with ⚠)</strong> — Anchored to a specific sample date, this marker means the recorded weight is inconsistent with the surrounding feed and growth evidence (e.g. a weight jump that the feed ledger cannot support). Treat that sample as suspect: re-check the field notes, scale calibration, and whether mortality was correctly logged before acting on downstream metrics.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="w-4 h-3 mt-0.5 shrink-0 rounded-sm" style={{backgroundColor: 'rgba(245,158,11,0.15)', borderLeft: '2px solid #f59e0b', borderRight: '2px solid #f59e0b'}}></span>
                            <span><strong className="text-gray-700">Loss Window (amber shaded band)</strong> — A vertical date range highlighted in faint amber with <em>Loss Window</em> labelled on top. It marks the most probable interval during which a trajectory jump (biomass loss) occurred — the actual line decoupled from the feed-implied model across this window. Use it as the focus window for reviewing mortality, escapes, or data gaps.</span>
                          </li>
                        </ul>
                      </div>

                      {/* ★ Context-aware Wingman trigger prompts — appears
                          directly below "How to Read This Chart" per PRD. */}
                      <WingmanTriggerPrompts moduleName="Spreadsheet Uploader" />
                      
                      {/* Line Visibility Controls - Below chart */}
                      <div className="mt-3" data-testid="line-visibility-panel" data-pdf-hide="true">
                        <div className="bg-gray-50 rounded-lg border border-gray-200 px-3 py-2">
                          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
                            <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                              <GitCompareArrows className="w-3 h-3" />
                              Line Visibility
                            </h4>
                          
                            {/* Current analysis lines */}
                            <label className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-1 py-0.5" data-testid="visibility-model-growth">
                              <input
                                type="checkbox"
                                checked={lineVisibility.modelGrowth}
                                onChange={(e) => setLineVisibility(prev => ({ ...prev, modelGrowth: e.target.checked }))}
                                className="w-3.5 h-3.5 rounded border-blue-500 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="w-3 h-0.5 bg-blue-500 rounded"></span>
                              <span className="text-xs text-gray-700">Model Growth</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-1 py-0.5" data-testid="visibility-actual-growth">
                              <input
                                type="checkbox"
                                checked={lineVisibility.actualGrowth}
                                onChange={(e) => setLineVisibility(prev => ({ ...prev, actualGrowth: e.target.checked }))}
                                className="w-3.5 h-3.5 rounded border-red-500 text-red-600 focus:ring-red-500"
                              />
                              <span className="w-3 h-0.5 bg-red-500 rounded"></span>
                              <span className="text-xs text-gray-700">Actual Growth</span>
                            </label>
                            {/* Yellow-dotted adjusted-growth toggle. Rendered only when the
                                chart would actually show the line (showAdjustedCurve) so the
                                checkbox doesn't appear on cages where there's nothing to toggle. */}
                            {showAdjustedCurve && (
                              <label className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-1 py-0.5" data-testid="visibility-adjusted-growth">
                                <input
                                  type="checkbox"
                                  checked={lineVisibility.adjustedGrowth}
                                  onChange={(e) => setLineVisibility(prev => ({ ...prev, adjustedGrowth: e.target.checked }))}
                                  className="w-3.5 h-3.5 rounded border-amber-500 text-amber-600 focus:ring-amber-500"
                                />
                                <span className="w-3 h-0.5 border-t-2 border-dashed border-amber-500"></span>
                                <span className="text-xs text-gray-700">Adjusted Growth</span>
                              </label>
                            )}
                            <label className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-1 py-0.5" data-testid="visibility-sample-points">
                              <input
                                type="checkbox"
                                checked={lineVisibility.samplePoints}
                                onChange={(e) => setLineVisibility(prev => ({ ...prev, samplePoints: e.target.checked }))}
                                className="w-3.5 h-3.5 rounded border-teal-500 text-teal-600 focus:ring-teal-500"
                              />
                              <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                              <span className="text-xs text-gray-700">Sample Points</span>
                            </label>

                            {/* Potential-loss visual overlays — shown only when a
                                trajectory jump has actually been detected for this
                                cage. Disabled on cages with no jump so the panel
                                stays uncluttered. */}
                            {trajectoryJump?.jump_event_detected && (
                              <>
                                <label className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-1 py-0.5" data-testid="visibility-loss-event-marker">
                                  <input
                                    type="checkbox"
                                    checked={lineVisibility.lossEventMarker}
                                    onChange={(e) => setLineVisibility(prev => ({ ...prev, lossEventMarker: e.target.checked }))}
                                    className="w-3.5 h-3.5 rounded border-amber-500 text-amber-600 focus:ring-amber-500"
                                  />
                                  <span className="w-3 h-3 rounded-full ring-2 ring-amber-300 bg-amber-500" />
                                  <span className="text-xs text-gray-700">Loss Event Marker</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-1 py-0.5" data-testid="visibility-loss-window">
                                  <input
                                    type="checkbox"
                                    checked={lineVisibility.lossWindow}
                                    onChange={(e) => setLineVisibility(prev => ({ ...prev, lossWindow: e.target.checked }))}
                                    className="w-3.5 h-3.5 rounded border-amber-500 text-amber-600 focus:ring-amber-500"
                                  />
                                  <span className="w-3 h-2 rounded-sm border border-dashed border-amber-500 bg-amber-100" />
                                  <span className="text-xs text-gray-700">Loss Window</span>
                                </label>
                              </>
                            )}

                            {/* Incorrect Sample Flag — red dashed line + ⚠ at MFI-detected
                                data-quality-suspect samples. Visible only when at least one
                                incorrect sample was flagged by Missing Fish Intelligence. */}
                            {missingFishIntelligence?.analyticalSignals?.incorrectSamples?.samples?.length > 0 && (
                              <label className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-1 py-0.5" data-testid="visibility-incorrect-sample-flag">
                                <input
                                  type="checkbox"
                                  checked={lineVisibility.incorrectSampleFlag}
                                  onChange={(e) => setLineVisibility(prev => ({ ...prev, incorrectSampleFlag: e.target.checked }))}
                                  className="w-3.5 h-3.5 rounded border-red-500 text-red-600 focus:ring-red-500"
                                />
                                <span className="inline-flex items-center text-red-600 font-bold text-[11px]" aria-hidden="true">
                                  &#9888;
                                </span>
                                <span className="text-xs text-gray-700">Incorrect-Sample Flag</span>
                              </label>
                            )}
                          
                            {/* Comparison analyses visibility */}
                            {comparisonAnalyses.length > 0 && comparisonAnalyses.map((comp, idx) => {
                              const colors = COMPARISON_COLORS[idx % COMPARISON_COLORS.length];
                              const compKey = `comp_${idx}`;
                              const compLabel = comp.name || `Cycle ${idx + 1}`;
                              return (
                                <div key={comp.id || idx} className="flex items-center gap-3">
                                  <label className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-1 py-0.5">
                                    <input
                                      type="checkbox"
                                      checked={lineVisibility[`${compKey}_model`] !== false}
                                      onChange={(e) => setLineVisibility(prev => ({ ...prev, [`${compKey}_model`]: e.target.checked }))}
                                      className="w-3.5 h-3.5 rounded"
                                      style={{ accentColor: colors.model }}
                                    />
                                    <span className="w-3 h-0.5 rounded" style={{ backgroundColor: colors.model }}></span>
                                    <span className="text-xs text-gray-600 truncate">{compLabel} (M)</span>
                                  </label>
                                  <label className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-1 py-0.5">
                                    <input
                                      type="checkbox"
                                      checked={lineVisibility[`${compKey}_actual`] !== false}
                                      onChange={(e) => setLineVisibility(prev => ({ ...prev, [`${compKey}_actual`]: e.target.checked }))}
                                      className="w-3.5 h-3.5 rounded"
                                      style={{ accentColor: colors.actual }}
                                    />
                                    <span className="w-3 h-0.5 rounded" style={{ backgroundColor: colors.actual }}></span>
                                    <span className="text-xs text-gray-600 truncate">{compLabel} (A)</span>
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}

                  {/* ====== DETECTION METHOD SELECTOR ====== */}
                  {cageStatus === 'ongoing' && showMissingFishIntelligence && missingFishIntelligence && (
                    <div className="bg-white p-4 rounded-lg border border-indigo-200 shadow-sm" data-testid="detection-method-selector">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-800">Detection Method</h4>
                          <p className="text-[10px] text-gray-500 mt-0.5">Primary method for estimating missing fish</p>
                        </div>
                        <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5" data-testid="detection-method-toggle">
                          <button
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${detectionMethod === 'jump' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            onClick={() => {
                              setDetectionMethod('jump');
                              // Re-run intelligence with new method
                              const reIntel = generateMissingFishIntelligence({
                                daily_analysis_rows, activeRecord,
                                economicParams: { average_feed_price_eur_per_kg: economicParams.average_feed_price_eur_per_kg, fish_price_eur_per_kg: economicParams.fish_price_eur_per_kg, target_harvest_weight_kg: economicParams.target_harvest_weight_kg },
                                weightBasedModelFCR: modelFCR,
                                reportedClosingFCR: fcrResult?.reported?.fcr || null,
                                closingBiomassData: biologicalFCR && fcrResult ? { totalFeedKg: biologicalFCR.totalFeedKg, startBiomassKg: fcrResult.reported.startBiomass, closingBiomassKg: fcrResult.reported.closingBiomass, harvestBiomassKg: biologicalFCR.harvestBiomassKg, mortalityBiomassKg: biologicalFCR.mortalityBiomassKg, reportedRemaining: fcrResult.reported.remaining, lastWeightG: biologicalFCR.lastWeightG } : null,
                                detectionMethod: 'jump',
                                fcrPanelMissingFish: fcrResult?.fcrMissingFish || null,
                                excludedIndices: excludedSampleIndices,
                                bypassSpeciesClamp: showAllDataOverride,
                              });
                              if (reIntel) {
                                setAssumedFishNumber(reIntel.initialAssumedFishNumber);
                                setMissingFishIntelligence(reIntel);
                                setTrajectoryJump(reIntel.trajectoryJump);
                                setFishEstimationMode('automatic');
                              }
                            }}
                            data-testid="method-jump"
                          >Jump Analysis</button>
                          <button
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${detectionMethod === 'fcr' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            onClick={() => {
                              setDetectionMethod('fcr');
                              const reIntel = generateMissingFishIntelligence({
                                daily_analysis_rows, activeRecord,
                                economicParams: { average_feed_price_eur_per_kg: economicParams.average_feed_price_eur_per_kg, fish_price_eur_per_kg: economicParams.fish_price_eur_per_kg, target_harvest_weight_kg: economicParams.target_harvest_weight_kg },
                                weightBasedModelFCR: modelFCR,
                                reportedClosingFCR: fcrResult?.reported?.fcr || null,
                                closingBiomassData: biologicalFCR && fcrResult ? { totalFeedKg: biologicalFCR.totalFeedKg, startBiomassKg: fcrResult.reported.startBiomass, closingBiomassKg: fcrResult.reported.closingBiomass, harvestBiomassKg: biologicalFCR.harvestBiomassKg, mortalityBiomassKg: biologicalFCR.mortalityBiomassKg, reportedRemaining: fcrResult.reported.remaining, lastWeightG: biologicalFCR.lastWeightG } : null,
                                detectionMethod: 'fcr',
                                fcrPanelMissingFish: fcrResult?.fcrMissingFish || null,
                                excludedIndices: excludedSampleIndices,
                                bypassSpeciesClamp: showAllDataOverride,
                              });
                              if (reIntel) {
                                setAssumedFishNumber(reIntel.initialAssumedFishNumber);
                                setMissingFishIntelligence(reIntel);
                                setTrajectoryJump(reIntel.trajectoryJump);
                                setFishEstimationMode('automatic');
                              }
                            }}
                            data-testid="method-fcr"
                          >FCR Analysis (Primary)</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ====== FCR ANALYSIS PANEL ====== */}
                  {/* ★ Receives overriddenOutput for SSOT-enforced narrative */}
                  {sectionVisibility.fcrAnalysis && cageStatus === 'ongoing' && showMissingFishIntelligence && missingFishIntelligence?.fcrBasedDetection && (
                    <FCRAnalysisPanel
                      fcrBasedDetection={missingFishIntelligence?.fcrBasedDetection}
                      fcrPanelMissingFish={fcrResult?.fcrMissingFish || null}
                      originalFishNumber={detectionResults?.inputs?.inputNumber || activeRecord?.input_number}
                      methodResolution={missingFishIntelligence?.methodResolution}
                      ssot={missingFishIntelligence?.ssot}
                      overriddenOutput={effectiveOutput}
                    />
                  )}

                  {/* ====== SAMPLE JUMP ANALYSIS PANEL ====== */}
                  {/* ★ Receives overriddenOutput for SSOT-enforced narrative */}
                  {sectionVisibility.sampleJump && cageStatus === 'ongoing' && trajectoryJump && (
                    <>
                      {/* VHC suppression note — appears INSIDE the MFI/jump area
                          so analysts immediately see why a jump-loss conclusion
                          is being deprioritised for this specific event. */}
                      {vhcActive && (
                        <div
                          className="border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5 rounded-md"
                          data-testid="vhc-suppression-note"
                        >
                          <p className="text-xs text-amber-900 dark:text-amber-100 leading-snug">
                            <strong>
                              {verifiedHarvestCorrection.status === 'verified'
                                ? 'Verified Harvest Correction active.'
                                : 'Possible Harvest Correction active.'}
                            </strong>{' '}
                            The jump on <strong>{verifiedHarvestCorrection.trigger?.date}</strong> originates from a
                            harvest-derived sample
                            {verifiedHarvestCorrection.status === 'verified' && verifiedHarvestCorrection.confirmations.length > 0
                              ? ` and was confirmed by ${verifiedHarvestCorrection.confirmations.length} subsequent harvest sample${verifiedHarvestCorrection.confirmations.length === 1 ? '' : 's'}.`
                              : ` but lacks sufficient subsequent harvest confirmation.`}{' '}
                            This event is interpreted as historical sample underestimation rather than fish loss; the
                            Sample Jump panel below remains visible for reference but should not be used as the sole evidence of
                            missing fish for this event.
                          </p>
                        </div>
                      )}
                      <SampleJumpAnalysisPanel
                        trajectoryJump={trajectoryJump}
                        assumedFishNumber={assumedFishNumber}
                        onAssumedFishNumberChange={(val) => {
                          setAssumedFishNumber(val);
                          if (fishEstimationMode === 'manual') setManualFishCount(String(val));
                        }}
                        originalFishNumber={detectionResults?.inputs?.inputNumber}
                        hasFeedData={!!(detectionResults?.available && detectionResults?.inputs?.startingBiomassKg)}
                        methodResolution={missingFishIntelligence?.methodResolution}
                        ssot={missingFishIntelligence?.ssot}
                        overriddenOutput={effectiveOutput}
                      />
                      {/* ★ FCR PREFERRED METHODOLOGY NOTE — Spreadsheet Uploader specific */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2" data-testid="fcr-preferred-note">
                        <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-blue-800 leading-relaxed">
                          <strong>FCR analysis is the preferred methodology</strong> for missing fish estimation due to the feed aspect of the analysis. Feed data provides a more reliable foundation for stock discrepancy detection.
                        </p>
                      </div>
                    </>
                  )}

                  {/* ====== FISH COUNT MODE SELECTOR ====== */}
                  {sectionVisibility.sampleJump && cageStatus === 'ongoing' && showMissingFishIntelligence && (
                    <div className="bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 p-6 rounded-xl border-2 border-purple-300 shadow-lg ring-2 ring-purple-100" data-testid="fish-estimation-mode">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-purple-600 rounded-lg">
                            <Fish className="w-5 h-5 text-white" />
                          </div>
                          <h4 className="text-base font-bold text-purple-900">Fish Count Adjustment</h4>
                        </div>
                        <div className="flex gap-1 bg-white rounded-lg p-1 shadow-sm border border-purple-200">
                          <button
                            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${fishEstimationMode === 'automatic' ? 'bg-purple-600 text-white shadow-md' : 'text-purple-600 hover:bg-purple-50'}`}
                            onClick={() => {
                              setFishEstimationMode('automatic');
                              if (missingFishIntelligence?.initialAssumedFishNumber) {
                                setAssumedFishNumber(missingFishIntelligence.initialAssumedFishNumber);
                              }
                              setManualFishCount('');
                            }}
                            data-testid="mode-automatic"
                          >Automatic Estimation</button>
                          <button
                            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${fishEstimationMode === 'manual' ? 'bg-purple-600 text-white shadow-md' : 'text-purple-600 hover:bg-purple-50'}`}
                            onClick={() => {
                              setFishEstimationMode('manual');
                              setManualFishCount(String(assumedFishNumber || activeRecord?.input_number || ''));
                            }}
                            data-testid="mode-manual"
                          >Manual Adjustment</button>
                        </div>
                      </div>
                      {fishEstimationMode === 'automatic' && (
                        <div className="bg-white/70 rounded-lg p-4 border border-purple-200">
                          {(() => {
                            // Show the engine-suggested adjusted count whenever
                            // Missing Fish Intelligence has produced one —
                            // even if confidenceMode ends up 'evidence' or
                            // 'signal' — so the panel always reflects the
                            // missing-fish adjusted population rather than
                            // falling back to the reported (pre-adjustment)
                            // count.
                            const _origN = activeRecord?.input_number || 0;
                            const _intelN = missingFishIntelligence?.initialAssumedFishNumber
                              || missingFishIntelligence?.fcrBasedDetection?.adjustedFishCount
                              || missingFishIntelligence?.trajectoryJump?.adjusted_fish_count
                              || missingFishIntelligence?.trajectoryJump?.best_fit_fish_number
                              || null;
                            const _activeN = assumedFishNumber || _intelN || _origN;
                            const _hasAdjustment = _intelN && _intelN !== _origN;
                            const _activeMethodLabel = missingFishIntelligence?.methodResolution?.activeMethod === 'jump'
                              ? 'Jump Analysis' : 'FCR Analysis';
                            if (effectiveOutput?.finalDecision?.missingFishUserVisible || _hasAdjustment) {
                              return (
                                <p className="text-sm text-gray-700" data-testid="fish-adjustment-auto-label">
                                  Fish count set automatically via <span className="font-bold text-indigo-700">{_activeMethodLabel}</span>:
                                  {' '}<span className="font-bold text-purple-700 text-lg">{_activeN.toLocaleString()}</span> fish
                                  {' '}<span className="text-xs text-gray-500">(reported: {_origN.toLocaleString()})</span>
                                </p>
                              );
                            }
                            return (
                              <p className="text-sm text-gray-700">Using reported fish count: <span className="font-bold text-slate-800 text-lg">{_origN.toLocaleString()}</span> fish</p>
                            );
                          })()}
                          {effectiveOutput?.finalDecision?.missingFishUserVisible && missingFishIntelligence?.methodResolution?.fallbackOccurred && (
                            <p className="text-amber-600 mt-2 text-sm">{missingFishIntelligence.methodResolution.fallbackReason || 'Fallback from Jump to FCR'}</p>
                          )}
                        </div>
                      )}
                      {fishEstimationMode === 'manual' && (
                        <div className="bg-white/70 rounded-lg p-4 border border-purple-200">
                          <div className="flex items-center gap-4">
                            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Fish count:</label>
                            <input
                              type="number"
                              value={manualFishCount}
                              onChange={(e) => {
                                const v = e.target.value;
                                setManualFishCount(v);
                                const n = parseInt(v, 10);
                                if (!isNaN(n) && n > 0) setAssumedFishNumber(n);
                              }}
                              className="w-48 px-4 py-2 text-base font-semibold border-2 border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                              placeholder="Enter fish count"
                              data-testid="manual-fish-count-input"
                            />
                            <span className="text-sm text-purple-600 font-medium">Updates all metrics in real-time</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ====== CHART 2: Mortality ====== */}
                  {sectionVisibility.mortalityChart && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-800">Mortality – Daily vs Accumulated</h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setMortalityChartExpanded(true)}
                        className="text-[#0891b2]"
                        data-testid="expand-mortality-chart-btn"
                        data-pdf-hide="true"
                      >
                        <Maximize2 className="w-4 h-4 mr-1" />
                        Expand
                      </Button>
                    </div>
                    
                    {/* Legend */}
                    <div className="flex flex-wrap gap-4 mb-3 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-0.5 bg-blue-600" style={{height: '2px'}}></div>
                        <span className="text-gray-600">Daily Mortality</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-0.5 bg-red-600" style={{height: '2px'}}></div>
                        <span className="text-gray-600">Accumulated Mortality</span>
                      </div>
                    </div>

                    <div className="bg-slate-50/50 rounded-xl p-4 border border-slate-100" data-pdf-chart="mortality">
                      {renderMortalityChart(220)}
                    </div>
                  </div>
                  )}

                  {/* ====== CHART 3: Accumulated Feed + Daily Variance ====== */}
                  {sectionVisibility.feedChart && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-gray-800">
                        Feed Analysis – Accumulated & Daily Variance{assumedFishNumber ? ' (Based on Adjusted Fish Count)' : ''}
                      </h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFeedChartExpanded(true)}
                        className="text-[#0891b2]"
                        data-testid="expand-feed-chart-btn"
                        data-pdf-hide="true"
                      >
                        <Maximize2 className="w-4 h-4 mr-1" />
                        Expand
                      </Button>
                    </div>
                    
                    {/* Legend */}
                    <div className="flex flex-wrap gap-4 mb-3 text-sm" data-pdf-hide="true">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-0.5 bg-orange-500" style={{height: '2px'}}></div>
                        <span className="text-gray-600">Daily Variance % (left axis)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-0.5 bg-green-600" style={{height: '2px'}}></div>
                        <span className="text-gray-600">Accum. Actual (right axis)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-0.5 bg-red-600" style={{height: '2px'}}></div>
                        <span className="text-gray-600">Accum. Recommended (right axis)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-0.5 bg-red-300" style={{height: '1px'}}></div>
                        <span className="text-gray-500 text-xs">100% benchmark</span>
                      </div>
                    </div>

                    {/* PDF capture scope: this wrapper carries BOTH the
                        `data-pdf-chart="feed"` selector (used by the
                        compact PDF export) AND the historical
                        `data-pdf-chart="rfi"` alias (retained for any
                        older code paths). html2canvas captures the same
                        aspect ratio as growth + mortality, so all three
                        charts come out the same visual height in the
                        exported PDF. */}
                    <div className="bg-slate-50/50 rounded-xl p-4 border border-slate-100" data-pdf-chart="feed" data-pdf-chart-alias="rfi">
                      {renderFeedChart(220)}
                    </div>

                    {/* Feed Chart Line Controls */}
                    <div className="flex flex-wrap items-center gap-3 mt-2 pt-2 border-t border-gray-100" data-testid="feed-chart-line-controls">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" className="w-3 h-3 rounded accent-orange-500" checked={feedLineVisibility.dailyVariance} onChange={(e) => setFeedLineVisibility(p => ({ ...p, dailyVariance: e.target.checked }))} data-testid="feed-toggle-dailyVariance" />
                        <span className="w-3 h-0.5 bg-orange-500 inline-block rounded" />
                        <span className="text-[10px] text-gray-600">Daily Variance</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" className="w-3 h-3 rounded accent-purple-600" checked={feedLineVisibility.accumulatedVariance} onChange={(e) => setFeedLineVisibility(p => ({ ...p, accumulatedVariance: e.target.checked }))} data-testid="feed-toggle-accumulatedVariance" />
                        <span className="w-3 h-0.5 bg-purple-600 inline-block rounded border-dashed" style={{ borderTop: '2px dashed #7c3aed', height: 0, background: 'none' }} />
                        <span className="text-[10px] text-gray-600">Accum. Variance</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" className="w-3 h-3 rounded accent-green-600" checked={feedLineVisibility.accumulatedActual} onChange={(e) => setFeedLineVisibility(p => ({ ...p, accumulatedActual: e.target.checked }))} data-testid="feed-toggle-accumulatedActual" />
                        <span className="w-3 h-0.5 bg-green-600 inline-block rounded" />
                        <span className="text-[10px] text-gray-600">Accum. Actual</span>
                      </label>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" className="w-3 h-3 rounded accent-red-600" checked={feedLineVisibility.accumulatedRecommended} onChange={(e) => setFeedLineVisibility(p => ({ ...p, accumulatedRecommended: e.target.checked }))} data-testid="feed-toggle-accumulatedRecommended" />
                        <span className="w-3 h-0.5 bg-red-600 inline-block rounded" />
                        <span className="text-[10px] text-gray-600">Accum. Recommended</span>
                      </label>
                      <div className="flex items-center gap-1.5 ml-auto">
                        <span className="text-[10px] text-gray-500">Y-Max (%):</span>
                        <input
                          type="number"
                          min="0"
                          placeholder="Auto"
                          value={varianceYMaxOverride}
                          onChange={(e) => setVarianceYMaxOverride(e.target.value)}
                          className="w-16 h-6 text-[10px] px-1.5 border border-gray-300 rounded bg-white text-gray-700 focus:outline-none focus:border-orange-400"
                          data-testid="variance-ymax-input"
                        />
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Feed Variance Comparison — Reported vs Adjusted (only when MFI adjusted) */}
                  {assumedFishNumber && sectionVisibility.feedVariance && (
                  <div className="mt-6 border-t border-gray-200 pt-4" data-testid="feed-variance-comparison">
                    <h3 className="font-semibold text-gray-800 mb-3">
                      Feed Variance Comparison – Reported vs Adjusted Fish Count
                    </h3>
                    {renderFeedVarianceComparison(220)}
                  </div>
                  )}

                  {/* Feeding Recommendation Summary — totals from feedRecommendationAnalysis.
                      Previously declared in sectionVisibility config but never rendered, so
                      enabling the "Feeding Recommendation" checkbox in Report Sections did
                      nothing and customers received reports with no feed-recommendation summary. */}
                  {sectionVisibility.feedSummary && feedRecommendationAnalysis?.available && (
                  <div className="mt-6 border-t border-gray-200 pt-4" data-testid="feeding-recommendation-summary">
                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <span className="inline-block w-1.5 h-5 bg-emerald-500 rounded-sm" />
                      Feeding Recommendation
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3" data-testid="feed-rec-actual">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total Feed Given</p>
                        <p className="text-xl font-bold text-slate-800 mt-1">
                          {feedRecommendationAnalysis.totals.total_actual_feed_kg.toLocaleString()} kg
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {feedRecommendationAnalysis.totals.days_with_feed_data} days w/ feed data
                        </p>
                      </div>
                      <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3" data-testid="feed-rec-recommended">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">SFR Recommended</p>
                        <p className="text-xl font-bold text-emerald-700 mt-1">
                          {feedRecommendationAnalysis.totals.total_recommended_feed_kg.toLocaleString()} kg
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {feedRecommendationAnalysis.totals.days_with_recommendation} days w/ recommendation
                        </p>
                      </div>
                      <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="feed-rec-percent">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Actual / Recommended</p>
                        <p className={`text-xl font-bold mt-1 ${
                          feedRecommendationAnalysis.totals.total_feed_percent === null ? 'text-slate-400' :
                          feedRecommendationAnalysis.totals.total_feed_percent > 105 ? 'text-rose-600' :
                          feedRecommendationAnalysis.totals.total_feed_percent < 95 ? 'text-amber-600' :
                          'text-emerald-600'
                        }`}>
                          {feedRecommendationAnalysis.totals.total_feed_percent !== null
                            ? `${feedRecommendationAnalysis.totals.total_feed_percent.toFixed(1)}%`
                            : '—'}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">100% = on plan</p>
                      </div>
                      <div className={`rounded-md border p-3 ${
                        feedRecommendationAnalysis.classification?.color === 'red' ? 'border-rose-200 bg-rose-50' :
                        feedRecommendationAnalysis.classification?.color === 'amber' ? 'border-amber-200 bg-amber-50' :
                        feedRecommendationAnalysis.classification?.color === 'green' ? 'border-emerald-200 bg-emerald-50' :
                        'border-slate-200 bg-slate-50'
                      }`} data-testid="feed-rec-status">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Status</p>
                        <p className={`text-xl font-bold mt-1 ${
                          feedRecommendationAnalysis.classification?.color === 'red' ? 'text-rose-700' :
                          feedRecommendationAnalysis.classification?.color === 'amber' ? 'text-amber-700' :
                          feedRecommendationAnalysis.classification?.color === 'green' ? 'text-emerald-700' :
                          'text-slate-700'
                        }`}>
                          {feedRecommendationAnalysis.classification?.status || '—'}
                        </p>
                        <p className="text-xs text-slate-600 mt-0.5">
                          {feedRecommendationAnalysis.classification?.description}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      Recommended feed = daily biomass × species-specific SFR (lookup at each day's weight + temperature). Underfeeding/overfeeding bands are ±5% of plan.
                    </p>
                  </div>
                  )}
                  {sectionVisibility.feedSummary && !feedRecommendationAnalysis?.available && (
                  <div className="mt-6 border-t border-gray-200 pt-4" data-testid="feeding-recommendation-unavailable">
                    <h3 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                      <span className="inline-block w-1.5 h-5 bg-slate-300 rounded-sm" />
                      Feeding Recommendation
                    </h3>
                    <p className="text-sm text-slate-500">
                      {feedRecommendationAnalysis?.message || 'No data available for feed recommendation summary.'}
                    </p>
                  </div>
                  )}
                </div>
                {/* ====== END PDF PAGE 1 (Charts) ====== */}

                {/* ====== SECTION VISIBILITY CONTROLS ====== */}
                <SectionVisibilityControls
                  visibility={sectionVisibility}
                  onChange={setSectionVisibility}
                />

                {/* ====== DECISION-GRADE PDF LAYOUT (Off-screen) ====== */}
                {/* Clean, client-facing PDF structure - max 3-5 pages */}
                <div ref={pdfReportRef} className="bg-white" style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '1200px' }}>
                  <DecisionReportPdf
                    activeRecord={activeRecord}
                    missingFishIntelligence={missingFishIntelligence}
                    economicParams={economicParams}
                    dataQuality={dataQuality}
                    liveModelFCR={modelFCR}
                    activeFcrVersion={activeFcrVersion}
                    modelAdjustmentPercent={modelAdjustmentPercent}
                  />
                </div>
                {/* ====== END DECISION-GRADE PDF LAYOUT ====== */}

                {/* Data Table - Reads from daily_analysis_rows (NOT included in PDF) */}
                {sectionVisibility.dataTable && (
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setShowTable(!showTable)}
                    data-testid="toggle-table-btn"
                  >
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-800">Daily Data Table</h3>
                      <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">Advanced diagnostics</span>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{dailyRows.length} rows</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">{showTable ? 'Click to minimize' : 'Click to expand'}</span>
                      {showTable ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </div>
                  </div>
                  
                  {showTable && (
                    <div className="overflow-x-auto border-t">
                      <div className="max-h-96 overflow-y-auto">
                        <table className="w-full text-xs sm:text-sm">
                          <thead className="sticky top-0 bg-slate-50 z-10">
                            <tr>
                              <th className="border-b border-slate-200 px-2 py-2.5 text-left font-semibold text-slate-600 text-[10px] uppercase tracking-wider">Date</th>
                              <th className="border-b border-slate-200 px-2 py-2.5 text-right font-semibold text-slate-600 text-[10px] uppercase tracking-wider">Temp (°C)</th>
                              <th className="border-b border-slate-200 px-2 py-2.5 text-right font-semibold text-slate-600 text-[10px] uppercase tracking-wider hidden sm:table-cell">Feed (kg)</th>
                              <th className="border-b border-slate-200 px-2 py-2.5 text-right font-semibold text-emerald-500 text-[10px] uppercase tracking-wider">Rec. Feed</th>
                              <th className="border-b border-slate-200 px-2 py-2.5 text-right font-semibold text-slate-600 text-[10px] uppercase tracking-wider">Feed %</th>
                              <th className="border-b border-slate-200 px-2 py-2.5 text-right font-semibold text-slate-600 text-[10px] uppercase tracking-wider hidden sm:table-cell">Mortality</th>
                              <th className="border-b border-slate-200 px-2 py-2.5 text-right font-semibold text-blue-500 text-[10px] uppercase tracking-wider">Model (g)</th>
                              <th className="border-b border-slate-200 px-2 py-2.5 text-right font-semibold text-rose-500 text-[10px] uppercase tracking-wider">Actual (g)</th>
                              <th className="border-b border-slate-200 px-2 py-2.5 text-center font-semibold text-slate-600 text-[10px] uppercase tracking-wider">Sample</th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* TABLE READS FROM daily_analysis_rows (foundation dataset) */}
                            {(showAllRows ? daily_analysis_rows : daily_analysis_rows.slice(0, 50)).map((row, idx) => {
                              // Feed variance as percentage: Actual/Recommended * 100
                              // 100% = match, <100% = underfeeding, >100% = overfeeding
                              const feedPercent = row.feed_variance_percent;
                              return (
                              <tr key={row.date || `day-${idx}`} className={`hover:bg-slate-50 transition-colors ${row.is_sample ? 'bg-amber-50/50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                                <td className="border-b border-slate-100 px-2 py-1.5">{row.date}</td>
                                <td className="border-b border-slate-100 px-2 py-1.5 text-right">{row.temperature !== null ? row.temperature.toFixed(1) : '-'}</td>
                                <td className="border-b border-slate-100 px-2 py-1.5 text-right hidden sm:table-cell">{row.daily_feed_kg !== null ? row.daily_feed_kg.toFixed(0) : '-'}</td>
                                <td className="border-b border-slate-100 px-2 py-1.5 text-right text-emerald-600">
                                  {row.recommended_feed_kg !== null 
                                    ? row.recommended_feed_kg.toFixed(1) 
                                    : '-'}
                                </td>
                                <td className={`border-b border-slate-100 px-2 py-1.5 text-right font-medium ${
                                  feedPercent !== null && feedPercent > 105 ? 'text-rose-600' :
                                  feedPercent !== null && feedPercent < 95 ? 'text-amber-600' :
                                  'text-emerald-600'
                                }`}>
                                  {feedPercent !== null 
                                    ? `${feedPercent.toFixed(1)}%` 
                                    : '-'}
                                </td>
                                <td className="border-b border-slate-100 px-2 py-1.5 text-right hidden sm:table-cell">{row.daily_mortality || 0}</td>
                                <td className="border-b border-slate-100 px-2 py-1.5 text-right text-blue-600 font-medium">{row.model_weight_g !== null ? row.model_weight_g.toFixed(1) : '-'}</td>
                                <td className="border-b border-slate-100 px-2 py-1.5 text-right text-rose-600 font-medium">{row.actual_weight_g !== null ? row.actual_weight_g.toFixed(1) : '-'}</td>
                                <td className="border-b border-slate-100 px-2 py-1.5 text-center">
                                  {row.sample_weight && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">S:{row.sample_weight}g</span>}
                                  {row.harvest_weight && <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full ml-1">H:{row.harvest_weight}g</span>}
                                  {row.harvest_number && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full ml-1">#{row.harvest_number}</span>}
                                </td>
                              </tr>
                            )})}
                          </tbody>
                        </table>
                      </div>
                      {daily_analysis_rows.length > 50 && (
                        <div className="flex items-center justify-center gap-2 py-3 border-t bg-gray-50">
                          <span className="text-sm text-gray-600">
                            {showAllRows ? `Showing all ${daily_analysis_rows.length} rows` : `Showing 50 of ${daily_analysis_rows.length} rows`}
                          </span>
                          <button
                            onClick={() => setShowAllRows(!showAllRows)}
                            className="text-sm text-[#0891b2] hover:text-[#0e7490] font-medium underline"
                            data-testid="show-all-rows-btn"
                          >
                            {showAllRows ? 'Show less' : 'Show all rows'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                )}

                {/* Economic Impact Parameters */}
                {sectionVisibility.economicParams && (
                <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="economic-params-panel">
                  <h4 className="text-sm font-semibold text-emerald-800 mb-3">Economic Impact Parameters (optional)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium text-emerald-700 block mb-1">Feed Price (EUR/kg)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={economicParams.average_feed_price_eur_per_kg}
                        onChange={(e) => saveEconomicParams({ ...economicParams, average_feed_price_eur_per_kg: e.target.value })}
                        className="w-full border border-emerald-300 rounded-md px-3 py-1.5 text-sm focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="1.50"
                        data-testid="feed-price-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-emerald-700 block mb-1">Fish Price (EUR/kg)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={economicParams.fish_price_eur_per_kg}
                        onChange={(e) => saveEconomicParams({ ...economicParams, fish_price_eur_per_kg: e.target.value })}
                        className="w-full border border-emerald-300 rounded-md px-3 py-1.5 text-sm focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="e.g. 6.50"
                        data-testid="fish-price-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-emerald-700 block mb-1">Target Harvest Weight (kg)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={economicParams.target_harvest_weight_kg}
                        onChange={(e) => saveEconomicParams({ ...economicParams, target_harvest_weight_kg: e.target.value })}
                        className="w-full border border-emerald-300 rounded-md px-3 py-1.5 text-sm focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="e.g. 0.40"
                        data-testid="target-harvest-weight-input"
                      />
                    </div>
                  </div>
                </div>
                )}

                {/* Closed cage message */}
                {cageStatus === 'closed' && (
                  <div className="mt-4 bg-slate-50 border border-slate-300 rounded-lg p-4 text-center" data-testid="closed-cage-intelligence-msg">
                    <Lock className="w-5 h-5 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-slate-700">Missing fish estimation not applicable.</p>
                    <p className="text-xs text-slate-500 mt-1">Cage is closed and final fish numbers have already materialized.</p>
                  </div>
                )}

                {/* Missing Fish Intelligence Results */}
                {/* ★ Receives overriddenOutput for SSOT-enforced narrative */}
                {sectionVisibility.intelligenceReport && cageStatus === 'ongoing' && showMissingFishIntelligence && missingFishIntelligence && (
                  <div className="mt-6 space-y-4">
                    {/* Admin Hidden Sections Overview */}
                    <AdminHiddenSectionsOverview className="mb-4" />

                    {!missingFishIntelligence?.ssot?.hasMissingFish && (
                      <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="no-missing-fish-model-reminder">
                        <Info className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>No missing fish detected using the selected model. Consider reviewing your FCR model if it does not reflect your verified farm performance.</span>
                      </div>
                    )}
                    
                    <MissingFishIntelligenceReport 
                      data={missingFishIntelligence}
                      economicImpact={economicImpact}
                      dataQuality={dataQuality}
                      mortalityCheck={mortalityCheck}
                      samplingRecommendation={samplingRecommendation}
                      overfeeding={overfeedingRisk}
                      trajectoryJump={trajectoryJump}
                      fcrBasedDetection={missingFishIntelligence?.fcrBasedDetection}
                      growthFit={missingFishIntelligence?.growthFit}
                      methodResolution={missingFishIntelligence?.methodResolution}
                      sharedMetrics={missingFishIntelligence?.sharedMetrics}
                      ssot={missingFishIntelligence?.ssot}
                      overriddenOutput={effectiveOutput}
                      showDeviationTable={true}
                      showExplanations={true}
                      onShowAllDataChange={setShowAllDataOverride}
                      showAllDataOverride={showAllDataOverride}
                      dynamicFeedVariance={dynamicFeedVariance}
                      excludedSampleIndices={excludedSampleIndices}
                      onToggleSampleExclusion={handleToggleSampleExclusion}
                    />
                  </div>
                )}
                </>
                )}
                {/* End of Full Report View */}
              </div>
              </fieldset>
            )}
          </CardContent>
        </Card>

        {/* Wingman Proactive Alert */}
        {activeRecord && missingFishIntelligence && !isRestrictedDemo && (
          <WingmanNudge patterns={detectSpreadsheetPatterns({ missingFishIntelligence, activeRecord })} />
        )}

        {/* Wingman AI Insight */}
        {activeRecord && !isRestrictedDemo && (
          <WingmanInsight
            analysisType="Spreadsheet Growth Comparison"
            analysisData={{
              species: activeRecord.species,
              unit_name: activeRecord.name,
              fish_count: activeRecord.input_number,
              sample_count: activeRecord.sample_points?.length || 0,
              days_in_water: effectiveActiveRecord?.days_in_water || activeRecord.days_in_water || activeRecord.data_points?.length || 0,
              total_mortalities: effectiveActiveRecord?.total_mortalities ?? activeRecord.total_mortalities ?? 0,
              total_feed_kg: effectiveActiveRecord?.total_feed_given ?? activeRecord.total_feed_given ?? 0,
              reported_fcr: missingFishIntelligence?.sharedMetrics?.reported_fcr || null,
              model_fcr: missingFishIntelligence?.sharedMetrics?.model_fcr || null,
              missing_fish_detected: missingFishIntelligence?.ssot?.hasMissingFish || false,
              estimated_missing: missingFishIntelligence?.ssot?.missingFishCount || 0,
              // ★ SSOT — pass actionability so AI can gate definitive wording
              actionability: missingFishIntelligence?.ssot?.actionability || missingFishIntelligence?.decisionContext?.decisionClass || null,
              decision_class: missingFishIntelligence?.decisionContext?.decisionClass || null,
              // ★ Feeding classification — reconciled SSOT values (prevents contradictions)
              feeding_state: missingFishIntelligence?.ssot?.feedingState || null,          // Overfeeding | Optimal | Mixed | Restricted
              feeding_note: missingFishIntelligence?.ssot?.feedingNote || null,
              feeding_rfi_pct: missingFishIntelligence?.ssot?.rfi ?? null,
              wasted_feed_kg: missingFishIntelligence?.ssot?.wastedFeedKg ?? 0,
              waste_share_pct: missingFishIntelligence?.ssot?.wasteSharePct ?? 0,
              waste_economically_significant: !!missingFishIntelligence?.ssot?.isWasteEconomicallySignificant,
              overfeeding_level: missingFishIntelligence?.overfeedingRisk?.level || 'Low',  // Kept for backward-compat (raw signal)
              data_quality_score: missingFishIntelligence?.dataQuality?.overallScore || null,
              detection_strength: missingFishIntelligence?.ssot?.detectionStrength || null,
              confidence_mode: missingFishIntelligence?.ssot?.confidenceMode || null
            }}
          />
        )}

        {/* Record History Panel */}
        {activeRecord?.id && !isRestrictedDemo && (
          <RecordHistoryPanel
            recordId={activeRecord.id}
            recordType="spreadsheet"
            recordName={activeRecord.name}
          />
        )}

        {/* Saved Records Section */}
        <Card className="border-slate-200 shadow-md" data-testid="saved-records-card" hidden={isRestrictedDemo}>
          <CardHeader 
            className="cursor-pointer hover:bg-slate-50 transition-colors"
            onClick={() => { setShowRecords(!showRecords); if (!showRecords) fetchRecords(); }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <FolderOpen className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <CardTitle className="text-amber-700">Saved Analyses</CardTitle>
                  <CardDescription className="text-gray-600">View and manage your spreadsheet analyses</CardDescription>
                </div>
              </div>
              {showRecords ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </div>
          </CardHeader>
          
          {showRecords && (
            <CardContent className="space-y-4">
              {recordsLoading ? (
                <p className="text-center text-gray-500 py-8">Loading saved analyses...</p>
              ) : records.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No saved analyses yet. Upload a spreadsheet to create one.</p>
              ) : (
                <div className="overflow-x-auto">
                  {/* Excel-style sort + multi-select filter (persisted) */}
                  {recordsSortFilterBar}
                  {/* Bulk actions */}
                  {records.length > 0 && (
                    <div className="flex items-center gap-2 mb-2" data-testid="bulk-delete-actions">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50"
                        disabled={selectedRecordIds.size === 0}
                        onClick={confirmDeleteSelected}
                        data-testid="delete-selected-btn"
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        Delete Selected ({selectedRecordIds.size})
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => { setSelectedRecordIds(new Set(records.map(r => r.id))); }}
                        disabled={selectedRecordIds.size === records.length}
                        data-testid="select-all-records-btn"
                      >
                        Select All
                      </Button>
                    </div>
                  )}
                  <table className="w-full text-xs md:text-sm border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border px-2 py-1.5 w-8 text-center">
                          <input
                            type="checkbox"
                            className="w-3 h-3 rounded"
                            checked={records.length > 0 && selectedRecordIds.size === records.length}
                            ref={(el) => { if (el) el.indeterminate = selectedRecordIds.size > 0 && selectedRecordIds.size < records.length; }}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedRecordIds(new Set(records.map(r => r.id)));
                              else setSelectedRecordIds(new Set());
                            }}
                            data-testid="select-all-checkbox"
                          />
                        </th>
                        <th className="border px-2 py-1.5 text-left font-medium">Name</th>
                        <th className="border px-2 py-1.5 text-left font-medium hidden sm:table-cell">Species</th>
                        <th className="border px-2 py-1.5 text-left font-medium hidden md:table-cell">Start Date</th>
                        <th className="border px-2 py-1.5 text-left font-medium hidden lg:table-cell">Data Points</th>
                        <th className="border px-2 py-1.5 text-left font-medium hidden sm:table-cell">Data Through</th>
                        <th className="border px-2 py-1.5 text-left font-medium">Updated</th>
                        <th className="border px-2 py-1.5 text-left font-medium hidden md:table-cell" data-testid="th-harvest-correction">Harvest Correction</th>
                        <th className="border px-2 py-1.5 text-center font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRecords.map((record) => (
                        <tr key={record.id} className={`hover:bg-gray-50 ${activeRecord?.id === record.id ? 'bg-amber-50' : ''}`}>
                          <td className="border px-2 py-1.5 text-center">
                            <input
                              type="checkbox"
                              className="w-3 h-3 rounded"
                              checked={selectedRecordIds.has(record.id)}
                              onChange={(e) => {
                                setSelectedRecordIds(prev => {
                                  const n = new Set(prev);
                                  e.target.checked ? n.add(record.id) : n.delete(record.id);
                                  return n;
                                });
                              }}
                              data-testid={`record-checkbox-${record.id}`}
                            />
                          </td>
                          <td className="border px-2 py-1.5 font-medium">
                            <InlineRename
                              value={record.name}
                              onSave={(newName) => renameSpreadsheetRecord(record.id, newName)}
                            />
                          </td>
                          <td className="border px-2 py-1.5 hidden sm:table-cell">{record.species}</td>
                          <td className="border px-2 py-1.5 hidden md:table-cell">{record.start_date}</td>
                          <td className="border px-2 py-1.5 hidden lg:table-cell">{record.data_points?.length || 0}</td>
                          <td className="border px-2 py-1.5 hidden sm:table-cell text-gray-600">
                            {record.data_points?.length > 0 
                              ? new Date(record.data_points[record.data_points.length - 1].date).toLocaleDateString()
                              : '-'}
                          </td>
                          <td className="border px-2 py-1.5">{new Date(record.updated_at).toLocaleDateString()}</td>
                          <td className="border px-2 py-1.5 hidden md:table-cell" data-testid={`td-harvest-correction-${record.id}`}>
                            <HarvestCorrectionBadge status={record.harvest_correction_status} compact />
                          </td>
                          <td className="border px-2 py-1.5">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); loadRecord(record); }}
                                className="h-7 px-2 text-[#0891b2] hover:text-[#0e7490]"
                                title="Load analysis"
                                data-testid={`load-analysis-${record.id}`}
                              >
                                <FolderOpen className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => confirmDeleteRecord(record.id)}
                                className="h-7 px-2 text-red-500 hover:text-red-700"
                                title="Delete analysis"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Help & Benefits Section */}
        <HelpBenefits 
          youtubeUrl={HELP_URLS.spreadsheetComparison.youtube}
          benefitsUrl={HELP_URLS.spreadsheetComparison.benefits}
          featureName="Spreadsheet Growth Comparison"
        />

        {/* Glossary footer (collapsible) — only when a record is loaded */}
        {activeRecord && <ReportGlossary />}

        {/* Loss Window Estimator — now available to all authenticated
            users (formerly admin-only). Renders below Wingman when a
            record is loaded. Its "Show on chart" toggle streams the
            selected windows back up via `setEstimatorChartWindows`,
            which paints amber ReferenceAreas on renderGrowthChart. */}
        {activeRecord && !isRestrictedDemo && (
          <LossWindowEstimatorPanel
            recordId={activeRecord.id}
            token={token}
            onWindowsForChart={setEstimatorChartWindows}
          />
        )}
      </div>

      {/* Exact-name duplicate confirmation — appears when the backend
          rejects an upload with 409 `{duplicate_name: true}`. Primary
          action = Overwrite (matches user's dominant intent for
          re-uploads). Keep Both preserves both records. */}
      <AlertDialog
        open={!!duplicatePrompt}
        onOpenChange={(open) => {
          if (!open && duplicatePrompt) duplicatePrompt.cancel();
        }}
      >
        <AlertDialogContent data-testid="duplicate-name-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Analysis name already exists</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{duplicatePrompt?.name}</strong> already exists. Overwrite it with this upload?
              <br />
              <span className="text-xs text-slate-500">
                Overwrite replaces the existing record and invalidates any saved analysis on it.
                Keep Both preserves the existing record and creates a new one.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="duplicate-name-cancel-btn"
              onClick={() => duplicatePrompt?.cancel()}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => duplicatePrompt?.resubmit('keep_both')}
              data-testid="duplicate-name-keep-both-btn"
            >
              Keep Both
            </Button>
            <AlertDialogAction
              onClick={() => duplicatePrompt?.resubmit('overwrite')}
              data-testid="duplicate-name-overwrite-btn"
            >
              Overwrite
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Expanded Chart Modal */}
      <Dialog open={chartExpanded} onOpenChange={setChartExpanded}>
        <DialogContent className="max-w-[100vw] w-full h-[100vh] max-h-[100vh] p-0 m-0 border-0 rounded-none">
          <div className="flex flex-col h-full bg-white">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <DialogTitle className="text-lg font-semibold text-[#0891b2]">
                Growth Comparison - {activeRecord?.name}
              </DialogTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setChartExpanded(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="flex-1 p-4">
              {activeRecord && renderGrowthChart(window.innerHeight - 150)}
            </div>
            
            <div className="flex justify-center gap-6 p-4 border-t bg-gray-50 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-6" style={{borderTop: '3px dashed #2563eb'}}></div>
                <span>Model Growth (SGR-based)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 bg-red-600" style={{height: '3px'}}></div>
                <span>Actual Growth (SGR-corrected)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-600"></div>
                <span>Sample Points</span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mortality Chart Expanded Modal */}
      <Dialog open={mortalityChartExpanded} onOpenChange={setMortalityChartExpanded}>
        <DialogContent className="max-w-[100vw] w-full h-[100vh] max-h-[100vh] p-0 m-0 border-0 rounded-none">
          <div className="flex flex-col h-full bg-white">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <DialogTitle className="text-lg font-semibold text-[#0891b2]">
                Mortality – Daily vs Accumulated - {activeRecord?.name}
              </DialogTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMortalityChartExpanded(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="flex-1 p-4">
              {activeRecord && renderMortalityChart(window.innerHeight - 150)}
            </div>
            
            <div className="flex justify-center gap-6 p-4 border-t bg-gray-50 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-6 bg-blue-600" style={{height: '2px'}}></div>
                <span>Daily Mortality</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 bg-red-600" style={{height: '2px'}}></div>
                <span>Accumulated Mortality</span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Feed Chart Expanded Modal */}
      <Dialog open={feedChartExpanded} onOpenChange={setFeedChartExpanded}>
        <DialogContent className="max-w-[100vw] w-full h-[100vh] max-h-[100vh] p-0 m-0 border-0 rounded-none">
          <div className="flex flex-col h-full bg-white">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <DialogTitle className="text-lg font-semibold text-[#0891b2]">
                Feed Analysis – Accumulated & Daily Variance - {activeRecord?.name}{assumedFishNumber ? ' (Adjusted)' : ''}
              </DialogTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFeedChartExpanded(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="flex-1 p-4">
              {activeRecord && renderFeedChart(window.innerHeight - 150)}
            </div>
            
            <div className="flex justify-center gap-6 p-4 border-t bg-gray-50 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-6 bg-orange-500" style={{height: '2px'}}></div>
                <span>Daily Variance %</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 bg-green-600" style={{height: '2px'}}></div>
                <span>Accum. Actual</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 bg-red-600" style={{height: '2px'}}></div>
                <span>Accum. Recommended</span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Single Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Analysis</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this saved analysis? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-confirm-btn"
              onClick={() => {
                if (pendingDeleteId) deleteRecord(pendingDeleteId);
                setPendingDeleteId(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Batch Delete Confirmation */}
      <AlertDialog open={batchDeleteConfirmOpen} onOpenChange={setBatchDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedRecordIds.size} Analyses</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedRecordIds.size} saved {selectedRecordIds.size === 1 ? 'analysis' : 'analyses'}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="batch-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="batch-delete-confirm-btn"
              onClick={deleteSelectedRecords}
            >
              Delete All Selected
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ★ Upload diagnostics — row-level feedback after /upload or /reupload */}
      <Dialog open={diagnosticsOpen} onOpenChange={setDiagnosticsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="upload-diagnostics-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Upload Diagnostics
            </DialogTitle>
            <DialogDescription>
              {uploadDiagnostics?.mode === 'update'
                ? 'Your record was updated, but some rows in the file had issues and were flagged below.'
                : 'Your file was processed, but some rows had issues and were flagged below.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex flex-wrap gap-3 text-sm">
              <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5" data-testid="diagnostics-accepted-badge">
                <span className="text-emerald-700 font-semibold">Accepted:</span>{' '}
                <span className="text-emerald-900">{uploadDiagnostics?.accepted ?? '—'}</span>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5" data-testid="diagnostics-rejected-badge">
                <span className="text-amber-700 font-semibold">Flagged:</span>{' '}
                <span className="text-amber-900">{uploadDiagnostics?.rejected?.length ?? 0}</span>
              </div>
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="max-h-[50vh] overflow-auto">
                <table className="w-full text-sm" data-testid="diagnostics-table">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-slate-700 w-20">Row</th>
                      <th className="px-3 py-2 text-left font-semibold text-slate-700">Reason(s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(uploadDiagnostics?.rejected || []).map((d) => (
                      <tr key={`diag-row-${d.row}`} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2 align-top font-mono text-slate-900">{d.row}</td>
                        <td className="px-3 py-2 align-top">
                          <ul className="list-disc pl-4 space-y-1 text-slate-700">
                            {(Array.isArray(d.reasons) ? d.reasons : [String(d.reasons)]).map((r, i) => (
                              <li key={`r-${d.row}-${i}`}>{r}</li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-xs text-slate-500">
              Tip: Zero values in Sample Weight Update, Harvest Average Weight Update, and Harvest Number
              are treated as empty (ignored). Rows without a valid date are skipped entirely.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDiagnosticsOpen(false)}
              data-testid="diagnostics-close-btn"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <FcrGuidanceModal />
    </div>
  );
}
